/**
 * ES Translation File - Organized by Feature Domain
 *
 * This file contains all Spanish translations for the MyQCare application,
 * organized in a logical hierarchy for easy navigation and maintenance.
 *
 * TABLE OF CONTENTS:
 * ==================
 *
 * 1. CORE/FOUNDATION
 *    └── common - Basic UI actions, states, and reusable text
 *
 * 2. SYSTEM/INFRASTRUCTURE
 *    ├── ui - UI components & accessibility labels
 *    ├── form - Form components & validation messages
 *    ├── error - Error handling messages
 *    ├── theme - Theme & styling related text
 *    ├── navigation - Navigation labels
 *    ├── menu - Menu items & accessibility labels
 *    ├── splash - Splash screen content
 *    ├── logs - Application logging and debugging
 *    └── forceUpgrade - Force app update messages
 *
 * 3. AUTHENTICATION & USER MANAGEMENT
 *    ├── auth - Authentication flows
 *    ├── login - Login process
 *    ├── verify - Verification processes
 *    └── account - Account management
 *
 * 4. USER PROFILE & DATA
 *    ├── profile - User profile management
 *    ├── photo_id - ID verification
 *    ├── address - Address management
 *    └── addressVerification - Address verification
 *
 * 5. HEALTH & MEDICAL
 *    ├── health - General health content
 *    ├── healthHistory - Health history forms
 *    ├── healthAssistance - Health assistance programs
 *    ├── treatments - Treatment information
 *    ├── encounter - Encounter information
 *    ├── prescription/prescriptions - Prescription management
 *    ├── labs/lab/hivLab - Lab results and testing
 *    └── assessment/assessments - Health assessments
 *
 * 6. CARE MANAGEMENT
 *    ├── enrollment - Enrollment process
 *    ├── appointment/appointments - Appointment scheduling
 *    ├── home - Home dashboard
 *    ├── chat/chats/asyncMessaging - Messaging systems
 *    └── notifications - Notification messages
 *
 * 7. VISIT
 *    └── visit - Video visit/Twilio components
 *
 * 8. DOCUMENTS & INSURANCE
 *    ├── documents - Document management
 *    ├── insurance/insurances - Insurance handling
 *    ├── consent - Consent forms
 *    └── roiRequest - Release of information
 *
 * 9. PROGRAMS & APPLICATIONS
 *    ├── gileadApplication - Gilead program application
 *    └── welcome - Welcome & onboarding
 *
 * 10. UTILITIES & COMPONENTS
 *    ├── imageUploader - Image upload component
 *    ├── imageCrop - Image crop component
 *    ├── fileUploader - File upload component
 *    ├── documentUploader - Document upload component
 *    ├── pdfViewer - PDF viewer component
 *    ├── carousel - Carousel component
 *    └── miniGame - Mini game (2048) component
 *
 * 11. FINANCIAL
 *    └── payment - Payment processing
 *
 * NAMING CONVENTIONS:
 * ===================
 * - Use dot notation for nested organization (e.g., 'common.save')
 * - Group related functionality under same namespace
 * - Use descriptive, action-oriented keys
 * - Maintain consistency in naming patterns
 *
 * MAINTENANCE NOTES:
 * ==================
 * - Keep translations alphabetically sorted within each namespace
 * - Add new translations to the appropriate section
 * - Update this index when adding new namespaces
 * - Preserve all translation values when reorganizing
 */

const es = {
  // Core/Foundation
  'common.add': 'Añadir',
  'common.cancel': 'Cancelar',
  'common.clear': 'Limpiar',
  'common.confirm': 'Confirmar',
  'common.continue': 'Continuar',
  'common.complete': 'Completar',
  'common.conjunction.and': 'y',
  'common.conjunction.comma': ',',
  'common.copy': 'Copiar',
  'common.create': 'Crear',
  'common.date.today': 'Hoy',
  'common.date.tomorrow': 'Mañana',
  'common.date.yesterday': 'Ayer',
  'common.delete': 'Eliminar',
  'common.done': 'Hecho',
  'common.edit': 'Editar',
  'common.edit.a11y': 'Editar {item}',
  'common.edit.title': 'Editar {title}',
  'common.error': 'Error',
  'common.goBack': 'Volver',
  'common.image': 'Imagen',
  'common.loading': 'Cargando...',
  'common.logout': 'Cerrar sesión',
  'common.more': 'Más',
  'common.more.a11y': 'Cargar más elementos',
  'common.new': 'Nuevo',
  'common.new.title': 'Nuevo {title}',
  'common.no': 'No',
  'common.ok': 'OK',
  'common.options': 'Opciones',
  'common.options.no': 'No',
  'common.options.notSure': 'No estoy seguro',
  'common.options.yes': 'Sí',
  'common.progress': 'Progreso: {value}%',
  'common.refresh': 'Actualizar',
  'common.reload': 'Recargar',
  'common.remove': 'Quitar',
  'common.reset': 'Restablecer',
  'common.restart': 'Reiniciar la aplicación',
  'common.save': 'Guardar',
  'common.select': 'Seleccionar',
  'common.settings': 'Configuración',
  'common.skip': 'Saltar',
  'common.success': 'Éxito',
  'common.tryAgain': 'Inténtelo nuevamente',
  'common.update': 'Actualizar',
  'common.view': 'Ver',
  'common.upload': 'Subir',
  'common.yes': 'Sí',
  'common.close': 'Cerrar',
  'common.send': 'Enviar',
  'common.empty.title': 'Datos no disponibles',
  'common.empty.description': 'No hay información para mostrar en este momento.',

  // Network Status
  'network.offline': 'Estás sin conexión',
  'network.back_online': 'Conectado nuevamente',

  // UI Components & Accessibility
  'ui.accordion.a11y.collapseHint': 'Toque para minimizar',
  'ui.accordion.a11y.collapsed': 'Contraído',
  'ui.accordion.a11y.expandHint': 'Toque para expandir',
  'ui.accordion.a11y.expanded': 'Expandido',
  'ui.alert.close': 'Cerrar alerta',
  'ui.avatar.a11y.default': 'Foto de perfil',
  'ui.avatar.a11y.label': 'Foto de perfil de {name}',
  'ui.groupAvatar.a11y.label': 'Avatar de grupo con {count} miembros',
  'ui.badge.a11y.count': '{count} notificaciones',
  'ui.badge.a11y.label': 'Notificación {content}',
  'ui.bottomsheet.a11y.dismiss': 'Descartar',
  'ui.cancelButton.a11y.hint': 'Cancela la acción actual y regresa',
  'ui.chip.a11y.hint.default': 'Toque para seleccionar',
  'ui.chip.a11y.label': '{label}',
  'ui.confirmButton.a11y.hint': 'Confirma y guarda la acción actual',
  'ui.content.link.aria': 'Enlace: {text}',
  'ui.content.link.hint': 'Presiona para abrir enlace',
  'ui.content.youtube.aria': 'Video de YouTube',
  'ui.content.youtube.hint': 'Presiona para abrir video en YouTube',
  'ui.dialog.a11y.actionHint': 'Realiza la acción y cierra el diálogo',
  'ui.dialog.a11y.actionKeepOpenHint': 'Realiza la acción',
  'ui.dialog.a11y.actionsLabel': 'Acciones del diálogo',
  'ui.dialog.a11y.contentLabel': 'Contenido del diálogo',
  'ui.dialog.a11y.close': 'Cerrar diálogo',
  'ui.dialog.a11y.dismiss': 'Cerrar diálogo',
  'ui.dialog.a11y.dismissHint': 'Toque dos veces para cerrar este diálogo',
  'ui.dialog.a11y.headerLabel': 'Encabezado del diálogo',
  'ui.dialog.a11y.label': 'Diálogo',
  'ui.leavePrompt.unsavedChanges.title': 'Cambios sin guardar',
  'ui.leavePrompt.unsavedChanges.message': 'Tienes cambios sin guardar. ¿Estás seguro de que quieres salir de esta página?',
  'ui.leavePrompt.unsavedChanges.stay': 'Quedarse',
  'ui.leavePrompt.unsavedChanges.leave': 'Salir',
  'ui.leavePrompt.booking.title': '¿Salir antes de reservar?',
  'ui.leavePrompt.booking.message': 'Tu cita aún no está programada. Solo toma un minuto terminar.',
  'ui.leavePrompt.booking.stay': 'Seguir reservando',
  'ui.leavePrompt.booking.leave': 'Salir',
  'ui.leavePrompt.asyncVisit.title': '¿Salir antes de enviar tu visita?',
  'ui.leavePrompt.asyncVisit.message': 'Tu progreso está guardado, pero tu proveedor no podrá revisarlo hasta que termines.',
  'ui.leavePrompt.asyncVisit.stay': 'Continuar',
  'ui.leavePrompt.asyncVisit.leave': 'Salir',
  'ui.leavePrompt.asyncAssessment.title': '¿Salir antes de terminar el cuestionario?',
  'ui.leavePrompt.asyncAssessment.message': 'Tus respuestas están guardadas, pero tu visita no se puede enviar hasta que termines.',
  'ui.leavePrompt.asyncAssessment.stay': 'Continuar',
  'ui.leavePrompt.asyncAssessment.leave': 'Salir',
  'ui.leavePrompt.asyncVisitCancel.title': '¿Cancelar tu solicitud de visita?',
  'ui.leavePrompt.asyncVisitCancel.message':
    'Esto no se puede deshacer y tendrías que empezar una nueva solicitud desde el principio. ¿Necesitas un descanso? Tu progreso está guardado, así que puedes terminar después.',
  'ui.leavePrompt.asyncVisitCancel.stay': 'Continuar',
  'ui.leavePrompt.asyncVisitCancel.leave': 'Sí, cancelar',
  'ui.leavePrompt.assessment.title': '¿Salir antes de terminar el cuestionario?',
  'ui.leavePrompt.assessment.message': 'Tus respuestas están guardadas y puedes volver para terminar más tarde.',
  'ui.leavePrompt.assessment.stay': 'Continuar',
  'ui.leavePrompt.assessment.leave': 'Salir',
  'ui.leavePrompt.gileadApplication.title': '¿Salir antes de enviar tu solicitud?',
  'ui.leavePrompt.gileadApplication.message':
    'Tus respuestas están guardadas en este dispositivo, pero tu solicitud no se envía hasta que firmes.',
  'ui.leavePrompt.gileadApplication.stay': 'Continuar',
  'ui.leavePrompt.gileadApplication.leave': 'Salir',
  'ui.leavePrompt.roiRequest.title': '¿Salir antes de firmar tu solicitud?',
  'ui.leavePrompt.roiRequest.message':
    'Tus respuestas están guardadas en este dispositivo, pero tu solicitud no está completa hasta que firmes.',
  'ui.leavePrompt.roiRequest.stay': 'Continuar',
  'ui.leavePrompt.roiRequest.leave': 'Salir',
  'ui.leavePrompt.visit.title': '¿Salir de la llamada?',
  'ui.leavePrompt.visit.message': 'Hay una llamada en curso. Si sales ahora, se desconectará.',
  'ui.leavePrompt.visit.stay': 'Seguir en la llamada',
  'ui.leavePrompt.visit.leave': 'Salir',
  'ui.leavePrompt.onDemand.title': '¿Salir de la sala de espera?',
  'ui.leavePrompt.onDemand.message': 'Un proveedor puede unirse en cualquier momento. Si sales, podrías perder tu visita.',
  'ui.leavePrompt.onDemand.stay': 'Seguir esperando',
  'ui.leavePrompt.onDemand.leave': 'Salir',
  'ui.form.progress.a11y.collapseHint': 'Toque dos veces para contraer elementos completados',
  'ui.form.progress.a11y.expandHint': 'Toque dos veces para expandir elementos completados',
  'ui.form.progress.a11y.header': 'Progreso del formulario: {completed} de {total} completados',
  'ui.form.progress.completed': '{completed}/{total} Completado',
  'ui.form.progress.item.a11y.completed': '{label} - completado',
  'ui.form.progress.item.a11y.disabled': '{label} - deshabilitado',
  'ui.form.progress.item.a11y.pending': '{label} - pendiente',
  'ui.list.item.a11y.hint': 'Toque para {action}',
  'ui.loading.subtitle': 'Por favor espere mientras preparamos su experiencia...',
  'ui.loading.title': 'Cargando',
  'ui.logo.a11y.label': 'Logo de MyQCare',
  'ui.progress.a11y.label': 'Progreso: {percent} por ciento completado',
  'ui.progress.stepper.a11y.completed': 'completado',
  'ui.progress.stepper.a11y.current': 'paso actual',
  'ui.progress.stepper.a11y.pending': 'pendiente',
  'ui.progress.stepper.a11y.step': 'Paso {current} de {total}',
  'ui.refresh.hint': 'Toque para actualizar',
  'ui.refresh.label': 'Actualizar',
  'ui.signature.a11y.area': 'Área de firma de {label}',
  'ui.signature.a11y.hint': 'Dibuje su firma en esta área usando su dedo o stylus',
  'ui.signature.a11y.signed': 'Firma de {label} completada',
  'ui.signature.clear': 'Borrar Firma',
  'ui.signature.clear.hint': 'Eliminar la firma actual para empezar de nuevo',
  'ui.signature.label': 'Firma',
  'ui.slider.a11y.label': 'Control deslizante {label}',
  'ui.slider.a11y.range': 'Rango de {min} a {max}',
  'ui.slider.a11y.value': 'Valor: {value}',
  'ui.steps.item.a11y.label': 'Paso {number}: {text}',
  'ui.collapse': 'Contraer {label}',
  'ui.expand': 'Expandir {label}',
  'ui.tabs.a11y.activeTabHint': 'Pestaña actualmente seleccionada',
  'ui.tabs.a11y.inactiveTabHint': 'Toque para cambiar a esta pestaña',
  'ui.tabs.a11y.label': 'Navegación por pestañas',
  'ui.typography.heading.level': 'Encabezado nivel {level}',
  'ui.header.a11y.label': 'Encabezado de la aplicación',
  'ui.menu.a11y.label': 'Navegación del menú',
  'ui.menu.a11y.open': 'Abrir menú',

  // Form Components & Validation
  'form.address.loading': 'Cargando direcciones...',
  'form.address.no.options': 'No se encontraron direcciones',
  'form.address.placeholder': 'Seleccionar una dirección',
  'form.address.search': 'Buscar una dirección',
  'form.autocomplete.a11y.bottomsheet': 'Seleccionar una opción',
  'form.autocomplete.a11y.hint': 'Abre una lista de opciones con búsqueda',
  'form.autocomplete.a11y.options.count': '{count} opciones disponibles',
  'form.autocomplete.a11y.search.hint': 'Escriba para buscar opciones',
  'form.autocomplete.a11y.selected': 'Seleccionado: {label}',
  'form.autocomplete.min.chars': 'Escriba al menos {count} caracteres para buscar',
  'form.autocomplete.placeholder': 'Seleccionar una opción',
  'form.autocomplete.search.placeholder': 'Buscar...',
  'form.card.consent.description':
    'Escriba sus iniciales a continuación para dar su consentimiento a la revisión médica o tratamiento por un proveedor licenciado.',
  'form.card.consent.title': 'Consentimiento para la Atención',
  'form.card.referral.description': 'Por favor seleccione el miembro del equipo que lo refirió',
  'form.card.referral.title': 'Información de Referencia',
  'form.checkbox.a11y.checked': '{label}, marcado',
  'form.checkbox.a11y.checked.unlabeled': 'Marcado',
  'form.checkbox.a11y.unchecked': '{label}, no marcado',
  'form.checkbox.a11y.unchecked.unlabeled': 'No marcado',
  'form.datepicker.a11y.hint.date': 'Abre el selector de fecha',
  'form.datepicker.a11y.hint.datetime': 'Abre el selector de fecha y hora',
  'form.datepicker.a11y.hint.time': 'Abre el selector de hora',
  'form.datepicker.a11y.selected.date': 'Fecha seleccionada: {value}',
  'form.datepicker.a11y.selected.datetime': 'Fecha y hora seleccionadas: {value}',
  'form.datepicker.a11y.selected.time': 'Hora seleccionada: {value}',
  'form.datepicker.placeholder.date': 'Seleccionar una fecha',
  'form.datepicker.placeholder.datetime': 'Seleccionar fecha y hora',
  'form.datepicker.placeholder.time': 'Seleccionar una hora',
  'form.datepicker.title.date': 'Seleccionar Fecha',
  'form.datepicker.title.datetime': 'Seleccionar Fecha y Hora',
  'form.datepicker.title.time': 'Seleccionar Hora',
  'form.datepicker.a11y.hint.input': 'Ingrese la fecha como mes, día, año',
  'form.datepicker.placeholder.format': 'MM/DD/AAAA',
  'form.datepicker.validation.format': 'Ingrese una fecha válida en formato MM/DD/AAAA',
  'form.datepicker.validation.minDate': 'La fecha debe ser igual o posterior al {date}',
  'form.datepicker.validation.maxDate': 'La fecha debe ser igual o anterior al {date}',
  'form.datepicker.validation.invalid': 'Fecha inválida',
  'form.datepicker.validation.todayMax': 'No debe exceder la fecha de hoy',
  'form.datepicker.validation.dateMin': 'Debe ser después del 01/01/1901',
  'form.datepicker.validation.datesOrder': 'La fecha final debe ser posterior a la fecha de inicio',
  'form.datepicker.validation.datesSame': 'No puede ser la misma fecha',
  'form.date.day': 'Día',
  'form.date.month': 'Mes',
  'form.date.year': 'Año',
  'form.dob.ageRestriction': 'Debe tener al menos 18 años',
  'form.dob.hint': 'Seleccione su fecha de nacimiento para verificar su identidad',
  'form.dob.invalid': 'Fecha inválida',
  'form.dob.label': 'Fecha de Nacimiento',
  'form.dob.placeholder': 'Seleccione su fecha de nacimiento',
  'form.email.hint': 'Ingrese el correo electrónico de su cuenta',
  'form.email.invalid': 'Formato de correo electrónico inválido',
  'form.email.label': 'Correo electrónico',
  'form.email.maxAttempts': 'Demasiados intentos. Por favor, inténtelo más tarde.',
  'form.email.placeholder': 'Ingrese su correo electrónico',
  'form.error.invalid': 'Inválido',
  'form.error.label': 'Error',
  'form.error.max': 'El máximo es {max}',
  'form.error.maxLength': 'No puede exceder {max} caracteres',
  'form.error.min': 'El mínimo es {min}',
  'form.error.required': 'Obligatorio',
  'form.field.required': 'requerido',
  'form.help.label': 'Ayuda',
  'form.initials.consent': 'Requerimos su consentimiento para el examen y posible tratamiento',
  'form.initials.label': 'Iniciales',
  'form.initials.placeholder': 'ej. JD',
  'form.initials.validation': 'Deben ser las iniciales del nombre y apellido',
  'form.input.a11y.field': 'Campo de entrada {name}',
  'form.password.label': 'Contraseña',
  'form.phone.a11y.hint': 'Ingrese su número de teléfono de 10 dígitos con código de área',
  'form.phone.a11y.label': 'Campo de entrada de número de teléfono',
  'form.phone.invalid': 'Formato de número de teléfono inválido',
  'form.phone.label': 'Número de Teléfono',
  'form.phone.placeholder': '(123) 456-7890',
  'form.url.invalid': 'Formato de URL inválido',
  'form.referral.by.helper': 'Por favor seleccione el miembro del equipo que lo refirió',
  'form.referral.by.label': 'Referido Por',
  'form.referral.by.placeholder': 'Seleccione el miembro del equipo que lo refirió',
  'form.referral.details.label': 'Detalles de Referencia',
  'form.referral.details.placeholder': 'Por favor proporcione detalles sobre quién lo refirió',
  'form.select.a11y.bottomsheet.multiple': 'Seleccionar opciones',
  'form.select.a11y.bottomsheet.single': 'Seleccionar una opción',
  'form.select.a11y.hint.multiple': 'Abre una lista de opciones para selección múltiple',
  'form.select.a11y.hint.single': 'Abre una lista de opciones',
  'form.select.a11y.options.count': '{count} opciones disponibles',
  'form.select.a11y.search.hint': 'Escriba para filtrar las opciones disponibles',
  'form.select.a11y.selected.multiple': '{count} elementos seleccionados',
  'form.select.a11y.selected.single': 'Seleccionado: {label}',
  'form.select.a11y.clear': 'Limpiar selección',
  'form.select.a11y.clear.hint': 'Eliminar selección actual',
  'form.select.all': 'Marca todas las opciones que correspondan',
  'form.select.empty': 'No se encontraron opciones',
  'form.select.loading': 'Cargando opciones...',
  'form.select.option.a11y.checked': 'marcado',
  'form.select.option.a11y.disabled': 'deshabilitado',
  'form.select.option.a11y.not.selected': 'no seleccionado',
  'form.select.option.a11y.selected': 'seleccionado',
  'form.select.placeholder.multiple': 'Seleccionar opciones',
  'form.select.placeholder.single': 'Seleccionar una opción',
  'form.select.search.placeholder': 'Buscar opciones...',

  // Error Handling
  'error.boundary.message':
    'La aplicación encontró un error inesperado. Hemos registrado el problema y estamos trabajando para solucionarlo.',
  'error.boundary.title': 'Error inesperado',
  'error.copied': 'Detalles del error copiados al portapapeles',
  'error.general.message':
    'Pedimos disculpas por las molestias. Por favor, inténtelo de nuevo o contacte con soporte si el problema persiste.',
  'error.general.title': 'Algo salió mal',
  'error.notAuthorized.message': 'No tiene permiso para acceder a este recurso.',
  'error.notAuthorized.title': 'No autorizado',
  'error.notFound.message': 'La página que está buscando no existe o ha sido movida.',
  'error.notFound.title': 'Página no encontrada',
  'error.conversation.permission.message': 'No tiene permiso para iniciar una conversación con este usuario',
  'error.conversation.permission.title': 'Acceso Denegado',
  'error.tapToCopy': 'Toca para copiar los detalles del error',
  'error.tryAgain': 'Por favor, inténtelo de nuevo',
  'error.translationUnavailable.title': 'Traducción no disponible',
  'error.translationUnavailable.description':
    'Lamentamos informar que un problema técnico impide que podamos ofrecer la función de traducción por completo en este momento.\n\nPara una experiencia fluida, recomendamos desactivarla temporalmente.\n\nPedimos disculpas por cualquier inconveniente y agradecemos tu comprensión mientras trabajamos activamente para resolverlo.\n\nGracias por tu paciencia.',
  'error.offline.title': 'Sin Conexión a Internet',
  'error.offline.message':
    'Por favor verifica tu conexión a internet e inténtalo de nuevo. Necesitas estar en línea para usar esta aplicación.',
  'error.offline.tips.title': 'Consejos de Conexión:',
  'error.offline.tips.wifi': 'Verifica tu conexión Wi-Fi',
  'error.offline.tips.cellular': 'Comprueba que tus datos móviles estén activados',
  'error.offline.tips.airplane': 'Asegúrate de que el modo avión esté desactivado',
  'error.page.a11y.errorCode': 'Código de error {code}',
  'error.page.details.title': 'Detalles del Error',
  'error.page.viewError': 'Ver Error',
  'error.page.tryAgain': 'Intentar de Nuevo',
  'error.page.messageSupport': 'Contactar Soporte',
  'appUpdated.title': '¡Nuevas actualizaciones implementadas!',
  'appUpdated.improvements': 'Hemos hecho algunas mejoras.',
  'appUpdated.reloadPrompt': 'Recarga la página para disfrutar las últimas funciones.',
  'appUpdated.toast': 'Actualizado a la última versión',
  'appUpdated.available': 'Hay una nueva versión disponible.',

  // Theme & Styling
  'theme.options.dark': 'Oscuro',
  'theme.options.light': 'Claro',
  'theme.options.system': 'Sistema',

  // Navigation
  'navigation.back': 'Atrás',
  'navigation.next': 'Siguiente',
  'navigation.chat': 'Mensajes',
  'navigation.closePage': 'Cerrar página',
  'navigation.to.a11y': 'Navegar a {label}',
  'navigation.developerOptions': 'Opciones de Desarrollador',
  'navigation.health': 'Salud',
  'navigation.home': 'Inicio',
  'navigation.profile': 'Perfil',
  'navigation.tabs.a11y.badge': '{label}, {count} pendientes',
  'navigation.tabs.a11y.label': 'Navegación principal',

  // Menu
  'menu.environment': 'Entorno (no producción)',
  'menu.a11y.mainMenu': 'Menú principal',
  'menu.a11y.notificationsCount': '{count} nuevas notificaciones',
  'menu.a11y.fieldErrorsCount': '{count} campos del perfil tienen errores',
  'menu.a11y.fieldErrorsComplete': 'Todos los campos del perfil están completos',
  'menu.a11y.unreadMessages': '{count} mensajes sin leer',
  'menu.a11y.noUnreadMessages': 'Sin mensajes sin leer',
  'menu.a11y.patientName': 'Nombre del paciente: {name}',
  'menu.a11y.patientEmail': 'Correo del paciente: {email}',
  'menu.a11y.poweredByQCare': 'Desarrollado por QCare',

  // Splash Screen
  'splash.tagline': 'Su salud, nuestra prioridad',

  // Logs
  'logs.screen.title': 'Registros de la App',
  'logs.filters.title': 'Filtrar Registros',
  'logs.filters.description': 'Elige qué niveles de registro y criterios mostrar',
  'logs.filters.searchTerm.label': 'Buscar en Mensajes',
  'logs.filters.searchTerm.placeholder': 'Buscar en mensajes de registro...',
  'logs.filters.levels.label': 'Niveles de Registro',
  'logs.filters.namespaces.label': 'Espacios de Nombres',
  'logs.filters.startDate.label': 'Fecha de Inicio',
  'logs.filters.endDate.label': 'Fecha de Fin',
  'logs.filters.clearAll': 'Limpiar todos los filtros',
  'logs.filters.clearAll.a11y.label': 'Limpiar todos los filtros',
  'logs.filters.clear.button.a11y.label': 'Limpiar filtros',
  'logs.filters.clear.button.a11y.hint': 'Remover todos los filtros activos',
  'logs.filters.filterButton.a11y.label': 'Filtrar registros de la aplicación',
  'logs.export.title': 'Exportar Registros',
  'logs.export.description': 'Elige el formato de exportación',
  'logs.export.text': 'Exportar como TXT',
  'logs.export.json': 'Exportar como JSON',
  'logs.export.success.title': 'Exportación Completa',
  'logs.export.success.message': 'Registros exportados a: {path}',
  'logs.export.error.title': 'Error de Exportación',
  'logs.export.error.message': 'No se pudieron exportar los registros.',
  'logs.export.button.a11y.label': 'Exportar registros',
  'logs.clear.title': 'Limpiar Todos los Registros',
  'logs.clear.message': '¿Estás seguro de que quieres limpiar todos los registros? Esta acción no se puede deshacer.',
  'logs.clear.button.a11y.label': 'Limpiar todos los registros',
  'logs.clear.button.a11y.hint': 'Presiona para limpiar todos los registros permanentemente',
  'logs.entry.copied.title': 'Registro copiado al portapapeles',
  'logs.entry.copied.message': 'Registro {level} de {namespace}',
  'logs.entry.a11y.label': 'Entrada de registro {level} de {namespace} a las {time}',
  'logs.entry.a11y.hint': 'Presiona para copiar los detalles del registro al portapapeles',
  'logs.stats.total': 'Total',
  'logs.stats.errors': 'Errores',
  'logs.stats.warnings': 'Advertencias',
  'logs.empty.title': 'No se encontraron registros',
  'logs.empty.description': 'Intenta ajustar tus filtros o borrarlos para ver más registros',
  'logs.level.error': 'Error',
  'logs.level.warn': 'Advertencia',
  'logs.level.info': 'Información',
  'logs.level.debug': 'Depuración',
  'logs.filters.searchTerm.a11y.hint': 'Ingresa texto para buscar dentro de los mensajes de registro',
  'logs.filters.levels.a11y.hint': 'Selecciona uno o más niveles de registro para filtrar',
  'logs.filters.namespaces.a11y.hint': 'Selecciona uno o más espacios de nombres para filtrar',
  'logs.export.button.a11y.hint': 'Presiona para exportar registros en diferentes formatos',

  // Force App Update
  'forceUpgrade.description':
    'Una nueva versión de MyQCare está disponible con actualizaciones importantes y mejoras.\n\nPor favor actualiza tu aplicación para continuar usando todas las funciones.',
  'forceUpgrade.footer': 'Esta actualización es requerida para continuar usando MyQCare de forma segura.',
  'forceUpgrade.title': 'Actualización Requerida',
  'forceUpgrade.updateButton': 'Actualizar Ahora',

  // Authentication
  'auth.code.bothSent': 'He enviado un código de verificación a {phone} y {email}.',
  'auth.code.countdownMessage': 'Puedes solicitar un nuevo código en {seconds} segundos',
  'auth.code.dialog.contentEmail': 'Podemos reenviar el código de verificación a tu correo electrónico.',
  'auth.code.dialog.contentSMS': 'Podemos reenviar el código de verificación por SMS o enviarlo a tu correo electrónico.',
  'auth.code.dialog.resendEmail': 'Reenviar Correo',
  'auth.code.dialog.resendSMS': 'Reenviar SMS',
  'auth.code.dialog.supportNote': 'Si sigues teniendo problemas, por favor contacta con atención al cliente.',
  'auth.code.dialog.title': 'Reenviar Código de Verificación',
  'auth.code.dialog.useEmail': 'Usar Correo Electrónico',
  'auth.code.emailSent': 'He enviado un código de verificación a {email}.',
  'auth.code.enterBelow': 'Ingresa el código de 6 dígitos a continuación para continuar',
  'auth.code.expired': 'El código de verificación ha expirado. Por favor, solicita uno nuevo.',
  'auth.code.invalid': 'Código de verificación inválido. Por favor, inténtalo de nuevo.',
  'auth.code.maxAttempts': 'Demasiados intentos de verificación. Por favor, espere antes de intentar nuevamente.',
  'auth.code.messageSent': 'He enviado un código de verificación a {phone}.',
  'auth.code.resendCode': '¿No recibiste el código? Reenviar',
  'auth.code.retry.hint': 'Volver a enviar código de verificación',
  'auth.code.screenTitle': 'Código de Verificación',
  'auth.code.backToLogin': 'Volver al Inicio de Sesión',
  'auth.verify.expired': 'Su sesión de inicio de sesión ha expirado',
  'auth.sessionExpired.title': 'Sesión Expirada',
  'auth.sessionExpired.description': 'Por favor, inicie sesión nuevamente',
  'auth.verify.greeting.appointment': 'Hola {name},',
  'auth.verify.greeting.appointmentNoName': 'Hola,',
  'auth.verify.greeting.normal': '¡Bienvenido de nuevo {name},',
  'auth.verify.greeting.normalNoName': '¡Bienvenido de nuevo,',
  'auth.verify.screenTitle': 'Verificar Inicio de Sesión con Fecha de Nacimiento',
  'auth.verify.submit': 'Verificar e Iniciar Sesión',
  'auth.verify.subtitle.appointment': 'Antes de que comience su cita, necesitamos verificar su fecha de nacimiento',
  'auth.verify.subtitle.normal': 'Ingrese su fecha de nacimiento a continuación para que podamos confirmar que es usted',
  'auth.verify.error.linkNotWorking.title': 'Ese enlace no parece estar funcionando.',
  'auth.verify.error.linkNotWorking.description': 'Intenta iniciar sesión nuevamente.',
  'auth.verify.error.linkNotWorking.button': 'Iniciar sesión nuevamente',

  // Link Sent Process
  'auth.linkSent.messageSent.sms': 'He enviado un mensaje a {phone}.',
  'auth.linkSent.messageSent.email': 'He enviado un correo electrónico a {maskedEmail}.',
  'auth.linkSent.messageSent.both': 'He enviado un mensaje a {phone} y un correo electrónico a {maskedEmail}.',
  'auth.linkSent.description.single': 'Contiene un enlace para iniciar sesión.',
  'auth.linkSent.description.plural': 'Contienen enlaces para iniciar sesión.',
  'auth.linkSent.retry.sms': 'No recibí el mensaje.',
  'auth.linkSent.retry.email': 'No recibí el correo electrónico.',
  'auth.linkSent.retry.both': 'No recibí el mensaje ni el correo electrónico.',

  // Link Retry Process
  'auth.linkRetry.title.email': 'Podemos reenviar el enlace.',
  'auth.linkRetry.title.sms': 'Podemos reenviar el enlace o puedes intentar con tu dirección de correo electrónico.',
  'auth.linkRetry.supportNote': 'Si aún tienes problemas, por favor contacta con atención al cliente.',
  'auth.linkRetry.button.sendMessage': 'Enviar un nuevo mensaje',
  'auth.linkRetry.button.useEmail': 'Usar correo electrónico en su lugar',

  // Channel Verified Process
  'auth.channelVerified.greeting': '¡Excelente {name}!',
  'auth.channelVerified.verified.email': 'Gracias por verificar tu correo electrónico.',
  'auth.channelVerified.verified.phone': 'Gracias por verificar tu teléfono.',
  'auth.channelVerified.button.login': 'Iniciar sesión',
  'auth.channelVerified.error.title': 'Ese enlace no parece estar funcionando.',
  'auth.channelVerified.error.description': 'Intenta iniciar sesión.',

  // Login Process
  'login.screenTitle': 'Pantalla de inicio de sesión',
  'login.signIn': 'Iniciar sesión',
  'login.signIn.hint': 'Autentícate con tus credenciales',
  'login.title': 'Iniciar sesión',

  // Verification Processes
  'verify.phone.codeMessage': 'He enviado un código a {phone}.',
  'verify.phone.countdownMessage': 'Puedes solicitar un nuevo código en {seconds} segundos',
  'verify.phone.dialog.content': 'Podemos reenviar el código de verificación a tu teléfono.',
  'verify.phone.dialog.resend': 'Reenviar SMS',
  'verify.phone.dialog.title': 'Reenviar Código de Verificación',
  'verify.phone.enterBelow': 'Ingrésalo a continuación para que podamos verificar tu número de teléfono.',
  'verify.phone.incorrectCode': 'Lo sentimos, ese código es incorrecto. Por favor, inténtalo de nuevo.',
  'verify.phone.resend.a11y.hint': 'Presiona para solicitar un nuevo código de verificación',
  'verify.phone.resend.a11y.label': 'Reenviar código de verificación',
  'verify.phone.resendCode': 'Reenviar el código',
  'verify.phone.screen.a11y.label': 'Pantalla de verificación de teléfono',
  'verify.phone.success': 'Teléfono verificado exitosamente',
  'verify.phone.title': 'Verificar Número de Teléfono',
  'verify.phone.troubleshootingContent':
    '• Compruebe que el número de teléfono introducido es correcto.\n• Compruebe que su teléfono no está bloqueando el número de QCare+.\n\nSi sigue teniendo problemas para recibir mensajes, póngase en contacto con el servicio de atención al cliente.',

  // Account Management
  'account.screen.a11y.accountSection': 'Opciones de gestión de cuenta',
  'account.screen.a11y.label': 'Configuración de cuenta y gestión de perfil',
  'account.screen.a11y.logoutHint': 'Te desconecta de la aplicación',
  'account.screen.a11y.settingsSection': 'Configuración de aplicación y preferencias',
  'account.screen.a11y.supportSection': 'Opciones de ayuda y soporte',
  'account.screen.a11y.userInfo': 'Información del perfil de usuario',

  // User Profile Management
  'profile.aboutMe.subtitle': 'Demografía e identidad',
  'profile.aboutMe.title': 'Sobre Mí',
  'profile.account': 'Cuenta',
  'profile.blank.title': 'Elige una opción para abrirla aquí',
  'profile.address.subtitle': 'Tu dirección de casa y envío',
  'profile.address.title': 'Dirección',
  'profile.section.closeLabel': 'Cerrar {label}',
  'profile.section.goBack': 'Presiona para volver',
  'profile.appVersion': 'Versión de la aplicación {version}',
  'profile.badge.a11y.complete': 'Perfil completo',
  'profile.badge.a11y.incomplete': '{count} elementos del perfil incompletos',
  'profile.complete.title': 'Completa Tu Perfil',
  'profile.contact.description': 'Por favor, indícanos la mejor manera de contactarte',
  'profile.contact.description.a11y.label': 'Información de preferencia de contacto',
  'profile.contact.info':
    'Nuestro equipo puede comunicarse contigo sobre tu atención médica o si se necesita información adicional para tu cuenta, recetas o análisis',
  'profile.contact.method.a11y.hint': 'Elige cómo te gustaría ser contactado',
  'profile.contact.method.a11y.label': 'Seleccionar método de contacto preferido',
  'profile.contact.preferences': 'Preferencias de Contacto',
  'profile.contact.preferences.updated': 'Preferencia de contacto actualizada correctamente',
  'profile.contact.save.a11y.hint': 'Presiona para guardar tus preferencias de método de contacto',
  'profile.contact.save.a11y.label': 'Guardar preferencias de contacto',
  'profile.contactMethod': 'Preferencia',
  'profile.contactPreference': 'Preferencia de Contacto',
  'profile.contactPreference.subtitle': 'Cómo nos comunicamos contigo',
  'profile.contactSupport': 'Contactar Soporte',
  'profile.contactSupport.subtitle': 'Resolver un problema específico o hacer una pregunta',
  'profile.demographics.bornAs.label': 'Nacido como',
  'profile.demographics.bornAs.placeholder': 'Selecciona el sexo asignado al nacer',
  'profile.demographics.bornAs.select': 'Selecciona tu género de nacimiento',
  'profile.demographics.ethnicity.label': 'Etnia',
  'profile.demographics.ethnicity.placeholder': 'Selecciona tu etnia',
  'profile.demographics.ethnicity.select': 'Selecciona tu etnia',
  'profile.demographics.gender.label': 'Género',
  'profile.demographics.gender.placeholder': 'Selecciona tu identidad de género',
  'profile.demographics.gender.select': 'Selecciona tu género',
  'profile.demographics.height.add': 'Añade tu altura',
  'profile.demographics.height.label': 'Altura',
  'profile.demographics.height.placeholder': 'Selecciona tu altura en pies y pulgadas',
  'profile.demographics.height.invalid': 'La altura no es válida',
  'profile.demographics.race.label': 'Raza',
  'profile.demographics.race.min': 'Debes elegir al menos una raza',
  'profile.demographics.race.placeholder': 'Selecciona tu raza',
  'profile.demographics.race.select': 'Selecciona tu raza',
  'profile.demographics.relationship.label': 'Estado Civil',
  'profile.demographics.relationship.placeholder': 'Selecciona tu estado civil',
  'profile.demographics.saved': 'Información demográfica y de signos vitales guardada',
  'profile.demographics.sexuality.label': 'Sexualidad',
  'profile.demographics.sexuality.placeholder': 'Selecciona tu orientación sexual',
  'profile.demographics.sexuality.select': 'Selecciona tu sexualidad',
  'profile.demographics.title': 'Demografía e identidad',
  'profile.demographics.weight.add': 'Añade tu peso',
  'profile.demographics.weight.label': 'Peso',
  'profile.demographics.weight.placeholder': 'Ingresa tu peso en libras',
  'profile.demographics.weight.unit': 'lbs',
  'profile.error.emailInUse': 'El correo electrónico ya está en uso',
  'profile.error.phoneInUse': 'El teléfono ya está en uso',
  'profile.faq.subtitle': 'Encuentra respuestas sobre cómo funciona QCare+',
  'profile.faq.title': 'Preguntas Frecuentes',
  'profile.faq.header.title': 'Estamos aquí para ayudarte',
  'profile.faq.header.description': '¿Tienes preguntas? Consulta nuestras preguntas frecuentes o comunícate con nuestro equipo de soporte.',
  // General Section
  'profile.faq.section.general': 'General',
  'profile.faq.section.general.a11y': 'Sección general',
  'profile.faq.general.prep.question': '¿Qué es PrEP y cómo funciona?',
  'profile.faq.general.prep.answer':
    'Mira este video educativo sobre la PrEP para el VIH (Profilaxis Pre-Exposición). Aprende cómo funciona la PrEP, instrucciones de dosificación, efectos secundarios, requisitos de pruebas de laboratorio y qué esperar en tu atención con Q Care Plus.',
  'profile.faq.general.prep.video.title': 'Video Educativo: Comprendiendo la PrEP para el VIH y Tu Atención',
  // Account Section
  'profile.faq.section.account': 'Cuenta',
  'profile.faq.section.account.a11y': 'Sección de cuenta',
  'profile.faq.account.photoId.question': '¿Cómo subo mi identificación con foto?',
  'profile.faq.account.photoId.answer':
    'Navega a la sección de Perfil para subir tu identificación con foto. Aceptamos licencia de conducir, pasaporte o identificación de trabajo/escuela que muestre tu nombre y foto.',
  'profile.faq.account.photoId.button': 'Subir Identificación',
  'profile.faq.account.photoId.button.a11y.label': 'Ir a la sección de subir identificación con foto',
  'profile.faq.account.photoId.button.a11y.hint': 'Presiona para navegar a la carga de identificación',
  'profile.faq.account.restartCare.question': '¿Cómo puedo reiniciar mi atención?',
  'profile.faq.account.restartCare.answer':
    'Si has dejado de recibir atención y deseas reiniciar los servicios, por favor contacta a nuestro equipo de soporte.',
  'profile.faq.account.restartCare.button': 'Contactar Soporte',
  'profile.faq.account.restartCare.button.a11y.label': 'Contactar equipo de soporte',
  'profile.faq.account.restartCare.button.a11y.hint': 'Presiona para enviar un mensaje al soporte',
  // Testing Section
  'profile.faq.section.testing': 'Pruebas',
  'profile.faq.section.testing.a11y': 'Sección de pruebas',
  'profile.faq.testing.time.question': '¿Cuánto tiempo toma la prueba en persona?',
  'profile.faq.testing.time.answer':
    'Una cita típica de laboratorio en persona toma alrededor de 15 minutos una vez que llegas a la clínica. Hay más de 2,200 ubicaciones en todo el país donde puedes hacerte las pruebas.',
  'profile.faq.testing.results.question': '¿Qué tan rápido recibiré mis resultados de prueba?',
  'profile.faq.testing.results.answer':
    'Por lo general, toma de 5 a 7 días hábiles para que el laboratorio procese tu kit de prueba. Luego, tu proveedor revisará tus resultados y se comunicará contigo para discutir cualquier inquietud.',
  'profile.faq.testing.results.button': 'Ver Resultados de Laboratorio',
  'profile.faq.testing.results.button.a11y.label': 'Ver resultados de laboratorio',
  'profile.faq.testing.externalLabs.question': '¿Cómo puedo compartir resultados de laboratorio de otras fuentes?',
  'profile.faq.testing.externalLabs.answer':
    'Puedes cargar resultados de laboratorio externos en la sección de Trabajo de Laboratorio. Una vez cargados, tu proveedor los revisará dentro de 24-48 horas.',
  'profile.faq.testing.externalLabs.button': 'Cargar Resultados de Laboratorio',
  'profile.faq.testing.externalLabs.button.a11y.label': 'Ir a la sección de trabajo de laboratorio',
  // Lab Collection Instructions (Ash)
  'profile.faq.testing.ash.section': 'Instrucciones de Recolección de Muestras de Ash',
  'profile.faq.testing.ash.section.a11y': 'Sección de instrucciones de recolección de muestras de Ash',
  'profile.faq.testing.ash.driedBlood.question': '¿Cómo recolecto una muestra de gota de sangre seca?',
  'profile.faq.testing.ash.driedBlood.answer':
    'Mira este video instructivo para aprender cómo recolectar correctamente una muestra de gota de sangre seca usando tu kit de prueba en casa.',
  'profile.faq.testing.ash.driedBlood.video.title': 'Instrucciones de Recolección de Gota de Sangre Seca',
  'profile.faq.testing.ash.urine.question': '¿Cómo recolecto una muestra de orina?',
  'profile.faq.testing.ash.urine.answer':
    'Mira este video instructivo para aprender cómo recolectar correctamente una muestra de orina usando tu kit de prueba en casa.',
  'profile.faq.testing.ash.urine.video.title': 'Instrucciones de Recolección de Muestra de Orina',
  'profile.faq.testing.ash.oralSwab.question': '¿Cómo recolecto una muestra de hisopo oral?',
  'profile.faq.testing.ash.oralSwab.answer':
    'Mira este video instructivo para aprender cómo recolectar correctamente una muestra de hisopo oral usando tu kit de prueba en casa.',
  'profile.faq.testing.ash.oralSwab.video.title': 'Instrucciones de Recolección de Hisopo Oral',
  'profile.faq.testing.ash.rectalSwab.question': '¿Cómo recolecto una muestra de hisopo rectal?',
  'profile.faq.testing.ash.rectalSwab.answer':
    'Mira este video instructivo para aprender cómo recolectar correctamente una muestra de hisopo rectal usando tu kit de prueba en casa.',
  'profile.faq.testing.ash.rectalSwab.video.title': 'Instrucciones de Recolección de Hisopo Rectal',
  // Lab Collection Instructions (MTL)
  'profile.faq.testing.mtl.section': 'Instrucciones de recolección de muestras de laboratorio en casa',
  'profile.faq.testing.mtl.section.a11y': 'Instrucciones de recolección de muestras de laboratorio en casa',
  'profile.faq.testing.mtl.bloodSpot.title': 'Recolección de sangre',
  'profile.faq.testing.mtl.bloodSpot.description': 'Tarjeta de recolección de sangre',
  'profile.faq.testing.mtl.bloodSpot.before':
    'Antes de empezar: Lea todos los pasos antes de empezar. El kit es de un solo uso. No lo use si el empaque o su contenido están dañados.',
  'profile.faq.testing.mtl.bloodSpot.step.1.title': 'Prepare la tarjeta y sus manos',
  'profile.faq.testing.mtl.bloodSpot.step.1.body':
    'Escriba su nombre, fecha de nacimiento y fecha de recolección en la tarjeta. Use MM/DD/AAAA (mes/día/año). Abra la solapa sin tocar el papel. Lávese las manos con agua tibia durante al menos 30 segundos y agítelas con fuerza durante 15 segundos.',
  'profile.faq.testing.mtl.bloodSpot.step.1.image.1.alt': 'Manos lavándose con jabón.',
  'profile.faq.testing.mtl.bloodSpot.step.2.title': 'Limpie y pinche la yema del dedo',
  'profile.faq.testing.mtl.bloodSpot.step.2.body':
    'Limpie la yema con la toallita con alcohol. El kit recomienda el dedo medio o anular de la mano no dominante. Gire para quitar la tapa de la lanceta. Presione la punta pequeña contra un lado de la yema, cerca de la punta del dedo, hasta oír un clic. Use la lanceta una sola vez.',
  'profile.faq.testing.mtl.bloodSpot.step.2.image.1.alt': 'Lanceta colocada contra un lado de la yema del dedo.',
  'profile.faq.testing.mtl.bloodSpot.step.3.title': 'Llene todos los círculos',
  'profile.faq.testing.mtl.bloodSpot.step.3.body':
    'Mantenga la mano a la altura de la cintura y masajee el dedo desde la base hacia la punta. Deje caer sangre en el centro de cada círculo. No toque el papel con el dedo. Llene todos los círculos hasta que la sangre empape el papel de lado a lado. Puede pasar un poco de las líneas.',
  'profile.faq.testing.mtl.bloodSpot.step.3.image.1.alt': 'Una gota de sangre toca un círculo de la tarjeta de recolección.',
  'profile.faq.testing.mtl.bloodSpot.step.3.image.2.alt':
    'Correcto: todos los círculos llenos de sangre. Incorrecto: manchas de sangre incompletas o desiguales.',
  'profile.faq.testing.mtl.bloodSpot.step.4.title': 'Deje secar la tarjeta',
  'profile.faq.testing.mtl.bloodSpot.step.4.body':
    'Déjela plana y al aire durante al menos 30 minutos. No use calor ni sople sobre ella. Espere hasta que esté completamente seca antes de guardarla.',
  'profile.faq.testing.mtl.bloodSpot.step.4.image.1.alt': 'Tarjeta de recolección de sangre con la solapa abierta.',
  'profile.faq.testing.mtl.bloodSpot.step.5.title': 'Selle la bolsa para la muestra',
  'profile.faq.testing.mtl.bloodSpot.step.5.body':
    'Cierre la solapa. Coloque la tarjeta seca y el paquete desecante (el paquete que absorbe humedad) en su propia bolsa de riesgo biológico. Selle bien. Después siga la guía Prepare y devuelva su kit.',
  'profile.faq.testing.mtl.bloodSpot.step.5.image.1.alt': 'Bolsa de riesgo biológico separada para la tarjeta de sangre.',
  'profile.faq.testing.mtl.urineFillLine.title': 'Recolección de orina',
  'profile.faq.testing.mtl.urineFillLine.description': 'Etiqueta naranja “URINE / Orina” · Tubo con líneas de llenado',
  'profile.faq.testing.mtl.urineFillLine.before':
    'Antes de empezar: Kit de un solo uso; no use materiales dañados. No beba alcohol el día de la recolección. No recolecte durante la menstruación ni en las 24 horas después de las relaciones sexuales. No vacíe el líquido conservante.',
  'profile.faq.testing.mtl.urineFillLine.step.1.title': 'Espere antes de recolectar',
  'profile.faq.testing.mtl.urineFillLine.step.1.body':
    'No orine durante al menos 1 hora antes de recolectar. Se prefiere la primera orina de la mañana.',
  'profile.faq.testing.mtl.urineFillLine.step.1.image.1.alt': 'Reloj que indica una espera de una hora antes de recolectar la orina.',
  'profile.faq.testing.mtl.urineFillLine.step.2.title': 'Recolecte la orina en el vaso',
  'profile.faq.testing.mtl.urineFillLine.step.2.body':
    'Orine directamente en el vaso suministrado hasta llenarlo entre un tercio y la mitad.',
  'profile.faq.testing.mtl.urineFillLine.step.2.image.1.alt': 'Orina recolectada en el vaso suministrado.',
  'profile.faq.testing.mtl.urineFillLine.step.3.title': 'Pase la orina y revise las líneas',
  'profile.faq.testing.mtl.urineFillLine.step.3.body':
    'Use la pipeta (el gotero pequeño) para pasar la orina al tubo o los tubos con la etiqueta naranja “URINE / Orina”. El líquido total debe quedar entre las líneas marcadas. No pase la línea máxima; la hoja indica que una muestra demasiado llena será rechazada.',
  'profile.faq.testing.mtl.urineFillLine.step.3.image.1.alt': 'Pipeta que transfiere orina al tubo con las líneas de llenado señaladas.',
  'profile.faq.testing.mtl.urineFillLine.step.4.title': 'Cierre y etiquete cada tubo',
  'profile.faq.testing.mtl.urineFillLine.step.4.body':
    'Cierre cada tubo de manera uniforme y firme. Escriba su fecha de nacimiento (DOB) y la fecha de recolección de hoy (Coll. Date / Coll. DT) en MM/DD/AAAA (mes/día/año). Incluya los ceros iniciales.',
  'profile.faq.testing.mtl.urineFillLine.step.4.image.1.alt':
    'Etiqueta naranja URINE / Orina con campos para la fecha de nacimiento y la fecha de recolección.',
  'profile.faq.testing.mtl.urineFillLine.step.5.title': 'Selle la bolsa para la muestra',
  'profile.faq.testing.mtl.urineFillLine.step.5.body':
    'Ponga cada tubo en su propia bolsa de riesgo biológico con la almohadilla absorbente. Selle bien y siga la guía Prepare y devuelva su kit.',
  'profile.faq.testing.mtl.urineFillLine.step.5.image.1.alt': 'Tubo de orina dentro de una bolsa de riesgo biológico.',
  'profile.faq.testing.mtl.neatUrine.title': 'Recolección de orina: Neat Urine',
  'profile.faq.testing.mtl.neatUrine.description': 'Solo para el kit con la hoja “Neat Urine Collection Kit”',
  'profile.faq.testing.mtl.neatUrine.before':
    'Antes de empezar: Kit de un solo uso; no use materiales dañados. No recolecte durante la menstruación ni en las 24 horas después de las relaciones sexuales. No vacíe el líquido conservante, si lo hay.',
  'profile.faq.testing.mtl.neatUrine.step.1.title': 'Espere antes de recolectar',
  'profile.faq.testing.mtl.neatUrine.step.1.body':
    'No orine durante al menos 1 hora antes de recolectar. Se prefiere la primera orina de la mañana.',
  'profile.faq.testing.mtl.neatUrine.step.1.image.1.alt': 'Reloj que indica una espera de una hora antes de recolectar la orina.',
  'profile.faq.testing.mtl.neatUrine.step.2.title': 'Recolecte la orina en el vaso',
  'profile.faq.testing.mtl.neatUrine.step.2.body':
    'Orine directamente en el vaso suministrado hasta llenarlo entre un tercio y la mitad, como indica la hoja Neat Urine.',
  'profile.faq.testing.mtl.neatUrine.step.2.image.1.alt': 'Orina recolectada en el vaso suministrado.',
  'profile.faq.testing.mtl.neatUrine.step.3.title': 'Pase la orina al tubo',
  'profile.faq.testing.mtl.neatUrine.step.3.body':
    'Use la pipeta (el gotero pequeño) para pasar la orina al tubo Neat Urine. Llénelo hasta 10–13 mL, aproximadamente tres cuartos de su capacidad, según la hoja de este kit. No use esta cantidad para el kit de etiqueta naranja.',
  'profile.faq.testing.mtl.neatUrine.step.3.image.1.alt': 'Pipeta que transfiere orina al tubo de recolección Neat Urine.',
  'profile.faq.testing.mtl.neatUrine.step.4.title': 'Cierre y etiquete el tubo',
  'profile.faq.testing.mtl.neatUrine.step.4.body':
    'Cierre el tubo de manera uniforme y firme. Escriba su fecha de nacimiento (DOB) y la fecha de recolección de hoy (Coll. DT) en MM/DD/AAAA (mes/día/año). Incluya los ceros iniciales.',
  'profile.faq.testing.mtl.neatUrine.step.4.image.1.alt':
    'Etiqueta del tubo Neat Urine con campos para la fecha de nacimiento y la fecha de recolección.',
  'profile.faq.testing.mtl.neatUrine.step.5.title': 'Selle la bolsa para la muestra',
  'profile.faq.testing.mtl.neatUrine.step.5.body':
    'Coloque el tubo en su bolsa de riesgo biológico con la almohadilla absorbente. Selle bien y siga la guía Prepare y devuelva su kit.',
  'profile.faq.testing.mtl.neatUrine.step.5.image.1.alt': 'Tubo Neat Urine dentro de una bolsa de riesgo biológico.',
  'profile.faq.testing.mtl.packReturn.title': 'Prepare y devuelva su kit',
  'profile.faq.testing.mtl.packReturn.description': 'Después de recolectar todas las muestras requeridas',
  'profile.faq.testing.mtl.packReturn.before':
    'Antes de empezar: Primero siga las instrucciones de cada muestra de su kit. Ponga una sola muestra por bolsa. La tarjeta de sangre seca va en su propia bolsa con el paquete desecante.',
  'profile.faq.testing.mtl.packReturn.step.1.title': 'Revise todas las etiquetas',
  'profile.faq.testing.mtl.packReturn.step.1.body':
    'Cada tubo debe tener su fecha de nacimiento y fecha de recolección. La tarjeta de sangre también debe tener su nombre. Use MM/DD/AAAA (mes/día/año), con ceros iniciales; por ejemplo, 02/05/2027.',
  'profile.faq.testing.mtl.packReturn.step.1.image.1.alt':
    'Etiqueta del tubo con los campos de fecha de nacimiento y fecha de recolección.',
  'profile.faq.testing.mtl.packReturn.step.2.title': 'Revise la tarjeta de sangre, si la usó',
  'profile.faq.testing.mtl.packReturn.step.2.body':
    'Todos los círculos deben estar llenos y la sangre debe haber empapado el papel de lado a lado. La tarjeta debe secarse al aire al menos 30 minutos y estar totalmente seca antes de guardarla.',
  'profile.faq.testing.mtl.packReturn.step.2.image.1.alt':
    'Correcto: todos los círculos de la tarjeta llenos de sangre. Incorrecto: manchas incompletas o desiguales.',
  'profile.faq.testing.mtl.packReturn.step.3.title': 'Selle cada muestra por separado',
  'profile.faq.testing.mtl.packReturn.step.3.body':
    'Cierre bien y de forma uniforme las tapas. Ponga cada tubo en su propia bolsa de riesgo biológico con la almohadilla absorbente. La tarjeta de sangre seca va en otra bolsa con el paquete desecante. Selle todas las bolsas.',
  'profile.faq.testing.mtl.packReturn.step.3.image.1.alt': 'Bolsa de riesgo biológico para una sola muestra.',
  'profile.faq.testing.mtl.packReturn.step.4.title': 'Cierre el paquete de devolución',
  'profile.faq.testing.mtl.packReturn.step.4.body':
    'Coloque todas las bolsas selladas en la caja suministrada. Meta la caja en la bolsa de devolución prepagada y séllela completamente. Incluya todas las muestras requeridas para sus pruebas.',
  'profile.faq.testing.mtl.packReturn.step.5.title': 'Entregue el kit',
  'profile.faq.testing.mtl.packReturn.step.5.body':
    'Lleve el kit sellado a una oficina del Servicio Postal de Estados Unidos (USPS) (entréguelo en mano a un empleado de la oficina de correos). El folleto dice que recibirá un correo cuando el laboratorio reciba el kit y otro aviso cuando estén listos los resultados. No indica un plazo.',
  'profile.faq.testing.mtl.throatOralSwab.title': 'Hisopo de garganta (oral)',
  'profile.faq.testing.mtl.throatOralSwab.description': 'Etiqueta azul “ORAL / Bucal”',
  'profile.faq.testing.mtl.throatOralSwab.before':
    'Antes de empezar: Kit de un solo uso; no use materiales dañados. Durante 30 minutos antes, no coma, beba, fume ni se cepille los dientes. No beba alcohol el día de la recolección. No vacíe el líquido conservante del tubo.',
  'profile.faq.testing.mtl.throatOralSwab.step.1.title': 'Identifique el hisopo y el tubo',
  'profile.faq.testing.mtl.throatOralSwab.step.1.body':
    'Use el hisopo oral y el tubo con la etiqueta azul “ORAL / Bucal”. Sostenga el hisopo entre la línea tenue de corte (si está presente) y la punta de algodón.',
  'profile.faq.testing.mtl.throatOralSwab.step.1.image.1.alt': 'Hisopo oral junto al tubo con la etiqueta azul ORAL / Bucal.',
  'profile.faq.testing.mtl.throatOralSwab.step.2.title': 'Tome la muestra de la garganta',
  'profile.faq.testing.mtl.throatOralSwab.step.2.body':
    'Introduzca el hisopo en la boca y frote la punta contra la parte posterior de la garganta durante 10 segundos.',
  'profile.faq.testing.mtl.throatOralSwab.step.2.image.1.alt': 'Punta del hisopo colocada contra la parte posterior de la garganta.',
  'profile.faq.testing.mtl.throatOralSwab.step.3.title': 'Coloque el hisopo en el tubo',
  'profile.faq.testing.mtl.throatOralSwab.step.3.body':
    'Retire el hisopo con cuidado y colóquelo en el tubo oral. Dóblelo contra el borde del tubo para partirlo por la marca.',
  'profile.faq.testing.mtl.throatOralSwab.step.3.image.1.alt':
    'Varilla del hisopo partiéndose contra el borde del tubo de recolección oral.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.title': 'Cierre y etiquete el tubo',
  'profile.faq.testing.mtl.throatOralSwab.step.4.body':
    'Cierre la tapa de manera uniforme y firme. Escriba su fecha de nacimiento (DOB) y la fecha de recolección de hoy (Coll. Date / Coll. DT) en MM/DD/AAAA (mes/día/año), con ceros iniciales.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.image.1.alt': 'Etiqueta azul ORAL / Bucal con el campo de fecha de nacimiento.',
  'profile.faq.testing.mtl.throatOralSwab.step.4.image.2.alt': 'Etiqueta azul ORAL / Bucal con el campo de fecha de recolección.',
  'profile.faq.testing.mtl.throatOralSwab.step.5.title': 'Selle la bolsa para la muestra',
  'profile.faq.testing.mtl.throatOralSwab.step.5.body':
    'Ponga el tubo en su bolsa de riesgo biológico con la almohadilla absorbente. Selle bien y siga la guía Prepare y devuelva su kit.',
  'profile.faq.testing.mtl.throatOralSwab.step.5.image.1.alt': 'Tubo del hisopo oral dentro de su bolsa de riesgo biológico.',
  'profile.faq.testing.mtl.rectalAnalSwab.title': 'Hisopo rectal (anal)',
  'profile.faq.testing.mtl.rectalAnalSwab.description': 'Etiqueta verde “ANAL”',
  'profile.faq.testing.mtl.rectalAnalSwab.before':
    'Antes de empezar: Lea todos los pasos. Kit de un solo uso; no use el empaque o contenido dañados. No beba alcohol durante 1 hora antes de la recolección. No vacíe el líquido conservante del tubo.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.title': 'Identifique el hisopo y el tubo',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.body':
    'Use el hisopo anal y el tubo con la etiqueta verde “ANAL”. Sostenga el hisopo entre la línea tenue de corte (si está presente) y la punta de algodón.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.1.image.1.alt': 'Hisopo anal junto al tubo con la etiqueta verde ANAL.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.2.title': 'Recolecte la muestra',
  'profile.faq.testing.mtl.rectalAnalSwab.step.2.body':
    'Introduzca suavemente el hisopo entre 1 y 2 pulgadas (3–5 cm) en el canal anal. Gírelo suavemente durante 5 a 10 segundos.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.title': 'Coloque el hisopo en el tubo',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.body':
    'Retire el hisopo con cuidado y colóquelo en el tubo anal. Dóblelo contra el borde del tubo para partirlo por la marca.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.3.image.1.alt':
    'Varilla del hisopo partiéndose contra el borde del tubo de recolección anal.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.title': 'Cierre y etiquete el tubo',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.body':
    'Cierre la tapa de manera uniforme y firme. Escriba su fecha de nacimiento (DOB) y la fecha de recolección de hoy (Coll. DT) en MM/DD/AAAA (mes/día/año), con ceros iniciales.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.image.1.alt': 'Etiqueta verde ANAL con el campo de fecha de nacimiento.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.4.image.2.alt': 'Etiqueta verde ANAL con el campo de fecha de recolección.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.title': 'Selle la bolsa para la muestra',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.body':
    'Ponga el tubo en su bolsa de riesgo biológico con la almohadilla absorbente. Selle bien y siga la guía Prepare y devuelva su kit.',
  'profile.faq.testing.mtl.rectalAnalSwab.step.5.image.1.alt': 'Tubo del hisopo anal dentro de su bolsa de riesgo biológico.',
  // Pharmacy Section
  'profile.faq.section.pharmacy': 'Farmacia',
  'profile.faq.section.pharmacy.a11y': 'Sección de farmacia',
  'profile.faq.pharmacy.status.question': '¿Cómo averiguo el estado de mi receta?',
  'profile.faq.pharmacy.status.answer':
    'Para verificar el estado de tu receta, navega a la sección de Recetas y selecciona "Ver detalles" para la receta específica que te interesa.',
  'profile.faq.pharmacy.status.button': 'Ver Recetas',
  'profile.faq.pharmacy.status.button.a11y.label': 'Ver recetas',
  'profile.faq.pharmacy.contact.question': '¿Cuál es la información de contacto de mi farmacia?',
  'profile.faq.pharmacy.contact.noPharmacy':
    'No tenemos una farmacia registrada para ti. Por favor, contacta a tu proveedor para más información.',
  'profile.faq.pharmacy.contact.call': 'Llamar a {pharmacyName}',
  // Medical Section
  'profile.faq.section.medical': 'Médico',
  'profile.faq.section.medical.a11y': 'Sección médica',
  'profile.faq.medical.pep.question': '¿Cómo puedo reunirme con un proveedor si he estado expuesto al VIH?',
  'profile.faq.medical.pep.answer':
    'Si has estado potencialmente expuesto al VIH dentro de las últimas 72 horas, puedes reunirte con tu proveedor para determinar si la PEP es adecuada para ti.',
  'profile.faq.medical.pep.button': 'Programar Cita PEP',
  'profile.faq.medical.pep.button.a11y.label': 'Programar cita PEP',
  // Support Section
  'profile.faq.support.section.a11y': 'Sección de soporte',
  'profile.faq.support.title': '¿Todavía tienes preguntas?',
  'profile.faq.support.description':
    'Nuestro equipo de soporte está aquí para ayudarte con cualquier pregunta o inquietud que puedas tener.',
  'profile.faq.support.button': 'Contactar Soporte',
  'profile.faq.support.button.a11y.label': 'Contactar equipo de soporte',
  'profile.firstName': 'Nombre',
  'profile.firstName.placeholder': 'Introduce tu nombre',
  'profile.form.a11y.label': 'Formulario de información personal',
  'profile.healthAssistance.subtitle': 'Verificar elegibilidad para financiamiento',
  'profile.healthAssistance.title': 'Asistencia de Salud',
  'profile.primaryCareProvider.title': 'Médico de Atención Primaria',
  'profile.primaryCareProvider.subtitle': 'Información de tu médico de atención primaria',
  'profile.healthHistory.subtitle': 'Registros médicos',
  'profile.healthHistory.title': 'Historial de Salud',
  'profile.help': 'Ayuda',
  'profile.vitals.description': 'Rastree sus signos vitales con el tiempo',
  'profile.vitals.subtitle': 'Peso, altura, presión arterial, frecuencia cardíaca',
  'profile.vitals.title': 'Signos Vitales',
  'profile.insurance.subtitle': 'Detalles del seguro',
  'profile.insurance.title': 'Seguro',
  'profile.insurances.add': 'Añadir nuevo seguro',
  'profile.insurances.add.hint': 'Presiona para añadir una nueva tarjeta de seguro',
  'profile.insurances.title': 'Seguros',
  'profile.language': 'Idioma',
  'profile.language.english': 'English',
  'profile.language.label': 'Idioma',
  'profile.language.placeholder': 'Selecciona tu idioma preferido',
  'profile.language.spanish': 'Español',
  'profile.language.updated': 'Preferencia de idioma actualizada',
  'profile.lastName': 'Apellido',
  'profile.lastName.placeholder': 'Introduce tu apellido',
  'profile.logout.message': '¿Estás seguro de que quieres cerrar sesión?',
  'profile.logout.title': 'Cerrar Sesión',
  'profile.missing.emailVerification': 'Por favor, verifica tu correo electrónico',
  'profile.missing.phoneVerification': 'Por favor verifica tu número de teléfono',
  'profile.missing.language': 'Selecciona tu idioma preferido',
  'profile.missing.profilePicture': 'Agrega una foto de perfil',
  'profile.missing.firstName': 'Agrega un nombre',
  'profile.missing.lastName': 'Agrega un apellido',
  'profile.missing.email': 'Agrega una dirección de correo electrónico',
  'profile.missing.dob': 'Agrega una fecha de nacimiento',
  'profile.missing.photoId': 'Sube una imagen de tu identificación oficial',
  'profile.missing.contactMethod': 'Por favor elige una preferencia de contacto',
  'profile.nickname': 'Nombre Elegido',
  'profile.nickname.placeholder': 'Introduce tu nombre elegido',
  'profile.notifications': 'Notificaciones',
  'profile.notifications.description': 'Solo te enviamos notificaciones sobre información importante acerca de tu cuenta y atención médica',
  'profile.notifications.opted_out_start_description':
    'Has optado por no recibir mensajes de texto. Para comenzar a recibir mensajes de texto, envía START a {phone}.',
  'profile.notifications.opted_out_description':
    'Has optado por no recibir mensajes de texto. Habilita las notificaciones de texto para recibir actualizaciones importantes sobre tu atención.',
  'profile.notifications.opted_out_email_info':
    'Mientras estés dado de baja, te enviaremos mensajes de correo electrónico con información sobre tu cuenta y atención médica.',
  'profile.notifications.opt_in_button': 'Habilitar Notificaciones de Texto',
  'profile.notifications.opt_in_success': 'Te has suscrito a las alertas por SMS',
  'profile.notifications.opt_in.a11y.label': 'Habilitar notificaciones de texto',
  'profile.notifications.opt_in.a11y.hint': 'Toca para suscribirte para recibir notificaciones de texto',
  'profile.notifications.send_start_button': 'Enviar START',
  'profile.notifications.send_start.a11y.label': 'Enviar START por mensaje de texto',
  'profile.notifications.send_start.a11y.hint':
    'Abre tu aplicación de mensajes para enviar START y volver a activar las notificaciones de texto',
  'profile.notifications.subtitle': 'Preferencias de alertas',
  'profile.notifications.updated': 'Preferencias de notificación actualizadas correctamente',
  'profile.personalInfo.saved': 'Información personal guardada',
  'profile.photoId': 'Foto de Identificación Oficial',
  'profile.photoId.title': 'Photo ID',
  'profile.photoId.upload': 'Sube una imagen de tu identificación oficial',
  'profile.preferredCommunication': 'Enviar notificaciones a',
  'profile.profile.subtitle': 'Información personal',
  'profile.profilePicture': 'Foto de Perfil',
  'profile.profilePicture.a11y.add': 'Agregar foto de perfil',
  'profile.profilePicture.a11y.change': 'Cambiar foto de perfil',
  'profile.profilePicture.a11y.hint': 'Abre opciones de selección de foto',
  'profile.profilePicture.add': 'Toca para agregar foto',
  'profile.profilePicture.change': 'Toca para cambiar foto',
  'profile.profilePicture.edit.a11y': 'Editar foto de perfil',
  'profile.profilePicture.remove.toast': 'Foto de perfil eliminada',
  'profile.profilePicture.update.toast': 'Foto de perfil actualizada',
  'profile.progressItems.aboutMe.description': 'Completar demografía e identidad',
  'profile.progressItems.aboutMe.label': 'Sobre Mí',
  'profile.progressItems.contactPreference.description': 'Establecer método de contacto preferido',
  'profile.progressItems.contactPreference.label': 'Preferencia de Contacto',
  'profile.progressItems.healthAssistance.description': 'Completar información de asistencia de salud',
  'profile.progressItems.healthAssistance.label': 'Asistencia de Salud',
  'profile.progressItems.insurance.description': 'Añadir detalles del seguro',
  'profile.progressItems.insurance.label': 'Seguro',
  'profile.progressItems.photoId.description': 'Subir identificación con foto',
  'profile.progressItems.photoId.label': 'Identificación con Foto',
  'profile.progressItems.profile.description': 'Completar información personal',
  'profile.progressItems.profile.label': 'Perfil',
  'profile.progressItems.vitals.description': 'Añadir información de signos vitales',
  'profile.progressItems.vitals.label': 'Signos Vitales',
  'profile.pronouns': 'Pronombres',
  'profile.pronouns.placeholder': 'Selecciona tus pronombres',
  'profile.save.a11y.hint': 'Presiona para guardar tu información de perfil',
  'profile.save.a11y.label': 'Guardar cambios de perfil',
  'profile.settings': 'Configuración',
  'profile.support': 'Soporte',
  'profile.theme': 'Tema',
  'profile.theme.subtitle': 'Toca para cambiar entre opciones',
  'profile.title': 'Perfil',
  'profile.verification.email.action': 'Verificar',
  'profile.verification.email.description': 'Verifica tu correo electrónico para recuperación de cuenta',
  'profile.verification.email.sent': 'Te hemos enviado un correo electrónico con un enlace para verificar tu dirección de correo',
  'profile.verification.email.title': 'Verificación de Correo Electrónico',
  'profile.verification.phone.action': 'Verificar',
  'profile.verification.phone.description': 'Asegura tu cuenta con verificación por código SMS',
  'profile.verification.phone.title': 'Verificación de Teléfono',
  'profile.verify.email': 'Verificar tu correo electrónico',
  'profile.verify.phone': 'Verificar tu teléfono',

  // ID Verification
  'photo_id.after_appointment.description':
    '¡Has programado tu cita exitosamente!\n\nPor favor sube tu identificación emitida por el gobierno. Asegúrate de que tu nombre, fecha de nacimiento y foto sean visibles.\n\nSi no puedes subirla ahora, por favor tenla lista para el proveedor al inicio de tu cita.',
  'photo_id.description':
    'Para fines de identificación, necesitamos una foto de su identificación emitida por el gobierno, como una licencia de conducir, pasaporte o tarjeta de identificación estatal.',
  'photo_id.error.upload.message': 'Hubo un problema al subir su identificación con foto. Por favor, inténtelo de nuevo.',
  'photo_id.error.upload.title': 'Error de carga',
  'photo_id.no_photo': 'No se ha subido ninguna identificación con foto',
  'photo_id.success.message': 'Su identificación con foto ha sido subida con éxito.',
  'photo_id.success.title': 'Éxito',
  'photo_id.title': 'Subir Identificación',
  'photo_id.footer':
    'Los IDs fotográficos aceptables son: licencia de conducir, pasaporte o ID de trabajo/escuela que muestre tanto tu nombre como tu foto.',
  'photo_id.button.skip': 'Saltar por ahora',
  'photo_id.tip':
    'Consejo: Subir tu ID fotográfico ahora ahorrará tiempo durante tu cita. Si omites este paso, tu proveedor necesitará capturar una pantalla de tu ID al inicio de tu visita.',

  // Address Management
  'address.cbo.a11y.label': 'Diálogo de selección de socio comunitario',
  'address.cbo.a11y.select': 'Seleccionar {name}',
  'address.cbo.description': 'Tu nueva ubicación requiere unirte a un nuevo socio comunitario que sirve a tu área.',
  'address.cbo.title': 'Elige un socio comunitario',
  'address.county.a11y.label': 'Diálogo de selección de condado',
  'address.county.a11y.select': 'Seleccionar condado {county}',
  'address.county.description': 'Tu nueva ubicación requiere unirte a un nuevo socio comunitario que sirve a tu área.',
  'address.county.title': '¿En qué condado vives?',
  'address.edit.a11y.screen': 'Pantalla de formulario de dirección',
  'address.edit.a11y.submit': 'Guardar información de dirección',
  'address.edit.homeAddress.title': 'Dirección de Casa',
  'address.edit.notify.error.description': '¡Lamentamos verte partir!',
  'address.edit.notify.success': 'Información guardada',
  'address.edit.notify.success.description': 'Te notificaremos cuando un socio comunitario esté disponible en tu área',
  'address.edit.shippingAddress.title': 'Dirección de Envío',
  'address.edit.success': 'Información de dirección guardada',
  'address.edit.verifyConfirm': 'Verificar y Confirmar',
  'address.timezone.title': 'Confirma tu Zona Horaria',
  'address.timezone.description.change':
    'El cambio de dirección puede requerir actualizar tu zona horaria. Por favor confirma o selecciona una nueva zona horaria.',
  'address.timezone.description.select': 'Por favor selecciona tu zona horaria para asegurar la programación precisa de citas.',
  'address.timezone.current.label': 'Selección Actual',
  'address.timezone.select.available': 'Zonas Horarias Disponibles',
  'address.timezone.select.label': 'Seleccionar Zona Horaria',
  'address.insurance.a11y.label': 'Diálogo de verificación de seguro',
  'address.noinsurance.a11y.label': 'Diálogo de seguro requerido',
  'address.noinsurance.title': 'Seguro requerido',
  'address.medicare.a11y.label': 'Diálogo de verificación de Medi-Cal',
  'address.nocbo.a11y.label': 'Diálogo de socio comunitario no disponible',
  'address.nocbo.contact': 'Contactar con atención al cliente',
  'address.nocbo.title': 'No hay socio comunitario disponible',

  // Address Verification
  'addressVerification.title': 'Reiniciar Atención',

  // General Health
  'health.gridItem.a11y.label': '{title}. {description}',
  'health.section.care': 'Tu atención',
  'health.section.services': 'Servicios de salud',
  'health.section.support': 'Soporte',
  'health.screen.a11y.healthServices': 'Cuadrícula de servicios de salud',
  'health.screen.a11y.label': 'Pantalla principal de servicios de salud',
  'health.screen.a11y.quickActions': 'Acciones rápidas de salud',
  'health.screen.a11y.support': 'Opciones de soporte y asistencia',
  'health.screen.appointments.description': 'Ver y administrar tu horario',
  'health.screen.appointments.pageDescription':
    'Programa visitas, únete a videollamadas y mantén un seguimiento de todas tus citas médicas en un lugar conveniente.',
  'health.screen.appointments.title': 'Citas',
  'health.screen.assessments.description': 'Completar revisiones de salud',
  'health.screen.assessments.pageDescription':
    'Completa evaluaciones rápidas de salud que ayudan a tu equipo de atención a brindarte tratamiento personalizado y efectivo.',
  'health.screen.assessments.title': 'Evaluaciones',
  'health.screen.documents.description': 'Ver tus registros médicos',
  'health.screen.documents.pageDescription':
    'Accede a todos tus registros médicos y documentos importantes en una biblioteca digital segura y organizada.',
  'health.screen.documents.title': 'Documentos',
  'health.screen.healthHistory.description': 'Revisar tus condiciones previas',
  'health.screen.healthHistory.title': 'Historial de Salud',
  'health.screen.labResults.description': 'Acceder a tus resultados de pruebas',
  'health.screen.labResults.title': 'Resultados de Laboratorio',
  'health.screen.labs.pageDescription':
    'Sigue tu trayectoria de salud con resultados de laboratorio detallados e información que ayuda a guiar tus decisiones de atención médica.',
  'health.screen.needAssistance.description': 'Nuestro equipo de soporte está aquí para ayudar',
  'health.screen.needAssistance.title': '¿Necesitas Asistencia?',
  'health.screen.medReminders.title': 'Recordatorios de Medicamentos',
  'health.screen.medReminders.description': 'Configura recordatorios para tomar tu medicamento',
  'health.screen.prescriptions.description': 'Administrar tus medicamentos',
  'health.screen.prescriptions.pageDescription':
    'Administra tus medicamentos con facilidad: rastrea reposiciones, el estado de entrega y mantente al día con tu rutina de salud.',
  'health.screen.prescriptions.title': 'Recetas',

  // Health History
  'healthHistory.description':
    'Por favor, tómese un momento para revisar su Historial de Salud como se muestra a continuación. Si hay información nueva que no se muestra actualmente, siéntase libre de agregarla.',
  'healthHistory.empty': '{type}',
  'healthHistory.empty.instructions': 'Aún no hay nada agregado. Agregue uno o marque la casilla si no tiene ninguno.',
  'healthHistory.empty.noKnown': 'No tengo {type} conocidos/as',
  'healthHistory.empty.requiresAppointment': 'Se requiere tener reservada una cita antes de que poder completar su Historial de Salud',
  'healthHistory.empty.requiresProvider': 'Necesita un proveedor asignado antes de actualizar su Historial de Salud',
  'healthHistory.form.dosage': 'Dosis',
  'healthHistory.form.frequency': 'Frecuencia',
  'healthHistory.form.reasonForMedication': 'Razón del medicamento',
  'healthHistory.form.reasonForMedication.short': 'Razón',
  'healthHistory.form.name': 'Nombre',
  'healthHistory.form.onsetYear': 'Año',
  'healthHistory.form.reaction': 'Reacción',
  'healthHistory.form.selectYear': 'Seleccionar Año',
  'healthHistory.form.severity': 'Gravedad',
  'healthHistory.form.year': 'Año',
  'healthHistory.item.dosage': 'Dosis: {dosage}',
  'healthHistory.item.frequency': 'Frecuencia: {frequency}',
  'healthHistory.item.reasonForMedication': 'Razón: {reason}',
  'healthHistory.item.na': 'n/a',
  'healthHistory.item.onset': 'Inicio: {year}',
  'healthHistory.item.reaction': 'Reacción: {reaction}',
  'healthHistory.item.severity': 'Gravedad: {severity}',
  'healthHistory.item.year': 'Año: {year}',
  'healthHistory.lastUpdated': 'Última actualización:',
  'healthHistory.medications.instructionText':
    'Por favor enumere todos los medicamentos recetados, medicamentos de venta libre y suplementos que esté tomando actualmente. Para cada medicamento o suplemento agregado, asegúrese de enumerar la razón de uso en la sección de condiciones.',
  'healthHistory.singular.allergy': 'Alergia',
  'healthHistory.singular.condition': 'Condición',
  'healthHistory.singular.immunization': 'Inmunización',
  'healthHistory.singular.medication': 'Medicamento',
  'healthHistory.singular.surgery': 'Cirugía',
  'healthHistory.tabs.allergies': 'Alergias',
  'healthHistory.tabs.conditions': 'Condiciones',
  'healthHistory.tabs.immunizations': 'Inmunizaciones',
  'healthHistory.tabs.medications': 'Medicamentos',
  'healthHistory.tabs.surgeries': 'Cirugías',
  'healthHistory.update.addNew': 'Agregar nuevo/a {type}',
  'healthHistory.update.disclaimer':
    'Una vez que se actualiza el historial de salud, cualquier cambio adicional solo puede ser realizado por su proveedor de atención médica',
  'healthHistory.update.saveSuccess': 'Información de {type} guardada',

  // Vitals
  'vitals.bloodPressure.diastolic.label': 'Diastólica',
  'vitals.bloodPressure.diastolic.placeholder': '80',
  'vitals.bloodPressure.label': 'Presión arterial',
  'vitals.bloodPressure.recordedAt.label': 'Fecha de registro',
  'vitals.bloodPressure.systolic.label': 'Sistólica',
  'vitals.bloodPressure.systolic.placeholder': '120',
  'vitals.bloodPressure.unit': 'mmHg',
  'vitals.bloodPressure.validation.systolicGreater': 'La sistólica debe ser mayor que la diastólica',
  'vitals.empty.noRecorded': 'No hay {vital} registrado aún',
  'vitals.heartRate.label': 'Frecuencia cardíaca',
  'vitals.heartRate.unit': 'lpm',
  'vitals.heartRate.recordedAt.label': 'Fecha de registro',
  'vitals.height.label': 'Altura',
  'vitals.height.unit': 'pulgadas',
  'vitals.latest': 'Última:',
  'vitals.weight.label': 'Peso',
  'vitals.weight.unit': 'lbs',
  'vitals.update.saveSuccess': '{type} guardado exitosamente',

  // Health Assistance
  'healthAssistance.cbo.notAcceptingUninsured': 'El CBO no acepta pacientes sin seguro',
  'healthAssistance.description':
    'Usamos esta información para determinar si hay programas externos que puedan ayudar a financiar tu atención',
  'healthAssistance.form.a11y.label': 'Formulario de información de asistencia financiera',
  'healthAssistance.gilead.label': 'Gilead AA#',
  'healthAssistance.gilead.placeholder': 'Número de Gilead Advancing Access',
  'healthAssistance.household.invalid': 'Por favor ingresa un tamaño de hogar válido',
  'healthAssistance.household.label': 'Número de miembros del hogar',
  'healthAssistance.household.placeholder': 'Número de miembros del hogar',
  'healthAssistance.income.invalid': 'Por favor ingresa un ingreso válido',
  'healthAssistance.income.label': 'Ingreso del hogar',
  'healthAssistance.income.placeholder': 'Ingreso del hogar',
  'healthAssistance.medicaid.label': 'Medicaid',
  'healthAssistance.save.a11y.hint': 'Presiona para guardar tus detalles de asistencia financiera',
  'healthAssistance.save.a11y.label': 'Guardar información de asistencia de salud',
  'healthAssistance.saved': 'Información de asistencia de salud guardada',
  'healthAssistance.screen.a11y.label': 'Pantalla de formulario de asistencia de salud',
  'healthAssistance.social.invalid': 'Por favor ingresa un SSN válido',
  'healthAssistance.social.label': 'Últimos 4 dígitos del Número de Seguro Social',
  'healthAssistance.social.placeholder': 'Últimos 4 del SSN',
  'healthAssistance.title': 'Verificar elegibilidad para financiamiento',
  'healthAssistance.va.label': 'Veterano',

  // Primary Care Provider
  'primaryCareProvider.saved': 'Información del médico de atención primaria guardada',
  'primaryCareProvider.save.a11y.label': 'Guardar información del médico de atención primaria',
  'primaryCareProvider.save.a11y.hint': 'Presiona para guardar los detalles de tu médico de atención primaria',
  'primaryCareProvider.clinic.label': 'Clínica',
  'primaryCareProvider.clinic.placeholder': 'Ingresa el nombre de la clínica',
  'primaryCareProvider.name.label': 'Nombre del médico',
  'primaryCareProvider.name.placeholder': 'Ingresa el nombre del médico',
  'primaryCareProvider.address.label': 'Dirección',
  'primaryCareProvider.address.placeholder': 'Ingresa la dirección',

  'treatments.noTreatments.description':
    'Desafortunadamente, no hay opciones de tratamiento disponibles para tu cuenta en este momento. Por favor, contacta soporte para obtener asistencia.',
  'treatments.noTreatments.title': 'No Hay Tratamientos Disponibles',

  // Encounter Type Configurations (for appointment treatment selection)
  'encounter.default.title': 'Servicio de Salud',
  'encounter.default.subtitle': 'Atención Médica',
  'encounter.default.description': 'Servicios profesionales de atención médica.',
  'encounter.default.overview': 'Servicios profesionales de atención médica adaptados a tus necesidades.',
  'encounter.default.includes.1': 'Consulta con proveedor',
  'encounter.default.includes.2': 'Plan de atención personalizado',
  'encounter.default.includes.3': 'Apoyo continuo',
  'encounter.default.ideal': 'Para personas que buscan servicios de atención médica de calidad.',

  'encounter.prepInitial.title': 'PrEP y prevención del VIH',
  'encounter.prepInitial.subtitle': 'Comenzar',
  'encounter.prepInitial.description':
    'Obtenga PrEP para prevenir el VIH, además de pruebas de VIH e ITS cada 3 meses. También puede obtener DoxyPEP, medicamentos para la disfunción eréctil, tratamiento para la pérdida de cabello y anticonceptivos como parte de su atención de PrEP.',
  'encounter.prepInitial.overview':
    'Comienza tu viaje de PrEP con una consulta inicial. Nuestros proveedores de atención médica evaluarán tu elegibilidad, revisarán tu historial médico y crearán un plan de prevención personalizado.',
  'encounter.prepInitial.includes.1': 'Evaluación inicial de salud',
  'encounter.prepInitial.includes.2': 'Pruebas basales de VIH/ITS',
  'encounter.prepInitial.includes.3': 'Prescripción de PrEP si es elegible',
  'encounter.prepInitial.includes.4': 'Educación sobre adherencia a la medicación',
  'encounter.prepInitial.ideal': 'Para personas nuevas en PrEP que desean comenzar la prevención del VIH.',

  'encounter.prepFollowUp.title': 'Seguimiento de PrEP',
  'encounter.prepFollowUp.subtitle': 'Continúa Tu Atención',
  'encounter.prepFollowUp.description': 'Revisión regular para monitorear el progreso de tu tratamiento PrEP.',
  'encounter.prepFollowUp.overview':
    'Continúa tu atención de PrEP con una cita de seguimiento. Revisaremos tu salud, abordaremos cualquier inquietud y nos aseguraremos de que tu plan de prevención esté funcionando efectivamente.',
  'encounter.prepFollowUp.includes.1': 'Revisión del estado de salud',
  'encounter.prepFollowUp.includes.2': 'Evaluación de efectos secundarios',
  'encounter.prepFollowUp.includes.3': 'Renovación de prescripción',
  'encounter.prepFollowUp.includes.4': 'Apoyo continuo',
  'encounter.prepFollowUp.ideal': 'Para pacientes actuales de PrEP que necesitan una consulta de seguimiento.',

  'encounter.prepQuarterly.title': 'PrEP Trimestral',
  'encounter.prepQuarterly.subtitle': 'Laboratorio y Revisión Trimestral',
  'encounter.prepQuarterly.description': 'Visita trimestral de rutina incluyendo pruebas de laboratorio.',
  'encounter.prepQuarterly.overview':
    'Tu cita trimestral de PrEP incluye pruebas de laboratorio requeridas y una revisión con el proveedor para asegurar la efectividad continua y el monitoreo de salud.',
  'encounter.prepQuarterly.includes.1': 'Pruebas trimestrales de VIH/ITS',
  'encounter.prepQuarterly.includes.2': 'Monitoreo de función renal',
  'encounter.prepQuarterly.includes.3': 'Renovación de prescripción',
  'encounter.prepQuarterly.includes.4': 'Asesoramiento de prevención',
  'encounter.prepQuarterly.ideal': 'Para pacientes de PrEP que deben realizar sus laboratorios y revisión trimestral.',

  'encounter.injectablePrEP.title': 'PrEP Inyectable',
  'encounter.injectablePrEP.subtitle': 'Inyección Bimensual',
  'encounter.injectablePrEP.description': 'Prevención del VIH con una inyección conveniente cada dos meses.',
  'encounter.injectablePrEP.overview':
    'PrEP inyectable ofrece prevención del VIH a través de inyecciones bimensuales convenientes, eliminando la necesidad de pastillas diarias.',
  'encounter.injectablePrEP.includes.1': 'Inyecciones administradas profesionalmente',
  'encounter.injectablePrEP.includes.2': 'Sin pastillas diarias requeridas',
  'encounter.injectablePrEP.includes.3': 'Monitoreo regular de salud',
  'encounter.injectablePrEP.includes.4': 'Apoyo en programación de citas',
  'encounter.injectablePrEP.ideal': 'Perfecto para quienes prefieren no tomar medicación diaria pero desean prevención efectiva del VIH.',

  'encounter.pep.title': 'PEP',
  'encounter.pep.subtitle': 'Prevención de Emergencia',
  'encounter.pep.description': 'Prevención del VIH post-exposición — comenzar dentro de 72 horas.',
  'encounter.pep.overview':
    'PEP (Profilaxis Post-Exposición) es un medicamento de emergencia para prevenir el VIH después de una posible exposición. El tiempo es crítico — el tratamiento debe comenzar dentro de 72 horas.',
  'encounter.pep.includes.1': 'Acceso a medicación de emergencia',
  'encounter.pep.includes.2': 'Curso de tratamiento de 28 días',
  'encounter.pep.includes.3': 'Prueba de seguimiento de VIH',
  'encounter.pep.includes.4': 'Asesoramiento para transición a PrEP',
  'encounter.pep.ideal': 'Para personas que pueden haber estado expuestas al VIH en las últimas 72 horas.',

  'encounter.stiTesting.title': 'Pruebas de ITS por única vez',
  'encounter.stiTesting.subtitle': 'Hágase la prueba',
  'encounter.stiTesting.description': 'Hágase pruebas de VIH e ITS comunes, incluidas gonorrea, clamidia y sífilis.',
  'encounter.stiTesting.overview': 'Servicios de pruebas y prevención de ITS para mantenerte informado sobre tu salud sexual.',
  'encounter.stiTesting.includes.1': 'Panel completo de pruebas de ITS',
  'encounter.stiTesting.includes.2': 'Consulta profesional',
  'encounter.stiTesting.includes.3': 'Resultados rápidos y confidenciales',
  'encounter.stiTesting.includes.4': 'Asesoramiento de prevención',
  'encounter.stiTesting.ideal': 'Recomendado para detección rutinaria de salud sexual y prevención.',

  'encounter.stiFollowUp.title': 'Seguimiento de ITS',
  'encounter.stiFollowUp.subtitle': 'Tratamiento y Apoyo',
  'encounter.stiFollowUp.description': 'Atención de seguimiento para tratamiento y monitoreo de ITS.',
  'encounter.stiFollowUp.overview':
    'Cita de seguimiento para monitorear el progreso de tu tratamiento de ITS, asegurar la efectividad del tratamiento y proporcionar apoyo continuo.',
  'encounter.stiFollowUp.includes.1': 'Revisión de efectividad del tratamiento',
  'encounter.stiFollowUp.includes.2': 'Pruebas adicionales si es necesario',
  'encounter.stiFollowUp.includes.3': 'Apoyo en notificación a parejas',
  'encounter.stiFollowUp.includes.4': 'Educación en prevención',
  'encounter.stiFollowUp.ideal': 'Para pacientes que necesitan seguimiento después del diagnóstico o tratamiento de ITS.',

  'encounter.hivEnrollment.title': 'Inscripción VIH',
  'encounter.hivEnrollment.subtitle': 'Comienza Tu Viaje de Atención',
  'encounter.hivEnrollment.description': 'Comienza servicios integrales de atención y apoyo para VIH.',
  'encounter.hivEnrollment.overview':
    'Comienza tu viaje de atención del VIH con nuestro proceso integral de inscripción. Crearemos un plan de atención personalizado enfocado en tu salud y bienestar.',
  'encounter.hivEnrollment.includes.1': 'Evaluación inicial de salud',
  'encounter.hivEnrollment.includes.2': 'Desarrollo del plan de atención',
  'encounter.hivEnrollment.includes.3': 'Educación sobre tratamiento',
  'encounter.hivEnrollment.includes.4': 'Introducción a servicios de apoyo',
  'encounter.hivEnrollment.ideal': 'Para personas recién diagnosticadas o en transición a nuestros servicios de atención del VIH.',

  'encounter.hivInitial.title': 'Vivir con VIH',
  'encounter.hivInitial.subtitle': 'Recibir atención',
  'encounter.hivInitial.description': 'Inicie o continúe el tratamiento para el VIH y reciba atención continua.',
  'encounter.hivInitial.overview':
    'Tu cita inicial de atención del VIH incluye una evaluación exhaustiva de salud y el desarrollo de tu plan de tratamiento personalizado.',
  'encounter.hivInitial.includes.1': 'Evaluación completa de salud',
  'encounter.hivInitial.includes.2': 'Pruebas de laboratorio basales',
  'encounter.hivInitial.includes.3': 'Discusión del plan de tratamiento',
  'encounter.hivInitial.includes.4': 'Inicio de medicación si es apropiado',
  'encounter.hivInitial.ideal': 'Para nuevos pacientes que comienzan tratamiento y atención del VIH.',

  'encounter.hivFollowUp.title': 'Seguimiento VIH',
  'encounter.hivFollowUp.subtitle': 'Atención Continua',
  'encounter.hivFollowUp.description': 'Seguimiento regular para monitorear el progreso de tu atención del VIH.',
  'encounter.hivFollowUp.overview':
    'Continúa tu atención del VIH con citas de seguimiento regulares para monitorear tu salud, revisar la efectividad del tratamiento y ajustar la atención según sea necesario.',
  'encounter.hivFollowUp.includes.1': 'Monitoreo de carga viral',
  'encounter.hivFollowUp.includes.2': 'Revisión de conteo CD4',
  'encounter.hivFollowUp.includes.3': 'Manejo de medicación',
  'encounter.hivFollowUp.includes.4': 'Apoyo continuo de salud',
  'encounter.hivFollowUp.ideal': 'Para pacientes en tratamiento de VIH que necesitan revisiones regulares.',

  'encounter.hivAdherence.title': 'Adherencia VIH',
  'encounter.hivAdherence.subtitle': 'Apoyo con Medicación',
  'encounter.hivAdherence.description': 'Sesión de apoyo enfocada en la adherencia a la medicación.',
  'encounter.hivAdherence.overview':
    'Una sesión enfocada para apoyar tu adherencia a la medicación, abordar desafíos y ayudarte a mantener un tratamiento consistente para resultados óptimos de salud.',
  'encounter.hivAdherence.includes.1': 'Evaluación de adherencia',
  'encounter.hivAdherence.includes.2': 'Identificación de barreras',
  'encounter.hivAdherence.includes.3': 'Desarrollo de estrategias',
  'encounter.hivAdherence.includes.4': 'Herramientas y recursos de recordatorio',
  'encounter.hivAdherence.ideal': 'Para pacientes que necesitan apoyo para mantener su horario de medicación.',

  'encounter.hepC.title': 'Hepatitis C',
  'encounter.hepC.subtitle': 'Pruebas y Tratamiento',
  'encounter.hepC.description': 'Detección, tratamiento y verificación de cura de Hepatitis C.',
  'encounter.hepC.overview': 'Servicios integrales de pruebas de Hepatitis C, coordinación de tratamiento y verificación de cura.',
  'encounter.hepC.includes.1': 'Pruebas de Hepatitis C',
  'encounter.hepC.includes.2': 'Coordinación de tratamiento',
  'encounter.hepC.includes.3': 'Monitoreo de salud hepática',
  'encounter.hepC.includes.4': 'Verificación de cura',
  'encounter.hepC.ideal': 'Para personas que necesitan detección o tratamiento de Hepatitis C.',

  'encounter.wellness.title': 'Bienestar',
  'encounter.wellness.subtitle': 'Salud General',
  'encounter.wellness.description': 'Revisión general de bienestar y consulta de salud.',
  'encounter.wellness.overview':
    'Una cita de bienestar general para discutir tu salud general, abordar inquietudes y desarrollar estrategias para mantener un bienestar óptimo.',
  'encounter.wellness.includes.1': 'Revisión general de salud',
  'encounter.wellness.includes.2': 'Discusión de atención preventiva',
  'encounter.wellness.includes.3': 'Establecimiento de metas de salud',
  'encounter.wellness.includes.4': 'Recomendaciones de recursos',
  'encounter.wellness.ideal': 'Para personas que buscan orientación general de salud y apoyo de bienestar.',

  'encounter.weightLoss.title': 'Pérdida de Peso',
  'encounter.weightLoss.subtitle': 'Manejo de Peso',
  'encounter.weightLoss.description': 'Consulta y apoyo médico para pérdida de peso.',
  'encounter.weightLoss.overview':
    'Consulta de manejo de peso médico incluyendo evaluación, opciones de tratamiento y apoyo continuo para tu viaje de pérdida de peso.',
  'encounter.weightLoss.includes.1': 'Evaluación de salud',
  'encounter.weightLoss.includes.2': 'Discusión de opciones de tratamiento',
  'encounter.weightLoss.includes.3': 'Desarrollo de plan personalizado',
  'encounter.weightLoss.includes.4': 'Apoyo continuo',
  'encounter.weightLoss.ideal': 'Para personas que buscan apoyo médico para el manejo de peso.',

  // Prescription (Singular)
  'prescription.na': 'N/A',
  'prescription.pharmacy.phone': 'Teléfono: {phone}',
  'prescription.pharmacy.phone.a11y': 'Llamar farmacia al {phone}',
  'prescription.pharmacy.name.a11y': 'Nombre de la farmacia: {name}',
  'prescription.pharmacy.title': 'Farmacia',
  'prescription.pharmacy.call': 'Llamar',
  'prescription.pharmacy.call.a11y': 'Llamar a {name} al {phone}',
  'prescription.details.ariaLabel': 'Detalles de la receta',
  'prescription.details.title': 'Detalles de la receta',
  'prescription.details.view': 'Ver detalles',
  'prescription.provider.title': 'Proveedor Prescriptor',
  'prescription.refills.deliveredOn': 'Entregado el: {date}',
  'prescription.refills.delivered': 'Entregado',
  'prescription.refills.fill': 'Recarga {number}',
  'prescription.refills.pending': 'Pendiente',
  'prescription.refills.remainingLabel': 'Recargas restantes',
  'prescription.refills.remainingValue': '{remain} de {total}',
  'prescription.refills.shipped': 'Enviado',
  'prescription.refills.shippedOn': 'Enviado el: {date}',
  'prescription.refills.status.delivered': 'Entregado',
  'prescription.refills.status.inTransit': 'En Tránsito',
  'prescription.refills.status.pending': 'Pendiente',
  'prescription.refills.title': 'Historial de Recargas',
  'prescription.refills.tracking': 'Seguimiento: {trackingNumber}',
  'prescription.refills.trackShipment': 'Rastrear Envío',
  'prescription.stats.askPharmacy': 'Su farmacia puede confirmar cuántas recargas le quedan',
  'prescription.stats.fillOf': 'Surtido {current} de {total}',
  'prescription.stats.fillTotal': '1 surtido en total',
  'prescription.stats.fillsTotal': '{total} surtidos en total',
  'prescription.stats.noRefillsLeft': 'No quedan recargas',
  'prescription.stats.perFill': '{quantity} por surtido',
  'prescription.stats.refillLeft': 'Queda 1 recarga',
  'prescription.stats.refillsLeft': 'Quedan {count} recargas',
  'prescription.submissionDate.title': 'Fecha de Envío de la Receta',
  'prescription.title': 'Receta',
  'prescription.trackShipment': 'Rastrear Envío',
  'prescription.unknownMedication': 'Medicamento Desconocido',

  // Prescriptions (Plural)
  'prescriptions.card.a11y.label': 'Receta para {drugName}, estado: {status}, ordenada: {date}',
  'prescriptions.empty.description':
    'Después de tu visita al proveedor y completar el trabajo de laboratorio, te recetarán exactamente lo que necesitas. ¡Recibirás una notificación cuando esté listo!',
  'prescriptions.empty.title': '¡Tus medicamentos están en camino!',
  'prescriptions.title': 'Recetas',

  // Lab Results (Plural)
  'labs.actions.beingReviewed': 'Siendo revisado',
  'labs.actions.openOrder': 'Abrir Orden',
  'labs.actions.openOrder.a11y': 'Abrir formulario de orden Quest',
  'labs.actions.orderNotAvailable': 'Orden no disponible',
  'labs.actions.track': 'Rastrear',
  'labs.tracking.outbound': 'En camino a usted',
  'labs.tracking.inbound': 'En camino al laboratorio',
  'labs.order.title': 'Su orden de laboratorio',
  'labs.order.detail': 'Para su visita al laboratorio',
  'labs.instructions.title': 'Cómo devolverlo',
  'labs.instructions.detail': 'Entréguelo en una oficina de USPS',
  'labs.results.title': 'Sus resultados',
  'labs.results.view.a11y': 'Abrir sus resultados de laboratorio',
  'labs.actions.track.a11y': 'Rastrear envío',
  'labs.actions.view': 'Ver',
  'labs.actions.view.a11y': 'Ver detalles del laboratorio',
  'labs.defaultLabName': 'Laboratorio',
  'labs.details.dateAdded': 'Fecha Agregada',
  'labs.details.dateOrdered': 'Fecha Ordenada',
  'labs.details.dateReceived': 'Fecha Recibida',
  'labs.details.lab': 'Laboratorio',
  'labs.details.dateReleased': 'Resultados Publicados',
  'labs.shipping.returnLabel': 'Etiqueta de devolución',
  'labs.details.a11y.label': 'Detalles del Laboratorio',
  'labs.details.encounterReason': 'Motivo del Encuentro',
  'labs.details.labType.a11y': 'Tipo de laboratorio: {labType}',
  'labs.manual.patientCboLabs': 'Laboratorios Paciente/CBO',
  'labs.details.mtl.pricingInfo':
    'El precio total de sus servicios de laboratorio MTL en casa se agrupa en un precio para su conveniencia. Sin embargo, el monto total se facturará a su proveedor. Si desea conocer el precio exacto pagado por el servicio, comuníquese con hello@qcareplus.com',
  'labs.details.panels': 'Paneles',
  'labs.details.requisitionId': 'ID de Requisición',
  'labs.details.status': 'Estado',
  'labs.disclaimer.title': 'Aviso',
  'labs.disclaimer.manual':
    'Estos resultados son de un laboratorio que no está afiliado con Q Care Plus. Típicamente provienen de tu socio comunitario o podrían haber sido proporcionados por tu Proveedor de Atención Primaria.',
  'labs.empty.description':
    'Una vez que te reúnas con tu proveedor, ordenarán el trabajo de laboratorio perfecto para ti. ¡Podría ser un kit conveniente en casa o una visita rápida a un laboratorio local!',
  'labs.empty.title': '¡Tu aventura de laboratorio te espera!',
  'labs.title': 'Laboratorios',
  'labs.upload.description':
    'Si tienes resultados de trabajo de laboratorio de algún lugar que no sea Q Care Plus, puedes subirlos a continuación. Asegúrate de incluir tu nombre, fecha de nacimiento y fecha de recolección.',
  'labs.upload.alert.title': 'Por favor completa tu perfil',
  'labs.upload.alert.description':
    'Debes completar todos los elementos requeridos en tu perfil antes de poder subir cualquier trabajo de laboratorio.',
  'labs.upload.successMessage': 'El documento de laboratorio fue subido',
  'labs.upload.ariaLabel': 'Sube tus resultados de laboratorio',
  'labs.files.downloadOrder.a11y': 'Descargar documento de orden {filename}',
  'labs.files.downloadResult.a11y': 'Descargar archivo de resultado {filename}',
  'labs.files.orderDocument': 'Documento de Orden',
  'labs.files.results': 'Resultados',
  'labs.sections.details': 'Detalles',
  'labs.sections.files': 'Archivos',
  'labs.sections.observations': 'Observaciones',
  'labs.sections.shipping': 'Envío',
  'labs.shipping.address': 'Dirección',
  'labs.shipping.addressNotFound': 'Dirección no encontrada',
  'labs.shipping.delivered': 'Entregado',
  'labs.shipping.shipped': 'Enviado',
  'labs.shipping.toLab': 'Al Laboratorio',
  'labs.shipping.toYou': 'Para Ti',
  'labs.shipping.tracking': 'Seguimiento',
  'labs.stats.dateAdded': 'Fecha Agregada',
  'labs.stats.dateOrdered': 'Fecha Ordenada',
  'labs.stats.dateReceived': 'Fecha Recibida',
  'labs.stats.dateShipped': 'Fecha Enviada',
  'labs.stats.delivered': 'Entregado',
  'labs.stats.positiveResults': 'Resultados Positivos',
  'labs.stats.status': 'Estado',
  'labs.stats.tracking': 'Seguimiento',
  'labs.stats.unknown': 'Desconocido',
  'labs.status.pending': 'Pendiente',
  'labs.status.received': 'Recibido',
  'labs.notAvailable': 'No Disponible',
  'labs.resultsNotFound': 'Resultados no encontrados',
  'labs.types.ash': 'Kit Casero Ash',
  'labs.types.ghmc': 'GMHC',
  'labs.types.manual': 'Laboratorio de Paciente/CBO',
  'labs.types.mtl': 'Kit Casero MTL',
  'labs.types.quest': 'Quest',

  // Lab Results (Singular)
  'lab.title': 'Laboratorio',
  'lab.provider.title': 'Proveedor que Ordenó',

  // HIV Lab Results
  'hivLab.form.attestation.text':
    'Yo, {user}, confirmo que he completado esta autoprueba de VIH según las instrucciones del fabricante y que la información proporcionada anteriormente es precisa.',
  'hivLab.form.collectionDate.label': 'Fecha de Recolección de Prueba',
  'hivLab.form.collectionDate.placeholder': 'Seleccione la fecha en que se hizo la prueba',
  'hivLab.form.description':
    'Por favor complete todos los campos a continuación y suba fotos claras de los resultados de su prueba. Asegúrese de que toda la información sea precisa antes de enviar.',
  'hivLab.form.expirationDate.label': 'Fecha de Vencimiento del Kit de Prueba',
  'hivLab.form.expirationDate.placeholder': 'Seleccione la fecha de vencimiento en su kit de prueba',
  'hivLab.form.lotNumber.label': 'Número de Lote del Kit de Prueba',
  'hivLab.form.lotNumber.placeholder': 'Ingrese el número de lote de su kit de prueba',
  'hivLab.form.resultPhotos.label': 'Subir Fotos de Resultados de Prueba',
  'hivLab.form.resultPhotos.placeholder': 'Tome fotos claras de los resultados completos de su prueba',
  'hivLab.form.submit.button': 'Enviar Resultados de Prueba',
  'hivLab.form.success.message':
    'Los resultados de su autoprueba de VIH han sido enviados exitosamente y serán revisados por su proveedor de atención médica.',
  'hivLab.form.success.title': 'Resultados de Prueba Enviados',
  'hivLab.form.testResult.label': 'Resultado de Prueba VIH',
  'hivLab.form.title': 'Resultados de Autoprueba VIH',
  'hivLab.form.validation.attestation.required': 'Debe confirmar la precisión de los resultados de su prueba para enviar',
  'hivLab.form.validation.collectionDate.afterExpiration':
    'La fecha de recolección de la prueba no puede ser posterior a la fecha de vencimiento del kit',
  'hivLab.form.validation.collectionDate.future': 'La fecha de recolección de la prueba no puede ser en el futuro',
  'hivLab.form.validation.resultPhotos.required': 'Por favor suba al menos una foto clara de los resultados de su prueba',
  'hivLab.testResults.negative': 'Negativo',
  'hivLab.testResults.notValid': 'No Válido',
  'hivLab.testResults.positive': 'Positivo',

  // Health Assessment (Singular)
  'assessment.dialog.completedOn': 'Completada {date}',
  'assessment.dialog.completedOn.aria': 'Evaluación completada el {date}',
  'assessment.encounterId': 'ID de Encuentro: {id}',
  'assessment.responseInfo.answers': 'Respuestas ({count})',
  'assessment.responseInfo.status': 'Estado:',
  'assessment.responseInfo.title': 'Información de Respuesta',
  'assessment.responseStatus.noResponse': 'Sin Respuesta',
  'assessment.responseStatus.received': 'Recibido',
  'assessment.responseStatus.receivedReviewed': 'Recibido (Revisado)',
  'assessment.responseStatus.sentToPatient': 'Enviado al paciente',
  'assessment.status.complete': 'Completa',
  'assessment.status.expired': 'Expirada',
  'assessment.status.finished': 'Finalizada',
  'assessment.status.in_progress': 'En Progreso',
  'assessment.status.pending': 'Pendiente',
  'assessment.status.reviewed': 'Revisada',
  'assessment.title': 'Evaluación',

  // Health Assessments (Plural)
  'assessments.async.canceled.returnButton': 'Volver al Panel de Control',
  'assessments.async.canceled.title': '{name} Cancelada',
  'assessments.async.completed.returnButton': 'Volver a la Cita Asíncrona',
  'assessments.async.completed.title': '{name} Completada',
  'assessments.async.enterAnswer': 'Ingrese su respuesta...',
  'assessments.async.enterNumber': 'Ingrese un número...',
  'assessments.async.enterPercentage': 'Ingrese porcentaje',
  'assessments.async.exactPercentage': 'Porcentaje exacto',
  'assessments.async.importantInformation': 'Información Importante',
  'assessments.async.notSet': 'No establecido',
  'assessments.async.percentage': 'Porcentaje',
  'assessments.async.progress': 'Progreso',
  'assessments.async.selectDate': 'Seleccione una fecha',
  'assessments.async.unsupportedQuestionType': 'Tipo de pregunta no compatible',
  'assessments.expired.description': 'Esta evaluación ha expirado. Por favor contacta a tu proveedor si necesitas asistencia.',
  'assessments.expired.title': '{name} Expirada',
  'assessments.reviewed.description': 'Tu proveedor ha revisado esta evaluación. Gracias por tus respuestas.',
  'assessments.reviewed.title': '{name} Revisada',
  'assessments.completed.description': 'Gracias por completar esta evaluación. Tus respuestas han sido enviadas exitosamente.',
  'assessments.completed.title': '{name} Completada',
  'assessments.default.name': 'Evaluación de Salud',
  'assessments.default.description':
    'Este cuestionario es para verificar cómo van las cosas con tu tratamiento. Tus respuestas serán enviadas a tu proveedor en QCare+.',
  'assessments.quarterly_lab.name': 'Evaluación de Salud de Renovación Trimestral',
  'assessments.quarterly_lab.description': 'Por favor responde las siguientes preguntas para tu revisión trimestral de PrEP.',
  'assessments.sti.name': 'Evaluación de Salud de ITS',
  'assessments.sti.description':
    'Tu proveedor solicitó que recibas tratamiento para una ITS en una clínica en tu área o departamento de salud local. Por favor completa este formulario compartiendo la fecha en que recibiste tu tratamiento, el nombre y dirección de la clínica, y el medicamento que recibiste.',
  'assessments.pep.name': 'Evaluación de Salud de PEP',
  'assessments.pep.description':
    'Por favor responde todas las preguntas antes de enviar. Tus respuestas serán revisadas por tu proveedor. Mientras tanto, por favor continúa tu medicación según lo prescrito y completa las pruebas de laboratorio.',
  'assessments.hiv.name': 'Evaluación de Salud de VIH',
  'assessments.hiv.description':
    'Este cuestionario es para verificar cómo van las cosas con tu tratamiento. Tus respuestas serán enviadas a tu proveedor en QCare+.',
  'assessments.prep_post_encounter.name': 'Evaluación de Medicamentos Post Encuentro Inicial de PrEP',
  'assessments.prep_post_encounter.description':
    'Este cuestionario es para verificar cómo van las cosas con tu tratamiento. Tus respuestas serán enviadas a tu proveedor en QCare+.',
  'assessments.prep.initial.name': 'Evaluación de PrEP',
  'assessments.prep.initial.description':
    'Completa tu evaluación de salud de PrEP para ayudar a nuestros proveedores a entender tus necesidades y determinar tu elegibilidad.',
  'assessments.sti.testing.name': 'Evaluación de Pruebas de ITS',
  'assessments.sti.testing.description':
    'Completa tu evaluación de salud de pruebas de ITS para ayudar a nuestros proveedores a entender tus necesidades y determinar tu elegibilidad.',
  'assessments.card.a11y.hint': 'Toca para ver detalles de la evaluación',
  'assessments.card.a11y.label': 'Evaluación {name}, estado: {status}, creada: {date}',
  'assessments.empty.description':
    'Ocasionalmente te enviaremos evaluaciones rápidas de salud para asegurar que recibas la mejor atención posible. ¡Mantente atento a nuestros mensajes!',
  'assessments.empty.title': '¡Listo para tus chequeos de salud!',
  'assessments.title': 'Evaluaciones',

  // Enrollment Process
  'enrollment.cboStep.agreeSharing':
    'Doy mi consentimiento para compartir mi información con el socio comunitario seleccionado para apoyar mi atención de telesalud.',
  'enrollment.cboStep.assignmentUpdate.outsideCoverageInsured':
    'Tu dirección de domicilio se actualizó a una ubicación fuera del área de cobertura de tu socio comunitario. No podemos seguir apoyando tu atención de PrEP a través de tu socio comunitario actual. Contacta al Soporte al Cliente para obtener ayuda.',
  'enrollment.cboStep.assignmentUpdate.outsideCoverageUninsured':
    'Tu dirección de domicilio se actualizó a una ubicación fuera del área de cobertura de tu socio comunitario. Como no tienes seguro, no podemos seguir apoyando tu atención de PrEP a través de tu socio comunitario actual. Para recursos de seguro, puedes explorar opciones como {prideHealth} o {americanExchange}. Contacta al Soporte al Cliente para obtener ayuda.',
  'enrollment.cboStep.assignmentUpdate.uninsured':
    'Has actualizado tu estado de seguro a sin seguro y no podemos seguir apoyando tu atención de PrEP. Envía un mensaje al soporte al cliente para obtener recursos.',
  'enrollment.cboStep.assignmentUpdateTitle': 'Actualización de Organización Comunitaria',
  'enrollment.cboStep.cashPayNotAvailable': 'Actualmente no ofrecemos pago por cuenta propia en su estado',
  'enrollment.cboStep.cashPayNotAvailableDescription':
    'Actualizamos con frecuencia los estados que servimos y le notificaremos cuando su estado esté disponible.',
  'enrollment.cboStep.description':
    'Estas son organizaciones locales cerca de ti. En la mayoría de los casos ayudan a cubrir el costo total de tus visitas y medicamentos.',
  'enrollment.cboStep.greatTitle': 'Genial, has sido añadido',
  'enrollment.cboStep.noPartnerDescription':
    'Necesitamos trabajar con un socio local para brindar atención.\nAñadimos nuevos socios con frecuencia, ¿te gustaría recibir una notificación cuando haya uno disponible?',
  'enrollment.cboStep.noPartnerTitle': 'Parece que ninguno de nuestros socios está disponible actualmente',
  'enrollment.cboStep.noServices': 'Sin servicios disponibles',
  'enrollment.cboStep.notSelectedState': 'No seleccionado',
  'enrollment.cboStep.partnersButton': 'Información sobre nuestros socios comunitarios',
  'enrollment.cboStep.partnersDialogTitle': 'Socios Comunitarios',
  'enrollment.cboStep.partnersInfo1':
    'Trabajamos junto con socios comunitarios locales que ayudan a encontrar programas para reducir el costo de la atención a los pacientes.',
  'enrollment.cboStep.partnersInfo2': 'En la mayoría de los casos, ayudan a cubrir el costo total de las visitas y recetas médicas.',
  'enrollment.cboStep.partnersInfo3': 'Y trabajan con nuestros Proveedores Médicos para garantizarte la mejor experiencia.',
  'enrollment.cboStep.resetInfo': 'Si alguna de tu información ha cambiado, considera comenzar el proceso de inscripción nuevamente.',
  'enrollment.cboStep.selectedState': 'Seleccionado',
  'enrollment.cboStep.servicesOffered': 'Servicios ofrecidos: {services}',
  'enrollment.cboStep.sorryDescription':
    'Siempre puedes volver a Q Care Plus más adelante y verificar tu inscripción.\n\nAñadimos nuevos socios y programas de beneficios con frecuencia.',
  'enrollment.cboStep.sorryTitle': 'Lamentamos verte partir',
  'enrollment.cboStep.startOver': 'Volver a empezar',
  'enrollment.cboStep.subscribe': '¡OK!',
  'enrollment.cboStep.title': 'Elige tu organización comunitaria',
  'enrollment.cboStep.waitlistConfirmation':
    'Tan pronto como tengamos lugares disponibles, nuestro equipo se pondrá en contacto contigo directamente.',
  'enrollment.consent.agreeTerms': 'Debes aceptar los términos y condiciones',
  'enrollment.consent.privacyPolicy': 'Política de Privacidad',
  'enrollment.consent.telehealthConsent': 'Consentimiento de Telesalud',
  'enrollment.consent.termsOfService': 'Términos de Servicio',
  'enrollment.consent.text': 'He leído y acepto los {termsOfService}, la {privacyPolicy} y el {telehealthConsent}.',
  'enrollment.consentStep.description':
    'Último paso antes de su visita – por favor confirme su consentimiento para ser tratado por nuestro equipo experto de proveedores. Esto incluye su consulta virtual, así como análisis de laboratorio y medicamentos si se prescriben.',
  'enrollment.consentStep.done': 'Listo',
  'enrollment.consentStep.form.error.notMatching': 'Deben ser las iniciales de su nombre y apellido',
  'enrollment.consentStep.instructions': 'Firmando como {name}. Escribe tus iniciales abajo.',
  'enrollment.consentStep.title': 'Una última firma',
  'enrollment.continueInApp.accept': 'Abrir',
  'enrollment.continueInApp.decline': 'Ahora no',
  'enrollment.continueInApp.description': 'Gratis en iOS y Android',
  'enrollment.continueInApp.opening': 'Abriendo…',
  'enrollment.continueInApp.title': 'Regístrate más rápido en la aplicación',
  'enrollment.countyStep.title': '¿En qué condado vives?',
  'enrollment.countyStep.label': 'Condado',
  'enrollment.countyStep.description': 'Te conectamos con un socio comunitario cercano, por eso necesitamos el condado.',
  'enrollment.detailsStep.address.city': 'Ciudad',
  'enrollment.detailsStep.address.cityPlaceholder': 'Ingrese la ciudad',
  'enrollment.detailsStep.address.homeDescription':
    'Confirma que tenemos licencia en tu estado. Los análisis y las recetas llegan en un paquete discreto.',
  'enrollment.detailsStep.address.shippingDescription': 'Envía los análisis y las recetas a otra dirección.',
  'enrollment.detailsStep.address.homeTitle': '¿Dónde vives?',
  'enrollment.detailsStep.address.line1': 'Dirección',
  'enrollment.detailsStep.address.line2': 'Apt. Suite',
  'enrollment.detailsStep.address.line2Placeholder': 'Apt, Suite, Unidad, etc.',
  'enrollment.detailsStep.address.shippingTitle': '¿A dónde te enviamos todo?',
  'enrollment.detailsStep.address.state': 'Estado',
  'enrollment.detailsStep.address.stateSearchPlaceholder': 'Búsqueda de estado por abreviatura',
  'enrollment.detailsStep.address.useHomeAsShipping':
    'Me gustaría usar esta como mi dirección de envío para análisis/medicamentos también.',
  'enrollment.detailsStep.address.zip': 'Código Postal',
  'enrollment.detailsStep.address.zipPlaceholder': 'Ingrese código postal de 5 dígitos',
  'enrollment.detailsStep.birthdate.description': 'Debe tener al menos 18 años.',
  'enrollment.detailsStep.payoff.title': 'Tu cuenta ya está lista',
  'enrollment.detailsStep.payoff.description':
    'Estos datos van en tu historial, para que podamos agendar tu cita y enviarte tu medicamento.',
  'enrollment.detailsStep.nextUp': 'A continuación: un par de preguntas rápidas sobre tu zona y tu cobertura, y listo',
  'enrollment.detailsStep.birthdate.title': '¿Cuándo naciste?',
  'enrollment.detailsStep.contactInfo.receiveNotifications':
    'Envíenme recordatorios de recargas y citas. La frecuencia de mensajes puede variar y se pueden aplicar tarifas de datos.',
  'enrollment.detailsStep.contactInfo.title': '¿Cuál es tu número de teléfono?',
  'enrollment.detailsStep.contactInfo.description': 'Opcional. Solo te escribimos sobre recargas y citas.',
  'enrollment.detailsStep.errors.nyResidencyRestriction':
    'Los residentes de Nueva York deben usar una dirección de envío de Nueva York para los análisis de laboratorio y las recetas',
  'enrollment.detailsStep.errors.phoneInUse': 'Este número de teléfono ya está en uso',
  'enrollment.detailsStep.errors.stateDoesntBelong': 'El estado debe pertenecer al código postal ingresado',
  'enrollment.detailsStep.errors.zipInvalid': 'Por favor ingrese un código postal válido de 5 dígitos',
  'enrollment.detailsStep.optOutDialog.description':
    'Solo le enviaremos mensajes relacionados con su atención, citas próximas, entregas y notificaciones de documentos para su revisión.',
  'enrollment.detailsStep.optOutDialog.title': 'Para proporcionar la mejor experiencia, recomendamos activar los mensajes de texto',
  'enrollment.detailsStep.personalInfo.description': 'Tal como aparece en tu identificación emitida por el gobierno.',
  'enrollment.detailsStep.personalInfo.firstName': 'Nombre',
  'enrollment.detailsStep.personalInfo.firstNamePlaceholder': 'Ingrese su nombre',
  'enrollment.detailsStep.personalInfo.lastName': 'Apellido',
  'enrollment.detailsStep.personalInfo.lastNamePlaceholder': 'Ingrese su apellido',
  'enrollment.detailsStep.personalInfo.title': '¿Cuál es tu nombre legal?',
  'enrollment.detailsStep.timezone.description': 'Para que podamos mostrarle los horarios de cita disponibles.',
  'enrollment.detailsStep.timezone.label': 'Zona horaria',
  'enrollment.detailsStep.timezone.placeholder': 'Seleccionar zona horaria',
  'enrollment.detailsStep.timezone.title': '¿En qué zona horaria estás?',
  'enrollment.detailsStep.securityAlert.title': 'Tu información está cifrada y nunca se comparte sin tu permiso',
  'enrollment.emailStep.contactSupport': 'Error al crear cuenta. Por favor, contacta con soporte.',
  'enrollment.emailStep.description1': 'Solo responde algunas preguntas breves para comenzar o programar tu consulta en línea.',
  'enrollment.emailStep.description2': 'No se requiere pago',
  'enrollment.emailStep.email.label': 'Correo electrónico',
  'enrollment.emailStep.email.placeholder': 'Ingresa tu correo electrónico',
  'enrollment.emailStep.title': 'Empecemos con tu correo electrónico',
  'enrollment.emailStep.unavailable': 'Correo electrónico no disponible. Por favor, ingresa una nueva dirección de correo.',
  'enrollment.insuranceStep.dialog.content1':
    'Nuestro equipo puede ayudarte inscribiéndote en Gilead Advancing Access® o el Programa de Co-Pago, que probablemente ayudará a cubrir el costo de tu medicamento.',
  'enrollment.insuranceStep.dialog.content2':
    'Ten en cuenta que, para calificar para PrEP sin costo, no debes ganar más de $62,450 anualmente. Esta información es confidencial y solo se comparte con el fabricante de medicamentos Gilead y no se realizan verificaciones de crédito.',
  'enrollment.insuranceStep.dialog.content3':
    'Si ganas más que esta cantidad, tenemos otras soluciones en las que podemos inscribirte para asegurar que tus medicamentos sean asequibles.',
  'enrollment.insuranceStep.dialog.title': 'Programa Gilead Advancing Access® y Co-Pay',
  'enrollment.insuranceStep.error.gileadConsent': 'Debe aceptar para continuar',
  'enrollment.insuranceStep.error.invalidHousehold': 'Por favor ingresa un tamaño de hogar válido',
  'enrollment.insuranceStep.error.invalidIncome': 'Por favor ingresa un ingreso válido',
  'enrollment.insuranceStep.form.annualIncome': 'Ingreso anual',
  'enrollment.insuranceStep.form.annualIncome.helper': 'Solo se usa para ver si Gilead cubre tu medicamento. Nunca afecta tu atención.',
  'enrollment.insuranceStep.form.payoff.title': 'Ya pasaste las preguntas de elegibilidad',
  'enrollment.insuranceStep.form.payoff.description': 'Este es el último grupo, y es lo que desbloquea el medicamento gratuito.',
  'enrollment.insuranceStep.form.description':
    'Si no tienes seguro o tu plan ha negado la cobertura, podemos ayudarte.\n\nTrabajamos con el Programa Gilead Advancing Access® o Co-Pay.\n\nRequerimos información adicional para que nuestro equipo pueda hacer todo el trabajo por ti.',
  'enrollment.insuranceStep.form.gileadConsent':
    'Doy mi consentimiento para compartir mis datos de salud con el Programa de Asistencia al Paciente de Gilead',
  'enrollment.insuranceStep.form.householdSize': 'Tamaño del hogar',
  'enrollment.insuranceStep.form.learnMore': 'Aprende más',
  'enrollment.insuranceStep.form.medicaidEligible': 'Elegible para Medicaid',
  'enrollment.insuranceStep.form.selectPlaceholder': 'Seleccionar',
  'enrollment.insuranceStep.form.title': 'Podemos ayudarte con el costo',
  'enrollment.insuranceStep.form.veteranBenefits': 'Beneficios de Asuntos de Veteranos',
  'enrollment.insuranceStep.question.no': 'No tengo',
  'enrollment.insuranceStep.question.title': '¿Tienes seguro médico?',
  'enrollment.insuranceStep.question.description': 'Esto define qué ayuda con los costos revisamos para ti.',
  'enrollment.insuranceStep.question.yes': 'Sí tengo',
  'enrollment.insuranceStep.waitingList.description':
    'Nuestros socios frecuentemente añaden programas para ayudar a pacientes sin seguro a recibir atención.\n\n¿Te gustaría recibir una notificación cuando estén disponibles?',
  'enrollment.insuranceStep.waitingList.no': 'No, gracias',
  'enrollment.insuranceStep.waitingList.title': 'Lo sentimos, pero {cboName} requiere seguro personal',
  'enrollment.insuranceStep.waitingList.yes': '¡Sí!',
  'enrollment.medicareStep.checkHere': 'consultar aquí',
  'enrollment.medicareStep.helpText':
    'Medi‑CAL es la versión de California de Medicaid. Es un seguro de salud público (no debe confundirse con la palabra "médico").',
  'enrollment.medicareStep.no': 'No tengo',
  'enrollment.medicareStep.title': '¿Tienes Medi-CAL?',
  'enrollment.medicareStep.unsureText': 'Si no estás seguro, puedes',
  'enrollment.medicareStep.yes': 'Sí tengo',
  'enrollment.navigation.back': 'Atrás',
  'enrollment.navigation.backTo': 'Volver a {appName}',
  'enrollment.navigation.backHint': 'Volver al paso anterior desde {stepName}',
  'enrollment.navigation.loading': 'Cargando, por favor espere',
  'enrollment.navigation.next': 'Siguiente paso',
  'enrollment.navigation.nextHint': 'Continuar al siguiente paso desde {stepName}',
  'enrollment.screen.a11y.label': 'Formulario de inscripción de cuenta',
  'enrollment.progress.a11y.label': 'Progreso de inscripción, {phase}',
  'enrollment.selectFlow.a11y.benefit': 'Beneficio',
  'enrollment.selectFlow.a11y.hint': 'Toque dos veces para seleccionar esta opción de inscripción',
  'enrollment.selectFlow.insurance.description':
    'La mayoría de los pacientes asegurados pagan $0 por visitas, análisis y recetas. ¡Por eso no pedimos un método de pago!',
  'enrollment.selectFlow.insurance.title': 'Con seguro',
  'enrollment.selectFlow.noInsurance.benefit.pricing': 'Precios transparentes y por adelantado',
  'enrollment.selectFlow.noInsurance.description':
    '¿Sin seguro? ¡No hay problema! Ofrecemos opciones de pago en efectivo convenientes para la atención de calidad que necesitas y sin sorpresas.',
  'enrollment.selectFlow.noInsurance.title': 'Sin seguro',
  'enrollment.selectFlow.title': 'Conéctate con un proveedor en minutos por video o mensajería*',
  'enrollment.selectFlow.disclaimer':
    '*la opción de mensajería se basa en las regulaciones estatales y puede no estar disponible en todos los estados',
  'enrollment.selectFlow.commonBenefits.title': 'Incluido con ambas opciones',
  'enrollment.selectFlow.benefit.delivery': '100% servicios en línea y entrega a domicilio',
  'enrollment.selectFlow.benefit.appointments': 'Atención disponible el mismo día y al día siguiente',
  'enrollment.selectFlow.benefit.hipaa': 'Confidencial y cumple con HIPAA',
  'enrollment.selectFlow.benefit.providers': 'Proveedores con licencia',
  'enrollment.steps.consent': 'Consentimiento',
  'enrollment.steps.insurance': 'Seguro y Consentimiento',
  'enrollment.steps.location': 'Ubicación y Elegibilidad',
  'enrollment.steps.personal': 'Información Personal',
  'enrollment.steps.personal.short': 'Personal',
  'enrollment.title': '¡Cuéntanos sobre ti!',
  'enrollment.toast.continueEnrollment':
    'Tu proceso de inscripción necesita un poco más de atención. Por favor continúa con los siguientes pasos para completarlo',

  // Appointments (Singular)
  'appointment.actions.a11y.group': 'Acciones de cita',
  'appointment.actions.cancel': 'Cancelar',
  'appointment.actions.cancel.aria': 'Cancelar cita',
  'appointment.actions.cancel.hint': 'Presiona para cancelar esta cita',
  'appointment.actions.join': 'Unirse',
  'appointment.actions.join.accessibility': 'Unirse a videollamada',
  'appointment.actions.join.hint': 'Presiona para unirse a la videollamada',
  'appointment.actions.message': 'Mensaje',
  'appointment.actions.message.accessibility': 'Mensaje al proveedor',
  'appointment.actions.message.hint': 'Presiona para enviar un mensaje a tu proveedor',
  'appointment.actions.message.provider.accessibility': 'Mensaje a {name}',
  'appointment.actions.reschedule': 'Reprogramar',
  'appointment.actions.reschedule.accessibility': 'Reprogramar cita',
  'appointment.actions.reschedule.hint': 'Presiona para cambiar la hora de la cita',
  'appointment.actions.viewSummary': 'Ver Resumen Después de la Visita',
  'appointment.actions.viewSummary.aria': 'Ver resumen de visita de la cita',
  'appointment.actions.viewSummary.hint': 'Presiona para abrir el documento de resumen de visita',
  'appointment.detail.cancelAppointment': 'Cancelar Cita',
  'appointment.detail.messageProvider': 'Si tienes alguna pregunta, envía un mensaje a tu proveedor.',
  'appointment.detail.messageProvider.aria': 'Instrucciones para enviar mensaje al proveedor con preguntas',
  'appointment.async.cancel.a11y.hint': 'Presiona para cancelar esta cita',
  'appointment.async.cancel.a11y.label': 'Cancelar cita de mensajería asíncrona',
  'appointment.async.cancel.confirm': 'Sí, Cancelar',
  'appointment.async.cancel.message': '¿Estás seguro de que quieres cancelar tu solicitud de visita asíncrona?',
  'appointment.async.cancel.title': '¿Cancelar cita?',
  'appointment.async.complete.message': 'Tu visita asíncrona está completa. Presiona para ver el resumen de la visita.',
  'appointment.async.description':
    'Envíe su información en nuestra plataforma encriptada. Un proveedor licenciado la revisará y le responderá durante horas laborales.',
  'appointment.async.establish.description':
    'Envíe su información en nuestra plataforma encriptada. Un proveedor licenciado la revisará y le dará seguimiento durante su cita programada.',
  'appointment.async.establish.expectations.questionnaire': 'Complete un cuestionario de salud confidencial en línea.',
  'appointment.async.establish.expectations.appointment':
    'Se necesita una cita rápida de 5 minutos por video o teléfono según las regulaciones de su estado.',
  'appointment.async.establish.expectations.response':
    'Si es elegible, recibirá confirmación de receta(s) o recomendaciones de tratamiento durante esta cita.',
  'appointment.async.establish.hivInitial.expectations.questionnaire':
    'Complete un cuestionario de salud confidencial en línea para determinar la elegibilidad de su programa',
  'appointment.async.establish.hivInitial.expectations.appointment': 'Se necesita una cita por video',
  'appointment.async.documenting.message': 'Tu solicitud está bajo revisión del proveedor',
  'appointment.async.error.existing': 'Ya tienes una Visita Asíncrona reservada',
  'appointment.async.error.unavailable': 'Lo sentimos, pero no hay proveedores disponibles en este momento',
  'appointment.async.expectations.clarifications':
    'Su proveedor asignado puede enviar mensajes en la aplicación para aclaración si es necesario',
  'appointment.async.expectations.questionnaire': 'Complete un cuestionario de salud confidencial en línea. No se requiere visita en vivo',
  'appointment.async.expectations.response':
    'Si es elegible, recibirá confirmación de receta(s) o recomendaciones de tratamiento en 1-2 días hábiles (¡usualmente en horas!).',
  'appointment.async.expectations.title': 'Cómo Funciona:',
  'appointment.async.incomplete.message':
    'Termina de enviar tu información para que nuestro proveedor con licencia pueda revisar y hacer seguimiento contigo durante el horario comercial.',
  'appointment.async.resume': 'Continuar',
  'appointment.async.resume.a11y.hint': 'Presiona para continuar con esta cita',
  'appointment.async.resume.a11y.label': 'Reanudar cita de mensajería asíncrona',
  'appointment.async.status.complete': 'Completo',
  'appointment.async.status.incomplete': 'Incompleto',
  'appointment.async.status.underReview': 'Bajo Revisión',
  'appointment.async.submit': 'Comience en Línea Ahora',
  'appointment.async.title': 'Iniciar Cuestionario en Línea',
  'appointment.async.visitType': 'Visita Asíncrona',
  'appointment.async.waiting.message':
    'Tu solicitud está siendo revisada por nuestros proveedores. Te notificaremos cuando un proveedor responda.',
  'appointment.booking.accessibility.book': 'Reservar una Cita',
  'appointment.booking.confirm.button.aria': 'Confirmar reserva de cita',
  'appointment.booking.confirm.button.hint': 'Presiona para confirmar y reservar tu cita',
  'appointment.booking.confirm.description': 'Revisa los detalles de tu cita y completa la información requerida a continuación',
  'appointment.booking.confirm.screen.aria': 'Pantalla de confirmación de cita',
  'appointment.booking.confirm.title': 'Confirma Tu Cita',
  'appointment.booking.error.alreadyScheduled': 'Ya tienes una cita programada',
  'appointment.booking.error.cannotSelfSchedule': 'Esta cita no pudo ser programada. Por favor intente nuevamente.',
  'appointment.booking.error.general.message':
    'Lo sentimos mucho pero no pudimos programar esa cita.\n\nPor favor inténtalo de nuevo o contacta a nuestro soporte al cliente.',
  'appointment.booking.error.time.unavailable.message':
    'Lo sentimos, pero ese horario ya no está disponible. Por favor selecciona un nuevo horario.',
  'appointment.booking.error.time.unavailable.title': 'Horario No Disponible',
  'appointment.booking.provider.notFound.title': 'Proveedor no encontrado',
  'appointment.booking.provider.notFound.description':
    'No se pudo encontrar al proveedor seleccionado. Por favor regresa y selecciona un proveedor diferente.',
  'appointment.booking.success.modified': 'Tu cita ha sido modificada según solicitaste.',
  'appointment.booking.success.scheduled': 'Tu cita ha sido programada según solicitaste.',
  'appointment.booking.success.title': '¡Todo Listo!',
  'appointment.booking.type.choose': 'Elige el Tipo de Cita',
  'appointment.booking.type.phone.aria': 'Opción de cita telefónica',
  'appointment.booking.type.phone.hint': 'Seleccionar tipo de cita telefónica',
  'appointment.booking.type.phone.subtitle': 'Cita solo por voz',
  'appointment.booking.type.phone.title': 'Llamada Telefónica',
  'appointment.booking.type.video.aria': 'Opción de cita por video',
  'appointment.booking.type.video.hint': 'Seleccionar tipo de cita por video',
  'appointment.booking.type.video.subtitle': 'Visita virtual con video',
  'appointment.booking.type.video.title': 'Videollamada',
  'appointment.booking.update.confirm.aria': 'Confirmar actualización de cita',
  'appointment.booking.update.title': 'Actualizar Cita',
  'appointment.calendar.no.timeslots': 'No hay horarios disponibles',
  'appointment.calendar.date.available.aria': '{date}, {day}, horarios disponibles',
  'appointment.calendar.date.unavailable.aria': '{date}, sin horarios disponibles',
  'appointment.calendar.selectDate.hint': 'Toque dos veces para seleccionar esta fecha',
  'appointment.calendar.today': 'Hoy',
  'appointment.calendar.tomorrow': 'Mañana',
  'appointment.calendar.unavailable': 'No Disponible',
  'appointment.cancel.dialog.message': '¿Te gustaría cancelar tu cita existente?',
  'appointment.cancel.dialog.title': '¿Cancelar cita?',
  'appointment.canceled.message': 'Tu cita fue cancelada.',
  'appointment.card.a11y.label': 'Cita de {visitReason} - Estado: {status} - Fecha: {date}',
  'appointment.complete.message': 'La cita fue completada.',
  'appointment.completed.title': 'Cita Completada',
  'appointment.dateTime.a11y.label': 'Cita programada para {day}, {date} a las {time}',
  'appointment.detail.screen.accessibility': 'Cita {status} con {provider}',
  'appointment.documenting.message': 'Tu proveedor está actualizando tu registro médico.',
  'appointment.failed.message': 'Tu cita no se completó. Esto pudo haber sido debido a un problema técnico.',
  'appointment.failed.title': 'Cita Falló',
  'appointment.failureMessage': 'Esta cita no se pudo completar',
  'appointment.missed.message': 'Tu cita no se completó. Ni tú ni tu proveedor estuvieron disponibles en el horario programado.',
  'appointment.missed.title': 'Cita Perdida',
  'appointment.notCompleted.message': 'Esta cita no se pudo completar.',
  'appointment.unavailable.message': 'Esta cita ya no está disponible.',
  'appointment.unavailable.title': 'Cita No Disponible',
  'appointment.insurance.checking.title': 'Verificando tu seguro...',
  'appointment.insurance.checking.subtitle': 'Esto solo toma un momento. Por favor permanece en esta pantalla.',
  'appointment.insurance.required.continue': 'Continuar',
  'appointment.insurance.required.description':
    'No pudimos verificar un plan de seguro activo. Agrega tu tarjeta de seguro para continuar con tu visita.',
  'appointment.insurance.required.title': 'Agrega tu seguro',
  'appointment.onDemand.button.start': 'Comenzar Visita Inmediata',
  'appointment.onDemand.button.switch': 'Cambiar a Visita Inmediata',
  'appointment.onDemand.error.abandoned.message':
    'Vemos que te has alejado. ¡No hay problema! Por favor, programa una nueva cita cuando te sea conveniente.',
  'appointment.onDemand.error.abandoned.title': 'Sesión Terminada',
  'appointment.onDemand.error.noProviders': 'No hay proveedores disponibles para citas bajo demanda en este momento',
  'appointment.onDemand.error.rejected.message':
    'Pedimos sinceras disculpas por el retraso debido a que nuestros proveedores están atendiendo a otros pacientes más tiempo del previsto. ¡Programemos una cita futura lo antes posible!',
  'appointment.onDemand.error.rejected.title': 'Cita Retrasada',
  'appointment.onDemand.features.connectImmediately':
    'Conéctate inmediatamente con un proveedor disponible. Un proveedor típicamente se unirá en 1-2 minutos',
  'appointment.onDemand.features.establishCare': 'Una breve visita por video de 5 minutos para establecer tu atención',
  'appointment.onDemand.features.secureConfidential':
    'La misma atención segura y confidencial que las visitas programadas. Las sesiones duran 15-20 minutos',
  'appointment.onDemand.features.urgentNeeds': 'Perfecto para necesidades de salud urgentes pero no de emergencia',
  'appointment.onDemand.loading.subtitle': 'Preparando su experiencia de atención médica...',
  'appointment.onDemand.loading.title': 'Cargando Atención Bajo Demanda',
  'appointment.onDemand.requiredInfo.step.tips': 'Consejos',
  'appointment.onDemand.requiredInfo.photoId.description':
    'Carga tu identificación y asegúrate de que tu nombre, fecha de nacimiento y foto sean visibles. Si no puedes subirla ahora, tenla disponible para que el proveedor pueda tomar una captura de pantalla al inicio de tu cita.',
  'appointment.onDemand.requiredInfo.title': 'Mientras esperas',
  'appointment.onDemand.screen.description': '¡Conéctate con un proveedor de inmediato - rápido, fácil y conveniente!',
  'appointment.onDemand.screen.title.book': 'Reservar Visita Inmediata',
  'appointment.onDemand.screen.title.update': 'Cambiar a Visita Inmediata',
  'appointment.onDemand.webview.a11y': 'Interfaz web de cita bajo demanda',
  'appointment.onDemand.whatToExpect.title': 'Qué Esperar',
  'appointment.onDemand.waitingRoom.message.start':
    'Estamos trabajando para conectarte con un proveedor disponible. Por favor ten en cuenta que esto puede tomar unos momentos. ¡Gracias por estar aquí!',
  'appointment.onDemand.waitingRoom.message.3min':
    'Nuestro equipo está trabajando arduamente para conectarte con el proveedor lo más rápido posible. Agradecemos tu espera — tu atención es nuestra prioridad.',
  'appointment.onDemand.waitingRoom.message.5min':
    '¡Ya casi! Tu proveedor está terminando con otro paciente. Gracias por tu paciencia continua — estaremos contigo en breve.',
  'appointment.onDemand.waitingRoom.message.6min':
    'Esto está tomando un poco más de lo esperado, pero el proveedor debería estar contigo en un momento. ¡Gracias por esperar con nosotros!',
  'appointment.onDemand.waitingRoom.message.9min': '¡Eres importante para nosotros! Conectaremos a tu proveedor tan pronto como podamos.',
  'appointment.onDemand.waitingRoom.timer.aria': 'Tiempo transcurrido: {time}',
  'appointment.onDemand.waitingRoom.playGame': 'Juega un juego mientras esperas ;)',
  'appointment.onDemand.waitingRoom.success.accepted': '¡Buenas noticias! Un proveedor comenzará tu visita en un momento.',
  'appointment.onDemand.waitingRoom.status': 'Conectándote con un proveedor',
  'appointment.payment.amount.subtitle': 'Pago procesado únicamente cuando llegue',
  'appointment.payment.amount.title': 'Solo {amount} para Reservar',
  'appointment.payment.button.processing': 'Asegurando Su Lugar...',
  'appointment.payment.button.reserve': 'Reservar Mi Cita por {amount}',
  'appointment.payment.description':
    '¡Asegure su horario de cita preferido con nuestro sistema de reserva seguro. Sin cargo hasta que comience su visita!',
  'appointment.payment.error.failed': 'El pago falló. Por favor intente nuevamente.',
  'appointment.payment.error.init': 'No se pudo preparar el método de pago. Intente nuevamente o contacte soporte si el problema persiste.',
  'appointment.payment.error.processing':
    'El procesamiento del pago encontró un problema inesperado. Intente nuevamente o contacte soporte.',
  'appointment.payment.error.setup': 'La configuración del pago encontró un problema. Verifique su conexión e intente nuevamente.',
  'appointment.payment.error.timeout':
    'La configuración del pago está tardando más de lo esperado. Verifique su conexión e intente nuevamente.',
  'appointment.payment.features.cancel': '¿Cambió de opinión? Cancele en cualquier momento sin cargos',
  'appointment.payment.features.encryption': 'Cifrado de grado militar protege su información',
  'appointment.payment.features.hold': 'Su dinero permanece en su cuenta hasta que comience la cita',
  'appointment.payment.features.secure': 'Asegure instantáneamente su horario de cita preferido',
  'appointment.payment.features.title': 'Por Qué Los Pacientes Aman Nuestro Sistema',
  'appointment.payment.form.description': 'Agregue su método de pago para asegurar instantáneamente su horario de cita',
  'appointment.payment.form.title': 'Complete Su Reservación',
  'appointment.payment.success': '¡Pago exitoso!',
  'appointment.payment.title': 'Reserve Su Lugar Hoy',

  'appointment.provider.title': 'Proveedor de Salud',
  'appointment.providerDetails.availableTimes': 'Horarios Disponibles para {date}',
  'appointment.providerDetails.bio.readMore': 'Ver biografía',
  'appointment.providerDetails.bio.showLess': 'Ver menos',
  'appointment.providerDetails.noTimeslots.description': 'Por favor seleccione una fecha diferente para ver los horarios disponibles.',
  'appointment.providerDetails.noTimeslots.title': 'No hay citas disponibles',
  'appointment.providerDetails.selectDate': 'Seleccione una Fecha',
  'appointment.providerDetails.title': 'Seleccione Fecha y Hora',
  'appointment.schedule.afternoon': 'Tarde',
  'appointment.schedule.dateSelector.aria': 'Selector de fecha',
  'appointment.schedule.evening': 'Noche',
  'appointment.schedule.morning': 'Mañana',
  'appointment.schedule.provider.calendar.aria': 'Calendario de disponibilidad del proveedor',
  'appointment.schedule.provider.select.hint': 'Toque dos veces para ver los detalles de este proveedor',
  'appointment.schedule.providers.description':
    'Selecciona entre nuestros proveedores de salud calificados que están disponibles para tu cita. Cada proveedor muestra sus horarios más próximos disponibles.',
  'appointment.schedule.providers.empty.description':
    'Lo sentimos mucho pero no hay proveedores disponibles en este momento. Por favor dirígete a tu clínica local o sala de emergencias para recibir asistencia adicional.',
  'appointment.schedule.providers.empty.title': 'No Hay Proveedores Disponibles',
  'appointment.schedule.providers.title': 'Proveedores',
  'appointment.schedule.timeSlot.select.hint': 'Toque dos veces para seleccionar este horario',
  'appointment.status.canceled': 'Cancelada',
  'appointment.status.complete': 'Completa',
  'appointment.status.inProgress': 'En Progreso',
  'appointment.status.missed': 'Perdida',
  'appointment.status.notCompleted': 'No Completada',
  'appointment.status.scheduled': 'Programada',
  'appointment.status.a11y.isScheduled': 'La cita está programada',
  'appointment.status.a11y.status': 'Estado de la cita: {status}',
  'appointment.title': 'Cita',
  'appointment.type.title': 'Tipo de Visita',
  'appointment.type.async.description':
    '¡Simplemente completa un cuestionario rápido y seguro en línea. Un proveedor te enviará un mensaje en línea durante horas laborales. ¡No se requiere video ni llamada telefónica!',
  'appointment.type.async.establish.any.subtitle':
    'Las regulaciones estatales requieren una visita de 5 minutos por video o teléfono para establecer atención',
  'appointment.type.async.establish.subtitle':
    'Las regulaciones estatales requieren una visita de 5 minutos por video para establecer atención',
  'appointment.type.async.subtitle': 'No se requiere videollamada',
  'appointment.type.async.title': 'Comienza con un Formulario en Línea',
  'appointment.type.book.title': 'Elige Tu Tipo de Visita',
  'appointment.type.description': 'Elige la opción con la que te sientas más cómodo',
  'appointment.type.empty.description':
    'No hay opciones de cita disponibles actualmente para este servicio. Por favor, contacta a soporte para obtener ayuda.',
  'appointment.type.empty.title': 'Sin Opciones Disponibles',
  'appointment.type.emergency.call911.aria': 'Llamar al 911 para emergencias médicas',
  'appointment.type.emergency.subtitle': 'Llama al 911 inmediatamente',
  'appointment.type.emergency.title': 'Emergencia Médica',
  'appointment.type.ondemand.description':
    '¡Inicia una videoconsulta en línea con un proveedor licenciado ahora mismo! (espera <10 minutos).',
  'appointment.type.ondemand.subtitle': 'Ver a un proveedor inmediatamente',
  'appointment.type.ondemand.title': 'Iniciar una Visita en Línea Ahora',
  'appointment.type.reschedule.title': 'Reprogramar Tu Cita',
  'appointment.type.schedule.description': 'Reserva una fecha y hora futura que se ajuste a tu horario con el proveedor de tu elección.',
  'appointment.type.schedule.subtitle': 'Reserva una Hora Posterior',
  'appointment.type.schedule.title': 'Programar una Visita Futura',
  'appointment.treatment.title': 'Servicio de Salud',
  'appointment.treatment.gotIt': 'Entendido',
  'appointment.treatment.info.aria': 'Más información',
  'appointment.treatment.select.description': 'Elige el servicio de salud para el que deseas programar una cita.',
  'appointment.treatment.select.title': 'Selecciona Tu Atención',
  'appointment.treatment.whatsIncluded': 'Qué Incluye:',
  'appointment.video.instructions':
    'Te enviaremos recordatorios por correo electrónico/texto con instrucciones y un enlace para asistir a tu cita.',
  'appointment.webview.aria': 'Interfaz de videoconsulta médica',
  'appointment.webview.loading': 'Preparando tu experiencia de atención médica...',

  // Appointments (Plural)
  'appointments.empty.description':
    'Programa tu primera cita y todas tus visitas futuras aparecerán aquí. ¡Tu aventura de bienestar comienza con solo un clic!',
  'appointments.empty.title': '¡Tu viaje de salud comienza aquí!',
  'appointments.list.a11y.label': 'Lista de citas con {count} elementos',
  'appointments.title': 'Citas',

  // Home Dashboard
  'home.badge.a11y.noTasks': 'No hay tareas pendientes',
  'home.badge.a11y.tasks': '{count} tareas pendientes',
  'home.empty.description': 'No tienes tareas pendientes en este momento.',
  'home.contact.careTeam.name': 'Su equipo de atención',
  'home.contact.careTeam.action': 'Enviar mensaje',
  'home.block.visits': 'Visitas',
  'home.block.seeAll': 'Ver todo',
  'home.block.labs': 'Laboratorios',
  'labs.kit.step.sent': 'Enviado',
  'labs.kit.step.you': 'Usted',
  'labs.kit.step.lab': 'Lab',
  'labs.kit.step.testing': 'Análisis',
  'labs.kit.step.results': 'Resultados',
  'labs.kit.days': '{count, plural, one {# día} other {# días}}',
  'labs.kit.instructions.cta': 'Ver instrucciones',
  'labs.kit.support.detail': 'Preguntas y reemplazos del kit',
  'labs.kit.stage.arrived.title': 'Su kit llegó',
  'labs.kit.stage.arrived.body': 'Recoja su muestra cuando esté listo.',
  'labs.kit.stage.collect.title': 'Recoja su muestra',
  'labs.kit.stage.collect.body': 'Entregado el {date}. Toma unos diez minutos.',
  'labs.kit.stage.mail.title': 'Envíe su kit de vuelta',
  'labs.kit.stage.mail.body': 'Entregado el {date}. Todavía lo estamos esperando.',
  'labs.kit.stage.overdue.title': 'Su kit sigue pendiente',
  'labs.kit.stage.overdue.body': 'Entregado el {date}. Sus resultados no pueden comenzar hasta que el laboratorio lo reciba.',
  'labs.kit.progress.a11y.sent': 'Su kit está en camino hacia usted',
  'labs.kit.progress.a11y.you': 'Su kit está con usted y debe enviarlo de vuelta',
  'labs.kit.progress.a11y.lab': 'Su kit está en camino al laboratorio',
  'labs.kit.progress.a11y.testing': 'El laboratorio está analizando su muestra',
  'labs.kit.progress.a11y.results': 'Sus resultados están listos',
  'home.footer.emergency.title': 'Emergencia médica',
  'home.footer.emergency.body': 'Ve a la sala de emergencias más cercana.',
  'home.footer.emergency.action': 'Llama al 911',
  'home.greeting.line': '{greeting}, {name}',
  'home.header.book': 'Agendar una consulta',
  'home.block.toFinishUp': 'Pendiente',
  'home.empty.title': '¡Todo al día!',
  'home.greeting.afternoon': 'Buenas tardes',
  'home.greeting.evening': 'Buenas noches',
  'home.greeting.morning': 'Buenos días',
  'home.header.notifications.aria': 'Notificaciones',
  'home.header.notifications.hint': 'Presiona para ver notificaciones',
  'home.header.profile.aria': 'Perfil de {name}',
  'home.header.profile.hint': 'Presiona para ir a configuración de cuenta',
  'home.scheduleUnavailable.message':
    'Según sus respuestas recientes, la opción para programar citas no está disponible por ahora. Por favor, inténtalo de nuevo en 24 horas.',
  'home.scheduleUnavailable.aria': 'Programación no disponible',
  'home.scheduleUnavailable.close': 'cerrar alerta',
  'home.hivExposure.dialog.title': '¿Has estado expuesto al VIH?',
  'home.hivExposure.dialog.description':
    'Si has estado potencialmente expuesto al VIH en las últimas 72 horas, puedes reunirte con tu proveedor para determinar si PEP es adecuado para ti. Comenzar la medicación PEP lo antes posible es importante para prevenir una infección por VIH.',
  'home.hivExposure.dialog.confirm': 'Reservar una Cita',
  'home.hivExposure.fab.aria': 'Reportar posible exposición al VIH y reservar una cita',
  'home.todos.card.a11y.label': 'Ver detalles de {title}',
  'home.todos.appointment.join': 'Unirse a {title}',
  'home.todos.appointment.title': 'Cita de {reason}',
  'home.todos.assessment.continue': 'Continuar Evaluación',
  'home.todos.assessment.description.general': 'Completar evaluación de salud para la cita',
  'home.todos.assessment.description.glp1': 'Completa un nuevo cuestionario de salud para tu tratamiento con GLP-1.',
  'home.todos.assessment.description.prep': 'Revisar medicamentos y efectos secundarios',
  'home.todos.assessment.start': 'Iniciar Evaluación',
  'home.todos.assessment.title.glp1': 'Evaluación de Salud GLP-1',
  'home.todos.asyncMessaging.continue': 'Continuar',
  'home.todos.asyncMessaging.progress': 'Paso {current} de {total}: {step}',
  'home.todos.asyncMessaging.description': 'Tu proveedor puede revisar tu solicitud en cuanto la envíes. Continúa justo donde lo dejaste.',
  'home.todos.asyncMessaging.resume': 'Continuar tu visita en línea',
  'home.todos.asyncMessaging.start': 'Comenzar',
  'home.todos.asyncMessaging.stepsLeft': '{count, plural, one {Falta # paso} other {Faltan # pasos}}',
  'home.todos.asyncMessaging.submitted.description': 'Un proveedor está revisando tu información y te contactará en horario laboral.',
  'home.todos.asyncMessaging.submitted.status': 'En revisión',
  'home.todos.asyncMessaging.submitted.title': 'Visita enviada',
  'home.todos.asyncMessaging.title': 'Termina tu visita en línea',
  'home.todos.gileadApplication.description': 'Completar solicitud Gilead Advancing Access',
  'home.todos.gileadApplication.title': 'Solicitud Gilead',
  'home.todos.gileadApplication.view': 'Ver Solicitud Gilead {id}',
  'home.todos.hivSelfTest.description': 'Completar prueba en casa y subir resultados',
  'home.todos.hivSelfTest.dialog.notReceived': 'No, no lo recibí',
  'home.todos.hivSelfTest.dialog.question': '¿Recibió su kit de autoprueba VIH?',
  'home.todos.hivSelfTest.dialog.received': 'Sí, lo recibí',
  'home.todos.hivSelfTest.dialog.title': 'Autoprueba VIH',
  'home.todos.hivSelfTest.open': 'Abrir instrucciones de autoprueba VIH',
  'home.todos.hivSelfTest.replacement.button': 'Solicitar Nueva Prueba',
  'home.todos.hivSelfTest.replacement.message':
    'El representante de servicios al paciente de su proveedor se comunicará lo antes posible para coordinar el pedido de una prueba de reemplazo. ¡Pedimos disculpas por las molestias!',
  'home.todos.hivSelfTest.replacement.success': 'Solicitud enviada exitosamente',
  'home.todos.hivSelfTest.replacement.title': '¡Sentimos que no haya recibido su prueba!',
  'home.todos.hivSelfTest.title': 'Kit de Autoprueba VIH',
  'home.todos.initialAppointmentV2.description': 'Elige el servicio que es correcto para ti',
  'home.todos.initialAppointment.description.generic': 'Iniciar cita de atención {providerType}',
  'home.todos.initialAppointment.description.hivContinue': 'Continuar cita de atención VIH+',
  'home.todos.initialAppointment.description.hivInitial': 'Cita inicial para atención VIH+',
  'home.todos.initialAppointment.description.injectable': 'Consulta para opciones de PrEP inyectable',
  'home.todos.initialAppointment.description.prep': 'Consulta inicial para comenzar tratamiento PrEP',
  'home.todos.initialAppointment.description.sti': 'Discutir opciones de pruebas de ITS',
  'home.todos.initialAppointment.schedule': 'Programar cita',
  'home.todos.initialAppointment.title': 'Reservar Cita',
  'home.todos.insurance.cardReady': 'Ten tu tarjeta de seguro lista (BIN, PCN, ID, RxGroup).',
  'home.todos.insurance.contact': 'Contactar a {name} al',
  'home.todos.insurance.description': 'Actualiza tu información de seguro para asegurar cobertura continua.',
  'home.todos.insurance.title': 'Verificación de Seguro',
  'home.todos.labFlow.description.general': 'Completar análisis de laboratorio para continuar atención',
  'home.todos.labFlow.description.quarterly': 'Completar renovación trimestral para PrEP',
  'home.todos.labFlow.open': 'Abrir flujo de laboratorio',
  'home.todos.labFlow.title': 'Análisis de Laboratorio',
  'labs.kit.instructions.body':
    'Cuando estés listo para devolver tu kit de laboratorio completado, llévalo dentro de tu oficina local de USPS y entrégalo directamente a un empleado postal. Pídele que escanee el paquete de devolución prepagado en el mostrador antes de irte. Este escaneo inicial activa la información de rastreo para que tanto tú como nuestro equipo puedan monitorear la devolución de tu kit.\n\nSiempre que sea posible, evita colocar tu kit en un buzón de USPS o dejarlo para que lo recoja tu cartero. Esos paquetes a menudo no se escanean cuando se recogen por primera vez, lo que puede retrasar el rastreo o, en algunos casos, resultar en la pérdida del paquete en tránsito.\n\nTomarte un minuto para que escaneen tu paquete en la oficina postal ayuda a garantizar que tu muestra llegue al laboratorio de la manera más rápida y confiable posible.',
  'labs.kit.instructions.title': 'Instrucciones de Devolución del Kit de Laboratorio',
  'home.todos.newDocument.description': 'Revisar documentos y formularios subidos',
  'home.todos.newDocument.title': 'Documentos',
  'home.todos.newDocument.view': 'Ver nuevos documentos',
  'home.todos.photoIdUpdate.description': 'Sube una foto de tu identificación oficial para verificar tu identidad',
  'home.todos.photoIdUpdate.title': 'Verificación de ID',
  'home.todos.photoIdUpdate.view': 'Subir foto de ID',
  'home.todos.passcodeSetup.description': 'Crea un código de acceso para un inicio de sesión más rápido y conveniente',
  'home.todos.passcodeSetup.title': 'Configurar Código',
  'home.todos.passcodeSetup.view': 'Configurar código de acceso',
  'home.todos.biometricSetup.description.faceId': 'Experimenta un inicio de sesión seguro y sin interrupciones usando Face ID',
  'home.todos.biometricSetup.description.fingerprint':
    'Experimenta un inicio de sesión seguro y sin interrupciones usando tu huella dactilar',
  'home.todos.biometricSetup.description.biometric':
    'Experimenta un inicio de sesión seguro y sin interrupciones usando autenticación biométrica',
  'home.todos.biometricSetup.title': 'Inicio Biométrico',
  'home.todos.biometricSetup.view': 'Configurar inicio biométrico',
  'home.todos.notificationSetup.description': 'Recibe actualizaciones instantáneas sobre citas, mensajes e información de salud importante',
  'home.todos.notificationSetup.title': 'Habilitar Notificaciones',
  'home.todos.notificationSetup.view': 'Habilitar notificaciones',
  'home.todos.establishRecovery.title': 'Termine de Establecer su Atención',
  'home.todos.establishRecovery.description': 'Su visita no se realizó. Inicie una visita en línea ahora o programe una para más tarde.',
  'home.todos.establishRecovery.a11y.label': 'Elija cómo completar su visita para establecer atención',
  'home.todos.onDemandAppointment.description': 'Continuar consulta por video con proveedor',
  'home.todos.onDemandAppointment.rejoin': 'Volver a unirse a la cita',
  'home.todos.onDemandAppointment.title': 'Cita por Video',
  'home.todos.restartCare.addressVerification':
    'Por favor verifica que tu información de dirección esté actualizada para que podamos continuar tu atención.',
  'home.todos.restartCare.description': 'Actualizar dirección y seguro para continuar atención',
  'home.todos.restartCare.success.message': 'Nuestro equipo de soporte se comunicará contigo en 24 horas.',
  'home.todos.restartCare.success.title': 'Solicitud Enviada',
  'home.todos.restartCare.title': 'Reiniciar Atención',
  'home.todos.restartCare.welcome.message':
    'Notamos que ha pasado tiempo desde tu última receta. ¿Te gustaría reiniciar tu atención con nosotros?\n\nReiniciar tu atención te permitirá obtener nuevas recetas y continuar recibiendo nuestros servicios.',
  'home.todos.restartCare.welcome.restart': 'Reiniciar Atención',
  'home.todos.restartCare.welcome.skip': 'Omitir por ahora',
  'home.todos.restartCare.welcome.title': '¡Bienvenido de Vuelta!',
  'home.todos.rescheduleAppointment.title': 'Reprogramar Visita',
  'home.todos.rescheduleAppointment.description': 'Su visita ha sido cancelada. Por favor reprograme para continuar su atención.',
  'home.todos.rescheduleAppointment.a11y.label': 'Reprogramar su visita cancelada',
  'home.todos.paymentFailure.accessibility': 'Reintentar su pago que no fue exitoso',
  'home.todos.paymentFailure.description':
    'Su pago reciente no fue procesado. Esto podría deberse a fondos insuficientes, una tarjeta vencida, o un error en la dirección de facturación. Por favor reintente su pago para continuar con su cita o servicio.',
  'home.todos.paymentFailure.title': 'Pago No Exitoso',
  'home.todos.roiRequest.description': 'Completar formulario de Liberación de Información',
  'home.todos.roiRequest.submit': 'Enviar Solicitud ROI',
  'home.todos.roiRequest.title': 'Solicitud ROI #{id}',
  'home.todos.profileIncomplete.title': 'Completa Tu Perfil',
  'home.todos.profileIncomplete.description':
    'Agrega los detalles restantes a tu perfil. Tu proveedor utilizará esta información durante tu visita.',
  'home.todos.profileIncomplete.a11y.label': 'Presiona para completar tu información',
  'home.todos.status.available': 'Disponible',
  'home.todos.status.dueNow': 'Vence ahora',
  'home.todos.status.inProgress': 'En Progreso',
  'home.todos.status.joinNow': 'Unirse Ahora',
  'home.todos.status.new': 'Nuevo',
  'home.todos.status.pending': 'Pendiente',
  'home.todos.status.recommended': 'Recomendado',
  'home.todos.status.required': 'Obligatorio',
  'home.todos.status.startingSoon': 'Comenzando Pronto',
  'home.todos.status.upcoming': 'Próxima',
  'home.footer.questions': '¿Tienes alguna pregunta?',
  'home.footer.faq': 'Consulta las preguntas frecuentes',
  'home.footer.faq.aria': 'Preguntas Frecuentes',
  'home.footer.emergency.link.aria': 'Llamar a servicios de emergencia',
  'home.tour.welcome.title': '¡Bienvenido a {name}!',
  'home.tour.welcome.tips': 'Aquí tienes algunos consejos para empezar.',
  'home.tour.welcome.todoList': 'Tus visitas, resultados de laboratorio y todo lo que necesitamos de ti aparecen en esta pantalla.',
  'home.tour.welcome.activities': 'La mantendremos al día a medida que avance tu atención.',
  'home.tour.appointment.next': 'Esta es tu próxima cita.',
  'home.tour.appointment.notification': 'Te enviaremos un recordatorio antes de que comience, con un enlace para unirte.',
  'home.tour.initialAppointment.book': 'Empieza aquí para reservar tu primera consulta.',
  'home.tour.nav.home': 'Inicio es tu punto de partida. Aquí aparecen tus visitas, laboratorios y todo lo que necesitamos de ti.',
  'home.tour.nav.health': 'En Salud están tus citas, resultados de laboratorio, recetas, documentos y evaluaciones.',
  'home.tour.nav.messages': 'En Mensajes hablas con tu equipo de atención. Las respuestas nuevas muestran un aviso aquí.',
  'home.tour.nav.profile': 'En Perfil actualizas tus datos y tu seguro. También encontrarás Preguntas Frecuentes y soporte.',

  // Security - Passcode Setup
  'security.passcode.screen.a11y.label': 'Pantalla de configuración de código',
  'security.passcode.create.title': 'Crea Tu Código',
  'security.passcode.create.description': 'Ingresa un código de 6 dígitos para un inicio de sesión seguro y conveniente',
  'security.passcode.confirm.title': 'Confirma Tu Código',
  'security.passcode.confirm.description': 'Ingresa tu código nuevamente para confirmar',
  'security.passcode.confirm.goBack': 'Empezar de Nuevo',
  'security.passcode.input.a11y.label': 'Ingresar código',
  'security.passcode.error.mismatch': 'Los códigos no coinciden. Inténtalo de nuevo.',
  'security.passcode.error.failed': 'Error al configurar el código. Inténtalo de nuevo.',
  'security.passcode.success.title': '¡Código Creado!',
  'security.passcode.success.description': 'Ahora puedes usar este código para iniciar sesión en tu cuenta',

  // Security - Lock Screen
  'security.lock.title': '¡Bienvenido de nuevo!',
  'security.lock.error.invalid': 'Código incorrecto. Inténtalo de nuevo.',
  'security.lock.error.invalidWithAttempts': 'Código incorrecto. {attempts} intentos restantes.',
  'security.lock.error.tooManyAttempts': 'Demasiados intentos incorrectos',
  'security.lock.logout': 'Cerrar sesión',
  'security.lock.forgotPasscode': '¿Olvidaste tu Código?',
  'security.lock.forgotPasscode.alert.title': '¿Olvidaste tu Código?',
  'security.lock.forgotPasscode.alert.message':
    'Aún puedes iniciar sesión usando tu correo electrónico. Esto cerrará tu sesión en la aplicación.',
  'security.lock.forgotPasscode.alert.goBack': 'Volver',
  'security.lock.forgotPasscode.alert.continue': 'Continuar',
  'security.lock.biometric.prompt': 'Autenticar para desbloquear',

  // Security - Biometric Setup
  'security.biometric.title.faceId': 'Habilitar Face ID',
  'security.biometric.title.fingerprint': 'Habilitar Huella Dactilar',
  'security.biometric.title.biometric': 'Habilitar Inicio Biométrico',
  'security.biometric.description.faceId': 'Usa Face ID para acceso rápido y seguro a tu cuenta',
  'security.biometric.description.fingerprint': 'Usa tu huella dactilar para acceso rápido y seguro a tu cuenta',
  'security.biometric.description.biometric': 'Usa autenticación biométrica para acceso rápido y seguro a tu cuenta',
  'security.biometric.enable': 'Habilitar',
  'security.biometric.skip': 'Omitir',
  'security.biometric.prompt.message': 'Autentícate para habilitar el inicio biométrico',

  // Notifications Setup
  'notifications.setup.title': 'Mantente Conectado',
  'notifications.setup.description':
    'Habilita las notificaciones push para recibir actualizaciones oportunas sobre:\n\n• Próximas citas\n• Nuevos mensajes de tu equipo de atención\n• Resultados de laboratorio y actualizaciones de recetas\n• Recordatorios de salud importantes',
  'notifications.setup.enable': 'Habilitar Notificaciones',
  'notifications.setup.denied.title': 'Las Notificaciones Están Deshabilitadas',
  'notifications.setup.denied.description':
    'Has denegado previamente los permisos de notificación. Para habilitar las notificaciones, necesitas actualizar tu configuración.',
  'notifications.setup.denied.openSettings': 'Abrir Configuración',
  'notifications.setup.success.title': '¡Notificaciones Habilitadas!',
  'notifications.setup.success.description': 'Ahora recibirás actualizaciones importantes sobre tu atención',

  // Chat/Messaging (Singular)
  'chat.a11y.conversation': 'Conversación con {name}, {unreadStatus}',
  'chat.badge.a11y.noUnread': 'No hay mensajes sin leer',
  'chat.badge.a11y.unread': '{count} mensajes sin leer',
  'chat.container.a11y.label': 'Área de conversación de chat',
  'chat.conversation.a11y.default': 'Conversación de chat',
  'chat.conversation.a11y.label': 'Chat con {name}',
  'chat.date.separator.a11y.label': 'Mensajes del {date}',
  'chat.date.today': '[Hoy]',
  'chat.date.yesterday': '[Ayer]',
  'chat.details.participant.a11y.label': '{name}, {role}',
  'chat.details.participants': 'Participantes',
  'chat.details.startChat.a11y.label': 'Chatear con {name}',
  'chat.disclaimer.title': 'Aviso Importante',
  'chat.disclaimer.cbo.message': 'Este chat no se monitorea con frecuencia.\nSi tienes preguntas, por favor contacta a Soporte.',
  'chat.disclaimer.provider.message':
    'Estamos disponibles de lunes a viernes, de 8am a 6pm EST.\nLas respuestas pueden tardar 1–2 días durante el horario laboral.',
  'chat.disclaimer.ma.message':
    'El horario de atención del Representante de Servicios al Paciente es de lunes a viernes de 8am a 6pm EST. Puede tardar hasta 1-2 días en responder a tu mensaje si se envió durante el horario comercial habitual.',
  'chat.header.viewDetails.a11y.hint': 'Abre la pantalla de detalles de la conversación',
  'chat.header.viewDetails.a11y.label': 'Ver detalles de la conversación',
  'chat.input.a11y.hint': 'Escribe tu mensaje aquí',
  'chat.input.a11y.label': 'Campo de mensaje',
  'chat.input.inaccessible': 'Esta conversación ya no es accesible. No puedes enviar mensajes.',
  'chat.input.inaccessible.a11y.label': 'Notificación de chat inaccesible',
  'chat.input.placeholder': 'Escribe un mensaje...',
  'chat.input.readonly': 'Solo lectura. Este chat ya no está disponible.',
  'chat.input.readonly.a11y.label': 'Notificación de chat de solo lectura',
  'chat.input.send.a11y.hint': 'Envía el mensaje escrito',
  'chat.input.send.a11y.label': 'Enviar mensaje',
  'chat.input.error.archived': 'Esta conversación ha sido archivada. No se pueden enviar nuevos mensajes.',
  'chat.input.error.sendFailed': 'No se pudo enviar el mensaje. Por favor intenta de nuevo.',
  'chat.input.error.outOfOffice':
    'Tu mensaje no se envió. Este miembro del equipo está fuera de la oficina en este momento. Intenta de nuevo más tarde.',
  'chat.outOfOffice.blocked': '{name} está fuera de la oficina y no puede recibir mensajes nuevos en este momento.',
  'chat.outOfOffice.help': 'Elige una opción a continuación para obtener ayuda.',
  'chat.outOfOffice.user': '{name} está fuera de la oficina',
  'chat.outOfOffice.a11y.label': 'Aviso de fuera de la oficina',
  'chat.message.a11y.label': '{sender} envió un mensaje a las {time}',
  'chat.message.bubble.a11y.mine': 'Tu mensaje enviado a las {time}',
  'chat.message.bubble.a11y.other': 'Mensaje de {sender} a las {time}',
  'chat.message.call.a11y.hint': 'Llama al número de soporte',
  'chat.message.call.a11y.label': 'Llamar a soporte',
  'chat.message.call.number': '(888) 708-0561',
  'chat.message.sentBy': 'Enviado por {name}',
  'chat.message.you': 'Tú',
  'chat.messages.empty': 'No hay mensajes todavía. ¡Inicia la conversación!',
  'chat.messages.list.a11y.label': 'Historial de mensajes de chat',
  'chat.newChat.a11y.hint': 'Abre la pantalla de nuevo chat',
  'chat.newChat.a11y.label': 'Iniciar nuevo chat',
  'chat.noUnread': 'sin mensajes sin leer',
  'chat.reply': 'Responder',
  'chat.reply.a11y.label': 'Responder a la conversación',
  'chat.search.placeholder': 'Buscar conversaciones...',
  'chat.send.title': 'Enviar un mensaje',
  'chat.select.assistantDescription': 'Ayuda con recetas existentes y problemas de laboratorio',
  'chat.select.assistantTitle': 'Representante de Servicios al Paciente',
  'chat.select.modal.title': 'Iniciar una Conversación',
  'chat.select.providerDescription': 'Preguntas sobre tu atención, cambios de medicación o consejos médicos',
  'chat.select.providerTitle': 'Mi Proveedor',
  'chat.select.screen.a11y.label': 'Seleccionar con quién chatear',
  'chat.select.subtitle': 'Elige de los miembros de tu equipo de atención médica a continuación',

  // Messages Page
  'messages.threads.ariaLabel': 'Hilos de mensajes',
  'messages.blank.title': '¡Inicia conversaciones significativas!',
  'chat.select.supportDescription': 'Ayuda con reinicio de atención, seguro, copago, cambio de dirección',
  'chat.select.supportTitle': 'Atención al Cliente',
  'chat.select.title': '¿Con quién te gustaría chatear?',
  'chat.support.businessHours':
    'El horario de atención de Soporte al Cliente es de lunes a viernes de 8am a 6pm EST. Puede tardar hasta 1-2 días en responder a tu mensaje si se envió durante el horario comercial habitual.',
  'chat.support.empty':
    '¿Necesitas ayuda para reiniciar la atención, con el seguro, los copagos, cambios de dirección o preguntas sobre recetas?',
  'chat.support.request.a11y.label': 'Pantalla de solicitud de chat de soporte',
  'chat.title': 'Chats',
  'chat.unreadCount': '{count} mensajes sin leer',
  'chat.user.request.a11y.label': 'Pantalla de solicitud de chat de usuario',
  'chat.userHeader.viewDetails.a11y.hint': 'Abre la pantalla de detalles del usuario',
  'chat.userHeader.viewDetails.a11y.label': 'Ver detalles de {name}',

  // Chat Dialog - Message Dialogs

  // Chat/Messaging (Plural)
  'chats.empty.default.description':
    'Comienza a conectarte con tu equipo de atención médica. Tus mensajes y actualizaciones importantes aparecerán aquí.',
  'chats.empty.default.title': '¡Tu centro de conversaciones te espera!',
  'chats.empty.search.description':
    'No pudimos encontrar ninguna conversación que coincida con "{search}". ¡Intenta con un término de búsqueda diferente!',
  'chats.empty.search.title': 'No se encontraron conversaciones',

  // Async Messaging
  'asyncMessaging.aboutMe.description':
    'Unas preguntas sobre usted. Sus respuestas son privadas y ayudan a su proveedor a darle la atención adecuada.',
  'asyncMessaging.assessment.continue': 'Continuar',
  'asyncMessaging.assessment.estimatedTime': 'Unas 30 preguntas rápidas · 5–10 min',
  'asyncMessaging.assessment.progress': 'Progreso: {progress}%',
  'asyncMessaging.assessment.start': 'Comenzar',
  'asyncMessaging.assessment.aria.start': 'Iniciar evaluación: {name}',
  'asyncMessaging.assessment.aria.continue': 'Continuar evaluación: {name}',
  'asyncMessaging.assessment.aria.canceled': 'Evaluación cancelada: {name}',
  'asyncMessaging.assessment.aria.view': 'Ver evaluación: {name}',
  'asyncMessaging.assessment.status.inProgress': 'En Progreso',
  'asyncMessaging.assessment.status.canceled': 'Cancelada',
  'asyncMessaging.assessment.status.completed': 'Completada',
  'asyncMessaging.assessment.status.unknown': 'Estado de la evaluación: {status}',
  'asyncMessaging.cancel.action': 'Cancelar solicitud de visita',
  'asyncMessaging.completed.description1': 'Gracias. Un proveedor revisará sus respuestas y se comunicará con usted si tiene preguntas.',
  'asyncMessaging.completed.description2':
    'Normalmente respondemos en 24 horas. En fines de semana y días festivos, le responderemos el siguiente día hábil.',
  'asyncMessaging.completed.nextStep1': 'Su información será revisada por nuestro equipo médico',
  'asyncMessaging.completed.nextStep2': 'Recibirá un correo electrónico cuando su proveedor responda',
  'asyncMessaging.completed.nextStep3': 'Un proveedor puede contactarlo para información adicional si es necesario',
  'asyncMessaging.completed.nextStepsTitle': '¿Qué sigue?',
  'asyncMessaging.completed.pageTitle': 'Visita enviada',
  'asyncMessaging.completed.returnDashboard': 'Ir a Inicio',
  'asyncMessaging.completed.title': '¡Todo listo!',
  'asyncMessaging.healthHistory.add': 'Añadir',
  'asyncMessaging.healthHistory.collapse': 'Contraer',
  'asyncMessaging.healthHistory.description':
    'Agregue sus alergias, medicamentos, condiciones, cirugías o inmunizaciones. Si no tiene ninguno, solo marque la casilla.',
  'asyncMessaging.healthHistory.expand': 'Expandir',
  'asyncMessaging.healthHistory.expandLabel': '{action} sección de {title}',
  'asyncMessaging.healthHistory.item': 'elemento',
  'asyncMessaging.healthHistory.itemCount': '{count} {pluralizedItem}',
  'asyncMessaging.healthHistory.items': 'elementos',
  'asyncMessaging.healthHistory.loading': 'Cargando...',
  'asyncMessaging.photoId.description':
    'Tome una foto clara de su licencia de conducir u otra identificación oficial. La usamos para confirmar su identidad antes de recetar. Se mantiene privada.',
  'asyncMessaging.photoId.uploadContinue': 'Continuar',
  'asyncMessaging.stepper.steps.aboutMe': 'Sobre Mí',
  'asyncMessaging.stepper.steps.assessment': 'Preguntas de salud',
  'asyncMessaging.stepper.steps.healthHistory': 'Historial de Salud',
  'asyncMessaging.stepper.steps.insurance': 'Seguro',
  'asyncMessaging.stepper.steps.photoId': 'Identificación',
  'asyncMessaging.stepper.steps.vitals': 'Signos Vitales',
  'asyncMessaging.primaryCare.description':
    'Por favor, proporcione la información de su médico de cabecera. Esto nos ayuda a coordinar su atención y garantizar la continuidad del tratamiento.',
  'asyncMessaging.stepper.steps.primaryCare': 'Médico de Cabecera',
  'asyncMessaging.stepper.steps.completion': 'Completar',
  'asyncMessaging.stepper.steps.video': 'Finalizar',
  'asyncMessaging.establish.confirmation.title': '¡Casi listo! Un paso más para establecer su atención.',
  'asyncMessaging.establish.confirmation.description':
    'Se requiere una cita breve de 5 minutos para establecer la atención. Debe programar esta cita antes de que la atención pueda continuar.',
  'asyncMessaging.establish.hivInitial.confirmation.description':
    'Se requiere una cita por video para establecer la atención. Debe programar esta cita antes de que la atención pueda continuar.',
  'asyncMessaging.establish.confirmation.action': 'Programar',
  'asyncMessaging.establish.video.instruction':
    'Vea el video a continuación para conocer su atención, luego programe su cita requerida de 5 minutos para establecer la atención.',
  'asyncMessaging.establish.video.description':
    'Este video explica nuestro proceso de telesalud y atención de establecimiento. Después de verlo, se le pedirá que programe una breve cita.',
  'asyncMessaging.vitals.description':
    'Agregue su altura y peso actuales. La presión arterial y la frecuencia cardíaca también ayudan, si las conoce.',
  'asyncMessaging.confirmation.title': 'Revise sus datos y confirme para enviar su solicitud de visita asíncrona.',
  'asyncMessaging.confirmation.description':
    'Al completar esta solicitud, confirma que la información proporcionada es precisa según su leal saber y entender.',
  'asyncMessaging.progress.step': 'Paso {current} de {total}',
  'asyncMessaging.progress.lastStep': 'Último paso',
  'asyncMessaging.progress.next': 'Siguiente: {step}',
  'asyncMessaging.vitals.hint': 'Agregue su {vitals} para continuar',
  'asyncMessaging.healthHistory.hint': 'Agregue un elemento o confirme que no tiene ninguno en {sections} para continuar',
  'asyncMessaging.healthHistory.noneOfThese': 'No tengo ninguno de estos',
  'asyncMessaging.healthHistory.noneOfRemaining': 'No tengo ninguno de los otros {count}',
  'asyncMessaging.insurance.optionalHint': 'El seguro es opcional. Puede agregarlo después desde su perfil.',
  'asyncMessaging.insurance.requiredHint': 'Agregue su seguro para continuar',
  'asyncMessaging.insurance.skip': 'Omitir por ahora',
  'asyncMessaging.title': 'Visita en línea de {type}',
  'asyncMessaging.video.title': 'Video educativo: Comprender la telesalud',
  'asyncMessaging.video.description': 'Este video explica nuestro proceso de telesalud y atención asíncrona.',
  'asyncMessaging.video.checkboxLabel': 'Vi el video y entiendo cómo funcionan las visitas en línea',
  'asyncMessaging.video.instruction':
    'Para completar su solicitud de visita asíncrona: vea el video a continuación, marque la casilla de confirmación y toque "Completar".',

  // Notifications
  'notifications.badge.a11y.noUnread': 'No hay notificaciones sin leer',
  'notifications.badge.a11y.unread': '{count} notificaciones sin leer',
  'notifications.empty.description': '¡Estás al día! Las nuevas notificaciones aparecerán aquí.',
  'notifications.empty.title': 'Sin notificaciones',
  'notifications.item.a11y.label': '{title}. {message}. {timeAgo}. {readStatus}',
  'notifications.list.a11y.label': 'Lista de notificaciones',
  'notifications.preferences.save.a11y.hint': 'Presiona para guardar tu configuración de notificaciones',
  'notifications.preferences.save.a11y.label': 'Guardar preferencias de notificaciones',
  'notifications.preferences.screen.a11y.label': 'Pantalla de preferencias de notificaciones',
  'notifications.preferences.select.a11y.hint': 'Elige cómo quieres recibir notificaciones',
  'notifications.preferences.select.a11y.label': 'Seleccionar método de comunicación preferido',
  'notifications.timeAgo.days': 'Hace {count}d',
  'notifications.timeAgo.hours': 'Hace {count}h',
  'notifications.timeAgo.minutes': 'Hace {count} min',
  'notifications.timeAgo.now': 'Ahora',
  'notifications.timeAgo.weeks': 'Hace {count}s',
  'notifications.title': 'Notificaciones',
  'notifications.toast.close': 'Cerrar notificación',
  'notifications.toast.joinAppointment.title': 'Unirse a la cita',
  'notifications.toast.joinAppointment.description': 'Tu cita programada está por comenzar. Por favor presiona aquí para entrar.',
  'notifications.toast.pressToView': 'Presiona para ver',

  // Visit (Video Visit/Twilio Components)
  'visit.completed.heading': 'Tu cita de video fue completada.',
  'visit.completed.messagePrompt': 'Si necesitas hablar con tu proveedor puedes enviarle un mensaje.',
  'visit.failed.heading': 'Lo sentimos pero tu reunión de video no está disponible.',
  'visit.failed.description':
    'Tu cita no fue completada. Esto puede haber sido debido a un problema técnico, o porque no pudimos proporcionar la atención que buscabas.',
  'visit.inProgress.joining': 'Uniéndose...',
  'visit.ended.heading': 'Saliste de la llamada',
  'visit.ended.rejoin': 'Volver a la llamada',
  'visit.call.duration.aria': 'Duración de la llamada {time}',
  'visit.call.status.cameraOff': 'Tu cámara está apagada',
  'visit.call.status.micOff': 'Tu micrófono está silenciado',
  'visit.pip.swap.aria': 'Cambiar el video principal',
  'visit.waiting.screen.title': 'Sala de espera',
  'visit.controls.flip.label': 'Girar',
  'visit.controls.camera.label': 'Cámara',
  'visit.controls.mute.label': 'Silenciar',
  'visit.controls.unmute.label': 'Activar',
  'visit.controls.speaker.label': 'Altavoz',
  'visit.controls.end.label': 'Finalizar',
  'visit.controls.chat.label': 'Chat',
  'visit.loading': 'Preparando tu experiencia de atención médica...',
  'visit.missed.heading': 'Lo sentimos pero tu cita programada no fue completada.',
  'visit.unavailable.heading': 'Lo sentimos pero tu reunión de video no está disponible.',
  'visit.unavailable.description': 'Solo puedes unirte a una cita programada que esté pendiente.',
  'visit.waiting.providerWillBeWithYou': '{providerName} estará contigo en breve.',
  'visit.waiting.willBeWithYouShortly': 'estará contigo en breve',
  'visit.waiting.status.waiting': 'Esperando…',
  'visit.waiting.cameraCheck.cameraOn': 'Cámara activada',
  'visit.tips.short.access': 'Acceso permitido',
  'visit.tips.short.camera': 'Cámara funcionando',
  'visit.tips.short.wifi': 'Buena señal wi-fi',
  'visit.tips.short.headphones': 'Usa audífonos',
  'visit.actions.finishEstablishingCare': 'Termine de Establecer su Atención',
  'visit.actions.bookNewAppointment': 'Reservar nueva Cita',
  'visit.actions.checkAudioVideo': 'Verificar tu audio y video',
  'visit.actions.goToHomePage': 'Ir a mi página de inicio',
  'visit.actions.messageProvider': 'Enviar mensaje al Proveedor',
  'visit.tips.beforeYourCall': 'Antes de tu llamada',
  'visit.tips.heading': 'Consejos para tener una gran llamada',
  'visit.tips.checkCamera': 'Verifica que la cámara y el audio estén funcionando',
  'visit.tips.quietPlace': 'Ve a un lugar tranquilo',
  'visit.tips.allowAccess': 'Permite el acceso a la cámara y el micrófono',
  'visit.tips.useHeadphones': 'Usa audífonos',
  'visit.tips.strongWifi': 'Ten una conexión wifi fuerte',
  'visit.controls.endCall': 'Finalizar Llamada',
  'visit.controls.endCall.dialog.title': '¿Salir del video?',
  'visit.controls.endCall.dialog.description':
    '¡Advertencia! Salir del video terminará la llamada con el proveedor y no podrás volver a unirte.',
  'visit.controls.endCall.dialog.leave': 'Salir',
  'visit.controls.endCall.dialog.continue': 'Continuar Videollamada',
  'visit.controls.endCall.error': 'No se pudo completar la llamada. Por favor intenta de nuevo.',
  'visit.controls.audio.mute': 'Silenciar',
  'visit.controls.audio.unmute': 'Activar sonido',
  'visit.controls.audio.noAudio': 'Sin Audio',
  'visit.controls.video.start': 'Iniciar Video',
  'visit.controls.video.stop': 'Detener Video',
  'visit.controls.video.noVideo': 'Sin Video',
  'visit.controls.camera.flip': 'Voltear Cámara',
  'visit.controls.chat.toggle': 'Alternar Chat',
  'visit.settings.device.title': 'Configuración de Audio y Video',
  'visit.settings.device.microphone': 'Micrófono',
  'visit.settings.device.microphone.select': 'Seleccionar micrófono',
  'visit.settings.device.microphone.noLocal': 'Sin Audio Local',
  'visit.settings.device.speakers': 'Altavoces',
  'visit.settings.device.speakers.select': 'Seleccionar altavoces',
  'visit.settings.device.speakers.default': 'Salida de Audio Predeterminada del Sistema',
  'visit.settings.device.camera': 'Cámara',
  'visit.settings.device.camera.select': 'Seleccionar cámara',
  'visit.settings.device.camera.noLocal': 'Sin Video Local',
  'visit.error.title': 'Error',
  'visit.error.code': 'Código: {code}',
  'visit.error.invalidToken': 'Por favor asegúrate de estar usando las credenciales correctas.',
  'visit.error.screenSharePermission':
    'No se puede compartir tu pantalla. Por favor asegúrate de que tu sistema operativo tenga los permisos correctos habilitados para compartir pantalla.',
  'visit.participant.videoOf': 'Video de {name}',
  'visit.participant.you': ' (Tú)',
  'visit.participant.screen': ' - Pantalla',
  'visit.participant.mainParticipant': 'Participante principal: {name}',
  'visit.participant.reconnecting': 'Reconectando...',
  'visit.participant.aria': 'Participante: {name}',
  'visit.participant.screenSharing': 'Compartiendo pantalla',
  'visit.participant.selected': 'Participante seleccionado',
  'visit.participant.list': 'Lista de Participantes',
  'visit.recording.label': 'Grabando',
  'visit.recording.tooltip':
    'El audio y video de todos los participantes está siendo grabado actualmente. Visita la configuración de la aplicación para detener la grabación.',
  'visit.permissions.welcome': 'Hola {firstName},',
  'visit.permissions.welcomeMessage': 'Bienvenido a tu reunión de video de {appName}.',
  'visit.permissions.accessRequired': 'Para tener una reunión de video necesitas permitir el acceso a tu cámara y micrófono.',
  'visit.permissions.error.title': 'Algo no está funcionando :(',
  'visit.permissions.error.description': 'Tu cámara y micrófono deben estar habilitados para la reunión.',
  'visit.permissions.success.title': '¡Todo listo!',
  'visit.permissions.success.description': 'Tu cámara y micrófono están listos. Ahora puedes unirte a tu cita de video.',
  'visit.permissions.camera': 'Cámara',
  'visit.permissions.microphone': 'Micrófono',
  'visit.permissions.continueToAppointment': 'Continuar a la cita',
  'visit.permissions.required.title': 'Permiso Requerido',
  'visit.permissions.required.description': 'Necesitamos acceso a tu cámara y micrófono para iniciar tu cita de video.',
  'visit.permissions.initial.title': 'Acceso a Cámara y Micrófono',
  'visit.permissions.initial.description': 'Para unirte a tu cita de video, necesitamos permiso para acceder a tu cámara y micrófono.',
  'visit.permissions.checking': 'Verificando permisos...',
  'visit.permissions.privacy.title': 'Tu privacidad está protegida',
  'visit.permissions.privacy.description':
    'Tu navegador pedirá permiso. Haz clic en "Permitir" para habilitar las reuniones de video. Siempre puedes cambiar esta configuración más tarde.',
  'visit.permissions.allowButton': 'Permitir acceso',
  'visit.permissions.status.allowed': 'Permitido',
  'visit.permissions.status.blocked': 'Bloqueado',
  'visit.permissions.status.unknown': 'Desconocido',
  'visit.permissions.enable.title': 'Habilitar Permisos',
  'visit.permissions.enable.description':
    'Por favor permite el acceso a la cámara y micrófono cuando tu navegador lo solicite. También puedes verificar la configuración de tu navegador o la configuración de privacidad del sistema operativo si los permisos están bloqueados.',
  'visit.menu.openMenu': 'Abrir menú',
  'visit.menu.mainControls': 'Controles principales',
  'visit.menu.audioVideoSettings': 'Configuración de Audio y Video',
  'visit.screenShare.sharing': 'Estás compartiendo tu pantalla',
  'visit.screenShare.stop': 'Dejar de Compartir',
  'visit.screenShare.stopAria': 'Dejar de compartir tu pantalla',
  'visit.screenShare.start': 'Compartir Pantalla',
  'visit.screenShare.stopShare': 'Dejar de Compartir Pantalla',
  'visit.screenShare.startAria': 'Iniciar compartir pantalla',
  'visit.screenShare.stopShareAria': 'Dejar de compartir pantalla',
  'visit.screenShare.tooltip.anotherUser': 'No se puede compartir pantalla cuando otro usuario está compartiendo',
  'visit.screenShare.tooltip.notSupported': 'Compartir pantalla no es compatible con este navegador',
  'visit.network.quality.unknown': 'Desconocido',
  'visit.network.quality.veryPoor': 'Muy Mala',
  'visit.network.quality.poor': 'Mala',
  'visit.network.quality.average': 'Promedio',
  'visit.network.quality.good': 'Buena',
  'visit.network.quality.excellent': 'Excelente',
  'visit.network.quality.aria': 'La calidad de red es {quality}',
  'visit.network.quality.title': 'Calidad de red: {quality}',
  'visit.notification.reconnecting': 'Conexión perdida: Reconectando a la sala...',
  'visit.notification.recordingStarted': 'La grabación ha comenzado.',
  'visit.notification.recordingInProgress': 'La grabación está en progreso.',
  'visit.notification.recordingFinished':
    'Grabación completa, puedes ver la grabación en la Consola de Twilio. Las grabaciones estarán disponibles una vez que esta sala haya terminado.',
  'visit.call.notification.title': 'Visita en curso',
  'visit.call.notification.text': 'Toque para volver a su visita por video.',

  // Document Management
  'documents.author.system': 'Sistema',
  'documents.camera.error.message': 'No se pudo acceder a la cámara. Por favor intenta de nuevo.',
  'documents.camera.error.title': 'Error en la Cámara',
  'documents.card.viewDocument': 'Ver documento: {name}',
  'documents.document.error.message': 'No se pudo seleccionar el archivo. Por favor intenta de nuevo.',
  'documents.document.error.title': 'Error de Selección de Archivo',
  'documents.empty.description':
    'Todos tus documentos médicos importantes se almacenarán aquí de forma segura una vez que estén disponibles',
  'documents.empty.title': '¡Tu biblioteca de documentos se está preparando!',
  'documents.loadMore.ariaLabel': 'Cargar más documentos',
  'documents.loadMore.button': 'más',
  'documents.fallback.name': 'Documento',
  'documents.gallery.error.message': 'No se pudo acceder a la galería de fotos. Por favor intenta de nuevo.',
  'documents.gallery.error.title': 'Error de Galería',
  'documents.lab.restriction.message':
    'Por favor completa todos los elementos requeridos en tu perfil antes de subir análisis de laboratorio.',
  'documents.lab.restriction.title': 'Perfil Incompleto',
  'documents.mimetype.audio': 'Audio',
  'documents.mimetype.excel': 'Hoja de Cálculo Excel',
  'documents.mimetype.gif': 'Imagen GIF',
  'documents.mimetype.jpeg': 'Imagen JPEG',
  'documents.mimetype.pdf': 'PDF',
  'documents.mimetype.png': 'Imagen PNG',
  'documents.mimetype.powerpoint': 'PowerPoint',
  'documents.mimetype.text': 'Archivo de Texto',
  'documents.mimetype.video': 'Video',
  'documents.mimetype.word': 'Documento Word',
  'documents.mimetype.zip': 'Archivo ZIP',
  'documents.permissions.camera.message': 'Por favor, permite el acceso a la cámara para tomar fotos',
  'documents.permissions.camera.title': 'Permiso de Cámara Requerido',
  'documents.permissions.photos.message': 'Por favor, permite el acceso a la galería para seleccionar imágenes',
  'documents.permissions.photos.title': 'Permiso de Galería de Fotos Requerido',
  'documents.remove.confirm.message': '¿Estás seguro de que quieres eliminar este archivo?',
  'documents.remove.failed.a11y': 'Quitar archivo fallido {index}',
  'documents.remove.file.a11y': 'Eliminar {filename}',
  'documents.title': 'Documentos',
  'documents.upload.button.a11y': 'Subir documento',
  'documents.upload.button.hint': 'Abrir opciones de carga para fotos y documentos',
  'documents.upload.document': 'Buscar Archivos',
  'documents.upload.document.a11y': 'Seleccionar archivo de documento',
  'documents.upload.document.description': 'Selecciona cualquier tipo de archivo de tu dispositivo',
  'documents.upload.files.placeholder': 'Subir archivos',
  'documents.upload.dropzone.ariaLabel': 'Zona para cargar archivos',
  'documents.upload.dropzone.desktop': 'Haz clic o arrastra y suelta para subir',
  'documents.upload.dropzone.mobile': 'Toca para subir',
  'documents.upload.gallery': 'Galería de Fotos',
  'documents.upload.gallery.a11y': 'Seleccionar foto de la galería',
  'documents.upload.gallery.description': 'Elige de tu galería',
  'documents.upload.loading.description': 'Por favor espera mientras subimos tu documento',
  'documents.upload.loading.title': 'Subiendo...',
  'documents.upload.options.a11y': 'Opciones de carga de documentos',
  'documents.upload.options.message': 'Elige cómo quieres agregar archivos',
  'documents.upload.options.subtitle': 'Elige cómo quieres agregar tu documento',
  'documents.upload.options.title': 'Opciones de carga',
  'documents.upload.photos.placeholder': 'Subir fotos',
  'documents.upload.placeholder': 'Subir documentos',
  'documents.upload.progress': '{count} / {limit} archivos subidos',
  'documents.upload.success.message': 'Tu documento ha sido subido exitosamente',
  'documents.upload.success.title': 'Subida Exitosa',
  'documents.upload.uploading': 'Subiendo documento...',

  // Insurance (Singular)
  'insurance.back.placeholder': 'Suba el reverso de su tarjeta de seguro',
  'insurance.back.title': 'Reverso de la Tarjeta de Seguro',
  'insurance.bin.label': 'BIN',
  'insurance.bin.placeholder': 'Introduzca BIN',
  'insurance.bin.required': 'Por favor, introduzca su Rx BIN',
  'insurance.company.helper': 'El nombre de su compañía de seguro médico, de su tarjeta de seguro.',
  'insurance.company.label': 'Compañía de Seguros',
  'insurance.company.placeholder': 'Introduzca el nombre de la compañía de seguros',
  'insurance.delete.a11y.hint': 'Presiona para eliminar esta tarjeta de seguro',
  'insurance.delete.a11y.label': 'Eliminar tarjeta de seguro',
  'insurance.delete.cancel': 'Cancelar',
  'insurance.delete.confirm': 'Eliminar',
  'insurance.delete.message':
    'Es posible que su socio comunitario requiera que mantenga un seguro activo, y la falta de seguro puede resultar en problemas para surtir sus recetas',
  'insurance.delete.title': 'Eliminar Seguro',
  'insurance.deleted': 'Información del seguro eliminada con éxito',
  'insurance.description':
    'Por favor, introduzca la información de su seguro a continuación. Esto nos ayuda a procesar su atención correctamente.',
  'insurance.edit.screen.a11y.label': 'Pantalla de editar tarjeta de seguro',
  'insurance.form.a11y.label': 'Formulario de información de seguro',
  'insurance.front.placeholder': 'Suba el frente de su tarjeta de seguro',
  'insurance.front.title': 'Frente de la Tarjeta de Seguro',
  'insurance.groupnum.label': 'GRP',
  'insurance.groupnum.placeholder': 'Introduzca GRP',
  'insurance.idnum.label': 'Número de ID',
  'insurance.idnum.placeholder': 'Introduzca el número de ID',
  'insurance.help.button': 'Ayuda con la tarjeta de seguro',
  'insurance.help.button.a11y': 'Obtener ayuda para encontrar la información en su tarjeta de seguro',
  'insurance.help.description': 'Su compañía de seguro médico le proporcionará una tarjeta con su información.',
  'insurance.help.image.alt': 'Ejemplo de tarjeta de seguro médico destacando campos de información clave.',
  'insurance.help.instructions': 'El siguiente ejemplo muestra cómo encontrar la información que necesita ingresar.',
  'insurance.help.optionalFields': 'Si su tarjeta tiene un número PCN, BIN o GRP, también puede ingresarlo.',
  'insurance.help.title': 'Ejemplo de tarjeta de seguro',
  'insurance.imagesTitle': 'Imágenes de la Tarjeta de Seguro',
  'insurance.new.screen.a11y.label': 'Pantalla de añadir nueva tarjeta de seguro',
  'insurance.pcn.label': 'PCN',
  'insurance.pcn.placeholder': 'Introduzca PCN',
  'insurance.save.a11y.hint': 'Presiona para guardar tu información de seguro',
  'insurance.save.a11y.label': 'Guardar tarjeta de seguro',
  'insurance.saved': 'Información del seguro guardada',
  'insurance.subscriber.helper': 'De su tarjeta de seguro médico. Puede aparecer como nombre del miembro, nombre, inscrito o asegurado.',
  'insurance.subscriber.label': 'Nombre del Asegurado',
  'insurance.subscriber.placeholder': 'Introduzca el nombre del asegurado',
  'insurance.twoColumns.a11y.label': 'Detalles adicionales de seguro',

  // Insurance (Plural)
  'insurances.card.a11y.hint': 'Presiona para ver o editar esta tarjeta de seguro',
  'insurances.card.a11y.label': 'Seguro de {company}, ID: {id}',
  'insurances.empty.description':
    'Mantén tu información de cobertura a mano y haz que las visitas médicas sean más fáciles agregando tus tarjetas de seguro aquí.',
  'insurances.empty.title': '¡Listo para agregar tu seguro!',
  'insurances.list.a11y.label': 'Lista de tarjetas de seguro',

  // Consent Forms
  'consent.dataSharing.agreement': 'Acepto compartir mi información con {cboName}',
  'consent.dataSharing.defaultPartner': 'nuestro socio comunitario',
  'consent.dataSharing.description':
    '{cboName}, nuestro socio comunitario, utiliza su información para eximir sus tarifas de proveedor y laboratorio.',
  'consent.dataSharing.error.required': 'Acepte compartir su información',
  'consent.dataSharing.note':
    'Algunos proveedores de seguros pueden restringir las opciones de farmacia, lo que puede afectar la participación.',
  'consent.dataSharing.title': 'Compartir Datos',
  'consent.header.description': 'Por favor revise y complete los consentimientos requeridos para continuar.',
  'consent.policyUpdates.accept': 'Acepto el Q Care Plus',
  'consent.policyUpdates.error.required': 'Acepte la política',
  'consent.policyUpdates.label': 'Nuestras políticas han sido actualizadas. Por favor revise y acepte para continuar.',
  'consent.policyUpdates.title': 'Actualizaciones de Política',
  'consent.treatment.description':
    'Requerimos que todos los pacientes confirmen su Consentimiento para recibir tratamiento por proveedores de Q Care Plus.',
  'consent.treatment.error.required': 'Ingrese sus iniciales',
  'consent.treatment.instructions': 'Escriba sus iniciales a continuación para dar su consentimiento.',
  'consent.treatment.title': 'Consentimiento para Recibir Tratamiento',

  // Release of Information
  'roiRequest.title': 'Solicitud ROI',
  'roiRequest.authorize.authorizeDisclosure.description': 'Autorizar a Q Care Plus a compartir su información con otro proveedor',
  'roiRequest.authorize.authorizeDisclosure.title': 'Autorizar Divulgación',
  'roiRequest.authorize.decline.message':
    'Si rechaza completar el formulario de Liberación de Información (ROI), no podremos liberar sus registros médicos.\n\nSi tiene preguntas sobre el formulario de Liberación de Información (ROI) o desea solicitar un nuevo formulario, por favor contacte a su representante de servicios al paciente.',
  'roiRequest.authorize.decline.title': '¿Rechazar Formulario de Solicitud ROI?',
  'roiRequest.authorize.discloseInfoTo.subtitle': 'Por favor proporcione los detalles completos a continuación',
  'roiRequest.authorize.discloseInfoTo.title': 'Enviar Mis Registros de QCare A',
  'roiRequest.authorize.discloseStatement':
    'Por la presente autorizo a Q Care Plus a divulgar información de salud protegida sobre la persona mencionada arriba a: {name}',
  'roiRequest.authorize.form.address.label': 'Dirección',
  'roiRequest.authorize.form.address.placeholder': 'Ingrese Dirección',
  'roiRequest.authorize.form.attention.label': 'Atención',
  'roiRequest.authorize.form.attention.placeholder': 'Ingrese Atención',
  'roiRequest.authorize.form.fax.label': 'Fax',
  'roiRequest.authorize.form.fax.placeholder': 'Ingrese Fax',
  'roiRequest.authorize.form.faxEmail.label': 'Fax o Correo Electrónico',
  'roiRequest.authorize.form.faxEmail.placeholder': 'Ingrese Fax o Correo Electrónico',
  'roiRequest.authorize.form.hospitalAgency.label': 'Nombre del Hospital/Agencia/Clínica/Médico',
  'roiRequest.authorize.form.hospitalAgency.placeholder': 'Ingrese Nombre del Hospital/Agencia/Clínica/Médico',
  'roiRequest.authorize.form.phone.label': 'Teléfono',
  'roiRequest.authorize.form.phone.placeholder': 'Ingrese número de Teléfono',
  'roiRequest.authorize.releaseInfoTo.subtitle': 'Por favor proporcione los detalles completos a continuación',
  'roiRequest.authorize.releaseInfoTo.title': 'Solicitar Mis Registros De',
  'roiRequest.authorize.releaseStatement':
    'Por la presente autorizo a {name}, {contact} a liberar información de salud protegida sobre la persona mencionada anteriormente a Q Care Plus',
  'roiRequest.authorize.requestRelease.description': 'Autorizar a otro proveedor a liberar su información a Q Care Plus',
  'roiRequest.authorize.requestRelease.title': 'Solicitar Liberación',
  'roiRequest.authorize.subtitle': 'Elija cómo desea manejar su información médica',
  'roiRequest.authorize.title': 'Tipo de Autorización',
  'roiRequest.authorize.validation.authorizationType': 'Por favor seleccione un tipo de autorización',
  'roiRequest.authorize.validation.phoneLength': 'Por favor ingrese un número teléfono válido',
  'roiRequest.consent.drugAlcohol.description': 'Participación en cualquier programa de abuso de drogas y alcohol asistido federalmente.',
  'roiRequest.consent.drugAlcohol.title': 'Programa de Drogas y Alcohol',
  'roiRequest.consent.hivAids.description':
    'Información relacionada con pruebas de VIH, estado de VIH o SIDA. Entiendo que dicha información está sujeta a protecciones especiales según las leyes y regulaciones estatales y federales.',
  'roiRequest.consent.hivAids.title': 'Información de VIH/SIDA',
  'roiRequest.consent.mentalHealth.description':
    'Información relacionada con el diagnóstico y tratamiento de condiciones mentales, alcohólicas, de dependencia de drogas o emocionales, que no sean notas registradas por un profesional de salud mental documentando o analizando conversaciones durante una sesión de consejería, siempre que dichas notas se mantengan por separado (a menos que esta autorización se refiera específicamente a notas de psicoterapia).',
  'roiRequest.consent.mentalHealth.title': 'Salud Mental y Abuso de Sustancias',
  'roiRequest.consent.subtitle':
    'Entiendo que la información a divulgar puede incluir información relacionada con lo siguiente y autorizo el uso o divulgación de este tipo de información:',
  'roiRequest.consent.title': 'Consentimiento para divulgar información sensible',
  'roiRequest.consent.validation.selectAtLeastOne': 'Seleccione al menos uno',
  'roiRequest.disclosure.admissionHistory.label': 'Historia de Admisión y Examen Físico',
  'roiRequest.disclosure.consultationReports.label': 'Reportes de Consulta',
  'roiRequest.disclosure.dateRange.from.label': 'Desde',
  'roiRequest.disclosure.dateRange.from.placeholder': 'Seleccionar fecha de inicio',
  'roiRequest.disclosure.dateRange.title': 'Rango de Fechas',
  'roiRequest.disclosure.dateRange.until.label': 'Hasta',
  'roiRequest.disclosure.dateRange.until.placeholder': 'Seleccionar fecha final',
  'roiRequest.disclosure.demographics.label': 'Información Demográfica',
  'roiRequest.disclosure.dischargeSummary.label': 'Resumen de Alta',
  'roiRequest.disclosure.entireRecord.label': 'Registro Médico Completo',
  'roiRequest.disclosure.imagingReports.label': 'Reportes de Imagenología / Radiología',
  'roiRequest.disclosure.informationTypes.title': 'Tipos de Información',
  'roiRequest.disclosure.labTests.label': 'Resultados de Pruebas de Laboratorio',
  'roiRequest.disclosure.nursingNotes.label': 'Notas de Enfermería',
  'roiRequest.disclosure.operativeReports.label': 'Reportes Operativos / de Procedimientos',
  'roiRequest.disclosure.other.label': 'Otro (especificar abajo)',
  'roiRequest.disclosure.other.textLabel': 'Especificar Otra Información',
  'roiRequest.disclosure.other.textPlaceholder': 'Por favor describa la información específica que desea divulgar...',
  'roiRequest.disclosure.physicianNotes.label': 'Notas de Progreso del Médico',
  'roiRequest.disclosure.physicianOrders.label': 'Órdenes del Médico',
  'roiRequest.disclosure.specificTypes.subtitle': 'O seleccione tipos específicos:',
  'roiRequest.disclosure.subtitle': 'Seleccione el rango de fechas y tipos de información a divulgar',
  'roiRequest.disclosure.title': 'Información a divulgar',
  'roiRequest.disclosure.validation.otherDetails': 'Por favor proporcione detalles',
  'roiRequest.expiration.defaultInfo':
    'A menos que se especifique una fecha anterior, esta autorización expirará 12 meses después de la fecha de firma a continuación.',
  'roiRequest.expiration.subtitle': 'Seleccione cuándo debe expirar esta autorización',
  'roiRequest.expiration.title': 'Expiración de la Autorización',
  'roiRequest.expiration.validUntil.description': 'Esta autorización es válida hasta la fecha del calendario:',
  'roiRequest.expiration.validUntil.label': 'Válida Hasta',
  'roiRequest.expiration.validUntil.placeholder': 'Seleccionar fecha de expiración',
  'roiRequest.expiration.validation.minDate': 'Debe ser al menos mañana',
  'roiRequest.navigation.complete': 'Completar',
  'roiRequest.navigation.decline': 'Rechazar',
  'roiRequest.sign.authorize.statement':
    'Yo, el abajo firmante, he leído lo anterior y autorizo la divulgación de dicha información de salud como se describe aquí. Entiendo que el tratamiento no está condicionado a la ejecución de esta autorización. Entiendo que si acepto firmar esta autorización, tengo derecho a recibir una copia firmada de este formulario. Entiendo que también tengo derecho a inspeccionar y recibir una copia de la información de salud que he autorizado a divulgar según esta autorización. Entiendo que la información descrita anteriormente puede estar sujeta a re-divulgación por parte del receptor y ya no puede estar protegida por las regulaciones federales de privacidad. Entiendo que puedo revocar esta autorización en cualquier momento, excepto en la medida en que se haya tomado acción basándose en esta autorización, o como se establece de otra manera en el Aviso de Prácticas de Privacidad.',
  'roiRequest.sign.date.label': 'Fecha: {date}',
  'roiRequest.sign.representative.authority.authorizedRepresentative': 'Representante Autorizado',
  'roiRequest.sign.representative.authority.guardian': 'Tutor',
  'roiRequest.sign.representative.authority.label': 'Autoridad para representar al individuo',
  'roiRequest.sign.representative.authority.parent': 'Padre/Madre',
  'roiRequest.sign.representative.authority.powerOfAttorney': 'Poder Notarial',
  'roiRequest.sign.representative.name.label': 'Nombre del Representante',
  'roiRequest.sign.representative.name.placeholder': 'Ingrese nombre del representante',
  'roiRequest.sign.revoke.statement':
    'Si se completa, envíe una copia de todo el formulario a la persona u organización nombrada en la Sección B. Ya no quiero que la información de salud perteneciente a la persona nombrada en la Sección A sea compartida con el hospital, agencia, clínica y/o médico nombrado en la Sección B. Entiendo que las acciones ya tomadas antes de que se reciba la revocación no se ven afectadas.',
  'roiRequest.sign.signaturePad.individual.label': 'Firma del Individuo',
  'roiRequest.sign.signaturePad.representative.label': 'Firma del Representante',
  'roiRequest.sign.status.label': 'Firma para Registro Médico',
  'roiRequest.sign.status.release': 'Liberación',
  'roiRequest.sign.status.revocation': 'Revocación',
  'roiRequest.sign.subtitle': 'Complete su firma para finalizar la autorización',
  'roiRequest.sign.title': 'Firma',
  'roiRequest.sign.user.individual': 'Individuo',
  'roiRequest.sign.user.label': 'Soy',
  'roiRequest.sign.user.representative': 'Representante',
  'roiRequest.sign.validation.signatureRequired': 'Por favor proporcione su firma',
  'roiRequest.steps.authorization': 'Autorización',
  'roiRequest.steps.details': 'Detalles',
  'roiRequest.steps.reviewSign': 'Revisar y Firmar',
  'roiRequest.success.submitted': 'Solicitud enviada para revisión',

  // Gilead Program Application
  'gileadApplication.contactAuthorization.description':
    'Por favor especifique cómo Advancing Access puede contactarlo sobre sus beneficios y comunicaciones del programa.',
  'gileadApplication.contactAuthorization.detailedMessage.label':
    'Autorizo a Advancing Access a dejar un mensaje detallado, incluyendo el nombre de mi receta, si no estoy disponible cuando llamen.',
  'gileadApplication.contactAuthorization.methods.email': 'Correo electrónico',
  'gileadApplication.contactAuthorization.methods.label':
    'Autorizo a Advancing Access a proporcionarme información sobre mis beneficios y otras comunicaciones que contengan referencia al programa Advancing Access o la Farmacia ARx Patient Solutions a través de lo siguiente (seleccione todas las que apliquen):',
  'gileadApplication.contactAuthorization.methods.mail': 'Correo postal de EE.UU.',
  'gileadApplication.contactAuthorization.methods.phone': 'Llamada telefónica',
  'gileadApplication.contactAuthorization.methods.text': 'Mensaje de texto',
  'gileadApplication.contactAuthorization.note.preference':
    'Si no selecciono una preferencia de contacto, entiendo que Advancing Access proporcionará las comunicaciones del programa por teléfono y/o a través de mi proveedor de salud',
  'gileadApplication.contactAuthorization.note.textRates':
    'Pueden aplicar tarifas de mensajes de texto y datos. Puede optar por no recibir dichos mensajes de texto en cualquier momento respondiendo "STOP"',
  'gileadApplication.contactAuthorization.note.title': 'Nota',
  'gileadApplication.contactAuthorization.title': 'Autorización de Contacto',
  'gileadApplication.title': 'Solicitud de Gilead',
  'gileadApplication.decline': 'Rechazar',
  'gileadApplication.decline.confirm.cancel': 'Cancelar',
  'gileadApplication.decline.confirm.decline': 'Rechazar',
  'gileadApplication.decline.confirm.message':
    'Si tiene preguntas sobre Gilead Advancing Access o piensa que algo en la solicitud debe cambiarse, por favor contacte al soporte al cliente.\n\nY si cambia de opinión, siempre puede enviar un mensaje al Soporte al Cliente y estarán encantados de preparar una nueva solicitud.',
  'gileadApplication.decline.confirm.title': '¿Desea rechazar el Programa Gilead AA?',
  'gileadApplication.declining': 'Rechazando...',
  'gileadApplication.incomeHousehold.description':
    'Para inscribirse en el Programa Gilead Advancing Access, necesitamos información adicional. Por favor proporcione los ingresos de su hogar y el número de miembros del hogar.',
  'gileadApplication.incomeHousehold.explanation.label': 'Por favor explique cómo se está manteniendo financieramente actualmente',
  'gileadApplication.incomeHousehold.explanation.placeholder': 'Describa su situación financiera',
  'gileadApplication.incomeHousehold.explanation.required': 'Por favor díganos cómo se está manteniendo financieramente antes de firmar.',
  'gileadApplication.incomeHousehold.income.invalid': 'Por favor ingrese un ingreso válido',
  'gileadApplication.incomeHousehold.income.label': 'Ingresos del Hogar',
  'gileadApplication.incomeHousehold.income.placeholder': 'Introduzca los ingresos de su hogar',
  'gileadApplication.incomeHousehold.members.invalid': 'Por favor ingrese un tamaño de hogar válido',
  'gileadApplication.incomeHousehold.members.label': 'Número de miembros del hogar',
  'gileadApplication.incomeHousehold.members.placeholder': 'Introduzca el número de miembros del hogar',
  'gileadApplication.incomeHousehold.social.hint': 'Ingrese 1111 si no tiene uno',
  'gileadApplication.incomeHousehold.social.invalid': 'Por favor ingrese un SSN válido',
  'gileadApplication.incomeHousehold.social.label': 'Últimos 4 dígitos del Número de Seguro Social',
  'gileadApplication.incomeHousehold.social.placeholder': 'Últimos 4 del SSN',
  'gileadApplication.incomeHousehold.title': 'Ingresos del Hogar y Miembros',
  'gileadApplication.insuranceInformation.adap.applied.label':
    '¿Ha solicitado el Programa de Asistencia para Medicamentos del SIDA (ADAP) o el Programa de Asistencia para Medicamentos PrEP?',
  'gileadApplication.insuranceInformation.adap.appliedDate.label': 'Fecha de Solicitud',
  'gileadApplication.insuranceInformation.adap.appliedDate.placeholder': 'Seleccione la fecha de solicitud',
  'gileadApplication.insuranceInformation.adap.denialFiles.label': 'Adjuntar carta(s) de denegación',
  'gileadApplication.insuranceInformation.adap.denialFiles.placeholder': 'Subir cartas de denegación (PDF)',
  'gileadApplication.insuranceInformation.adap.ineligibleReason.label': 'Razón',
  'gileadApplication.insuranceInformation.adap.ineligibleReason.placeholder': 'Por favor explique por qué no es elegible',
  'gileadApplication.insuranceInformation.adap.status.denied': 'Denegado (incluir carta de denegación)',
  'gileadApplication.insuranceInformation.adap.status.label': '¿Cuál es su estado de ADAP?',
  'gileadApplication.insuranceInformation.adap.status.notApplied': 'No he solicitado',
  'gileadApplication.insuranceInformation.adap.status.notEligible': 'No elegible',
  'gileadApplication.insuranceInformation.adap.status.pending': 'Pendiente',
  'gileadApplication.insuranceInformation.adap.status.waitListed': 'En lista de espera',
  'gileadApplication.insuranceInformation.description':
    'Por favor proporcione detalles sobre su cobertura de seguro y elegibilidad para varios programas.',
  'gileadApplication.insuranceInformation.exchange.applied.label':
    '¿Ha solicitado un plan de seguro ofrecido a través de un mercado de seguros estatal (también conocido como intercambio)?',
  'gileadApplication.insuranceInformation.exchange.appliedDate.label': 'Fecha de Solicitud',
  'gileadApplication.insuranceInformation.exchange.appliedDate.placeholder': 'Seleccione la fecha de solicitud',
  'gileadApplication.insuranceInformation.exchange.eligible.label':
    '¿Es elegible para un plan de seguro ofrecido a través de un mercado de seguros estatal (también conocido como intercambio)?',
  'gileadApplication.insuranceInformation.exchange.ineligibleReason.label': 'Razón',
  'gileadApplication.insuranceInformation.exchange.ineligibleReason.placeholder': 'Por favor, explique por qué no es elegible',
  'gileadApplication.insuranceInformation.medicaid.applied.label': '¿Ha solicitado Medicaid?',
  'gileadApplication.insuranceInformation.medicaid.appliedDate.label': 'Fecha de Solicitud',
  'gileadApplication.insuranceInformation.medicaid.appliedDate.placeholder': 'Seleccione la fecha de solicitud',
  'gileadApplication.insuranceInformation.medicaid.denialFiles.label': 'Adjuntar carta(s) de denegación (si fue denegado)',
  'gileadApplication.insuranceInformation.medicaid.denialFiles.placeholder': 'Subir cartas de denegación (PDF)',
  'gileadApplication.insuranceInformation.medicaid.eligible.label': '¿Es elegible para Medicaid?',
  'gileadApplication.insuranceInformation.medicaid.ineligibleReason.label': 'Razón',
  'gileadApplication.insuranceInformation.medicaid.ineligibleReason.placeholder':
    'Indique una razón (si fue negado, incluya una copia de la carta de denegación)',
  'gileadApplication.insuranceInformation.medicare.applied.label': '¿Ha solicitado Medicare?',
  'gileadApplication.insuranceInformation.medicare.appliedDate.label': 'Fecha de Solicitud',
  'gileadApplication.insuranceInformation.medicare.appliedDate.placeholder': 'Seleccione la fecha de solicitud',
  'gileadApplication.insuranceInformation.medicare.denialFiles.label': 'Adjuntar carta(s) de denegación (si fue denegado)',
  'gileadApplication.insuranceInformation.medicare.denialFiles.placeholder': 'Subir cartas de denegación (PDF)',
  'gileadApplication.insuranceInformation.medicare.eligible.label': '¿Es elegible para Medicare?',
  'gileadApplication.insuranceInformation.medicare.ineligibleReason.label': 'Razón',
  'gileadApplication.insuranceInformation.medicare.ineligibleReason.placeholder':
    'Indique una razón (si fue negado, incluya una copia de la carta de denegación)',
  'gileadApplication.insuranceInformation.title': 'Información Adicional de Seguro',
  'gileadApplication.insuranceInformation.va.benefits.label': '¿Es elegible para beneficios de VA?',
  'gileadApplication.insuranceInformation.va.triedMedication.label': '¿Ha intentado obtener el medicamento a través de VA?',
  'gileadApplication.navigation.decline': 'Rechazar',
  'gileadApplication.sign.date.label': 'Fecha: {date}',
  'gileadApplication.sign.description':
    'Por favor revise y firme su solicitud del Programa Gilead Advancing Access para completar el proceso de inscripción.',
  'gileadApplication.sign.signature.label': 'Su Firma',
  'gileadApplication.sign.signature.required': 'Por favor firme la solicitud',
  'gileadApplication.sign.statement':
    'Al firmar esta solicitud, reconozco que he leído y entendido los términos y condiciones del Programa Gilead Advancing Access. Certifico que toda la información proporcionada es verdadera y precisa según mi mejor conocimiento.',
  'gileadApplication.sign.title': 'Firmar Solicitud',
  'gileadApplication.steps.contactAuthorization': 'Autorización de Contacto',
  'gileadApplication.steps.incomeHousehold': 'Ingresos y Hogar',
  'gileadApplication.steps.insuranceInformation': 'Información de Seguro',
  'gileadApplication.steps.sign': 'Firmar',
  'gileadApplication.success.submitted': 'Solicitud enviada al proveedor para revisión',
  'gileadApplication.header.title': 'Inscribirse en el Programa Gilead\u00A0Advancing\u00A0Access',
  'gileadApplication.header.description1': 'Su farmacia está solicitando esto para ayudar a cubrir el costo de su receta.',
  'gileadApplication.header.description2':
    'El programa se compromete a ayudarlo a costear su medicamento sin importar su situación. Ya sea que tenga seguro o no, pueden explorar opciones de cobertura potenciales que puedan ser adecuadas para usted.',
  'gileadApplication.preview.title': 'Solicitud',
  'gileadApplication.preview.ariaLabel': 'Vista previa de la Solicitud Gilead Advancing Access',

  // Welcome & Onboarding
  'welcome.carousel': 'Carrusel de bienvenida',
  'welcome.getStarted': 'Comenzar',
  'welcome.getStarted.hint': 'Comienza el proceso de registro',
  'welcome.screenTitle': 'Pantalla de Bienvenida',
  'welcome.signIn': 'Iniciar Sesión',
  'welcome.signIn.hint': 'Te lleva a la pantalla de inicio de sesión',
  'welcome.slides.1.description':
    '¡Obtén PrEP en línea! Programa una cita en línea con cualquiera de nuestros expertos proveedores de telesalud calificados con 5 estrellas en Google en menos de 15 minutos. ¡Disfruta de la comodidad de citas el mismo día, por la tarde e incluso los fines de semana desde tu hogar, auto u oficina!',
  'welcome.slides.1.title': 'Consultas Rápidas en Línea',
  'welcome.slides.2.description':
    'Obtén PrEP en línea después de tu consulta inicial en línea con Q Care Plus. Enviaremos tu primera receta(s) de PrEP (o PrEP y doxy-PEP) y materiales de prueba fáciles de usar a la puerta de tu elección.',
  'welcome.slides.2.title': 'Pruebas a Domicilio y Entrega de Recetas',
  'welcome.slides.3.description':
    'Obtén PrEP en 4 sencillos pasos. Crea tu perfil, reúnete con tu proveedor en línea, completa las pruebas fáciles y recibe PrEP en tu puerta en un empaque discreto.',
  'welcome.slides.3.title': 'Cómo Funciona',
  'welcome.slides.4.description':
    'El 99% de los pacientes asegurados pagan $0 por sus visitas médicas, análisis y recetas. Obtener PrEP con Q Care Plus puede ser gratis para ti Y ADEMÁS ayudar a retribuir a tu comunidad.',
  'welcome.slides.4.title': '¿PrEP y doxy-PEP Gratis?',

  // Image Upload Component
  'imageUploader.camera': 'Tomar foto',
  'imageUploader.cameraError.message': 'Hubo un problema al acceder a su cámara. Por favor, inténtelo de nuevo.',
  'imageUploader.cameraError.title': 'Error de Cámara',
  'imageUploader.gallery': 'Elegir de la biblioteca',
  'imageUploader.galleryError.message': 'Hubo un problema al acceder a su biblioteca de fotos. Por favor, inténtelo de nuevo.',
  'imageUploader.galleryError.title': 'Error de Biblioteca de Fotos',
  'imageUploader.placeholder': 'Subir una imagen',
  'imageUploader.removePhoto': 'Eliminar Foto',
  'imageUploader.replaceImage.description': 'Elige cómo quieres reemplazar la imagen actual',
  'imageUploader.replaceImage.title': 'Reemplazar Imagen',
  'imageUploader.tapToUpload': 'Toca para subir',
  'imageUploader.uploadedImage.a11y': 'Imagen subida',
  'imageUploader.error.read': 'No se pudo leer el archivo seleccionado. Inténtelo de nuevo.',
  'imageUploader.requirements': 'PNG o JPG',

  // Image Crop Component
  'imageCrop.a11y.cropArea': 'Área de recorte de imagen',
  'imageCrop.a11y.cropper': 'Recortador de imagen',
  'imageCrop.a11y.dialog': 'Recortar imagen',
  'imageCrop.a11y.flipHorizontal': 'Voltear horizontalmente',
  'imageCrop.a11y.flipVertical': 'Voltear verticalmente',
  'imageCrop.a11y.rotateLeft': 'Rotar a la izquierda',
  'imageCrop.a11y.rotateRight': 'Rotar a la derecha',
  'imageCrop.error': 'Hubo un problema. Por favor, inténtelo de nuevo.',
  'imageCrop.idGuide':
    'Arrastre para reposicionar, pellizque o desplace para acercar, y ajuste el marco para que se vea toda su tarjeta de identificación',
  'imageCrop.control.rotateLeft': 'Girar izq.',
  'imageCrop.control.rotateRight': 'Girar der.',
  'imageCrop.control.flipH': 'Voltear horiz.',
  'imageCrop.control.flipV': 'Voltear vert.',

  // File Uploader Component
  'fileUploader.a11y.dropzone': '{title}. Presiona para subir.',
  'fileUploader.a11y.dropzoneDefault': 'Cargador de archivos. Presiona para subir.',
  'fileUploader.a11y.errors': 'Errores de carga de archivos',
  'fileUploader.a11y.errorsForFile': 'Errores para el archivo {path}',
  'fileUploader.a11y.preview': 'Vista previa de {name}',
  'fileUploader.cameraHelp.library':
    '¿Sigue sin funcionar? Toma la foto con la aplicación de cámara de tu teléfono, luego vuelve aquí y súbela desde tu biblioteca de fotos.',
  'fileUploader.cameraHelp.settings':
    'Si la cámara se abre pero la pantalla se queda en negro, abre la Configuración de tu teléfono, busca el navegador que estás usando y activa la Cámara.',
  'fileUploader.cameraHelp.title': '¿La cámara no funciona?',
  'fileUploader.clickToUpload': 'Haz clic para subir',
  'fileUploader.defaultAllowed': 'SVG, PNG, JPG o GIF',
  'fileUploader.dragAndDrop': 'o arrastra y suelta',
  'fileUploader.uploadNewImage': 'Subir nueva imagen',

  // Document Uploader Component
  'documentUploader.a11y.confirmUploadAll': 'Confirmar y subir todos los archivos',
  'documentUploader.a11y.dropzone': 'Zona de carga de archivos',
  'documentUploader.a11y.fileName': 'Nombre del archivo: {name}',
  'documentUploader.a11y.fileStatus': 'Estado del archivo: {status}',
  'documentUploader.a11y.input': 'Entrada de carga de archivos',
  'documentUploader.a11y.pdfPreview': 'Vista previa del PDF',
  'documentUploader.a11y.previewFile': 'Vista previa de {name}',
  'documentUploader.a11y.removeFile': 'Eliminar {name}',
  'documentUploader.a11y.uploading': 'Subiendo archivo',
  'documentUploader.error.unknown': 'Error desconocido, por favor intente de nuevo',
  'documentUploader.status.completed': 'Completado',
  'documentUploader.status.failed': 'Fallido',
  'documentUploader.status.loading': 'Cargando',
  'documentUploader.status.pressToPreview': 'Presiona para ver',
  'documentUploader.uploadAll': 'Subir Todos',

  // PDF Viewer Component
  'pdfViewer.a11y.viewer': 'Visor de PDF',
  'pdfViewer.a11y.document': 'Documento PDF',
  'pdfViewer.a11y.content': 'Contenido del PDF',
  'pdfViewer.a11y.controls': 'Controles del PDF',
  'pdfViewer.a11y.page': 'Página {current} de {total}',
  'pdfViewer.a11y.expand': 'Expandir PDF a pantalla completa',
  'pdfViewer.a11y.close': 'Cerrar vista de pantalla completa del PDF',
  'pdfViewer.a11y.zoomIn': 'Acercar',
  'pdfViewer.a11y.zoomOut': 'Alejar',
  'pdfViewer.a11y.rotateLeft': 'Rotar a la izquierda',
  'pdfViewer.a11y.rotateRight': 'Rotar a la derecha',
  'pdfViewer.a11y.download': 'Descargar PDF',
  'pdfViewer.a11y.previousPage': 'Página anterior',
  'pdfViewer.a11y.nextPage': 'Página siguiente',
  'pdfViewer.error.download': 'Error al descargar el PDF. Por favor, inténtelo de nuevo.',

  // Carousel Component
  'carousel.accessibility.currentSlide': 'Diapositiva actual',
  'carousel.accessibility.goToSlide': 'Ir a esta diapositiva',
  'carousel.accessibility.hint': 'Desliza a la izquierda o derecha para navegar entre diapositivas',
  'carousel.accessibility.nextSlide': 'Siguiente diapositiva',
  'carousel.accessibility.paginationDot': 'Diapositiva {current} de {total}',
  'carousel.accessibility.previousSlide': 'Diapositiva anterior',
  'carousel.accessibility.slideAnnouncement': 'Diapositiva {current} de {total}. {title}. {description}',
  'carousel.default.label': 'Carrusel de imágenes',

  // Mini Game (2048)
  'miniGame.score': 'Puntuación: {score}',
  'miniGame.faq': 'FAQ',
  'miniGame.faq.close': '¡Entendido!',
  'miniGame.win.title': '¡Ganaste!',
  'miniGame.gameOver.title': '¡Fin del juego!',
  'miniGame.button.continue': 'Continuar',
  'miniGame.button.newGame': 'Nuevo juego',
  'miniGame.faq.goal.title': 'Objetivo del juego',
  'miniGame.faq.goal.description':
    'Desliza las fichas numeradas en una cuadrícula para combinarlas y crear una ficha de 2048. ¡Continúa jugando después de alcanzar 2048 para lograr puntuaciones más altas!',
  'miniGame.faq.howToPlay.title': 'Cómo jugar',
  'miniGame.faq.howToPlay.desktop': 'En laptop/escritorio:',
  'miniGame.faq.howToPlay.desktopDescription': 'Usa las teclas de flecha para mover las fichas arriba, abajo, izquierda o derecha.',
  'miniGame.faq.howToPlay.mobile': 'En teléfono/tablet:',
  'miniGame.faq.howToPlay.mobileDescription': 'Desliza en la dirección en que quieres que se muevan las fichas.',
  'miniGame.faq.tips.title': 'Consejos para jugar',
  'miniGame.faq.tips.planAhead': 'Planifica con anticipación:',
  'miniGame.faq.tips.planAheadDescription': 'Piensa algunos movimientos adelante para maximizar las combinaciones.',
  'miniGame.faq.tips.corner': 'Mantén tu ficha más alta en una esquina:',
  'miniGame.faq.tips.cornerDescription': 'Ayuda a manejar las fichas más grandes y las mantiene organizadas.',
  'miniGame.faq.tips.monotonic': 'Construye filas o columnas monótonas:',
  'miniGame.faq.tips.monotonicDescription': 'Organiza las fichas para que sus valores aumenten o disminuyan consistentemente.',
  'miniGame.faq.tips.dontRush': 'No te apresures:',
  'miniGame.faq.tips.dontRushDescription': 'Haz movimientos estratégicos en lugar de mover fichas al azar.',

  // *** 10. FINANCIAL ***

  // Payment
  'payment.button.pay': 'Pagar {amount}',
  'payment.button.processing': 'Procesando...',
  'payment.completed.button': 'Volver al Inicio',
  'payment.completed.message': 'Su pago ha sido procesado exitosamente',
  'payment.completed.title': 'Pago Exitoso',
  'payment.error.general': 'Algo salió mal. Por favor intente nuevamente.',
  'payment.error.processing': 'El pago no pudo ser procesado. Por favor intente nuevamente.',
  'payment.error.setup': 'No se pudo configurar el pago. Verifique su conexión.',
  'payment.error.timeout': 'La configuración del pago está tardando más de lo esperado.',
  'payment.form.title': 'Complete Su Pago',
  'payment.form.description': 'Agregue su método de pago para procesar el pago de forma segura',
  'payment.header.subtitle': 'Su pago anterior no se procesó. Resolvamos esto rápida y seguramente.',
  'payment.header.title': 'Complete Su Pago',
  'payment.success.message': '¡Pago exitoso!',
  'payment.title': 'Pago',
  'webview.error.untrusted.title': 'No se puede abrir este enlace',
  'webview.error.untrusted.message':
    'Este enlace apunta fuera de QCare Plus, por lo que se bloqueó para proteger su cuenta. Vuelva atrás e inténtelo de nuevo.',

  'medReminders.title': 'Recordatorios de medicamentos',
  'medReminders.description': 'Los recordatorios se quedan en este teléfono y te avisan a las horas que elijas.',
  'medReminders.add': 'Agregar un medicamento',
  'medReminders.everyDay': 'Todos los días',
  'medReminders.empty.title': 'Aún no hay medicamentos',
  'medReminders.empty.description': 'Agrega uno y te recordaremos a las horas y en los días que elijas.',
  'medReminders.notificationsOff.title': 'Las notificaciones están desactivadas',
  'medReminders.notificationsOff.description': 'Actívalas para que tus recordatorios te lleguen',
  'medReminders.notification.channel': 'Recordatorios de medicamentos',
  'medReminders.notification.title': 'Es hora de tomar tu medicamento',
  'medReminders.notification.body': '{name}, {dose}',
  'medReminders.discreet.title': 'Notificaciones discretas',
  'medReminders.discreet.description': 'Deja el medicamento fuera del recordatorio',
  'medReminders.card.toggle.a11y.label': 'Recordatorios de {name}',
  'medReminders.card.on': 'Recordatorios activados',
  'medReminders.card.off': 'En pausa',
  'medReminders.form.title.new': 'Agregar medicamento',
  'medReminders.form.title.edit': 'Editar medicamento',
  'medReminders.form.description': 'Recibirás una notificación a cada hora que configures.',
  'medReminders.form.name.label': 'Nombre',
  'medReminders.form.name.placeholder': 'p. ej. Truvada',
  'medReminders.form.dose.label': 'Dosis (opcional)',
  'medReminders.form.dose.placeholder': 'p. ej. 1 tableta',
  'medReminders.form.times.label': 'Horas del recordatorio',
  'medReminders.form.times.add': 'Agregar otra hora',
  'medReminders.form.times.picker.title': 'Elige una hora',
  'medReminders.form.times.remove': 'Quitar hora',
  'medReminders.form.times.change': 'Cambiar hora',
  'medReminders.form.days.label': 'Días de la semana',
  'medReminders.form.save': 'Guardar',
  'medReminders.form.delete': 'Eliminar',
  'medReminders.form.delete.title': '¿Eliminar este medicamento?',
  'medReminders.form.delete.a11y.hint': 'Elimina este medicamento y sus recordatorios',
  'medReminders.form.delete.message': 'Tus recordatorios de {name} se detendrán.',
  'medReminders.form.validation.name': 'Ingresa el nombre del medicamento',
  'medReminders.weekday.sun.short': 'Dom',
  'medReminders.weekday.mon.short': 'Lun',
  'medReminders.weekday.tue.short': 'Mar',
  'medReminders.weekday.wed.short': 'Mié',
  'medReminders.weekday.thu.short': 'Jue',
  'medReminders.weekday.fri.short': 'Vie',
  'medReminders.weekday.sat.short': 'Sáb',
  'medReminders.weekday.sun.long': 'Domingo',
  'medReminders.weekday.mon.long': 'Lunes',
  'medReminders.weekday.tue.long': 'Martes',
  'medReminders.weekday.wed.long': 'Miércoles',
  'medReminders.weekday.thu.long': 'Jueves',
  'medReminders.weekday.fri.long': 'Viernes',
  'medReminders.weekday.sat.long': 'Sábado',
}

export default es
